import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../Navbar';
import { Container, Row, Col, Card, Button, NavDropdown, Form, Table, Badge, Modal, Spinner, FloatingLabel, Accordion, Nav, Dropdown, DropdownButton, ListGroup, Alert } from 'react-bootstrap';
import { API_ENDPOINTS } from '../config/api';
import PageTitle from '../components/PageTitle';
import OnboardingAssignModal from '../components/OnboardingAssignModal';
import OnboardingUploadModal from '../components/OnboardingUploadModal';
import OnboardingDocumentRequestModal from '../components/OnboardingDocumentRequestModal';
import OnboardingFormModal from '../components/OnboardingFormModal';
import UserAvatar from '../components/UserAvatar';
import { useDelegations } from "../contexts/DelegationsContext";
import DocumentCompletionPageV2, { ONBOARDING_ADMIN_PREVIEW_PACKAGE_ASSIGNMENT_ID } from './DocumentCompletionPageV2';
import { buildPrintDocument, openPrintWindow } from '../utils/printUtility';
import { getSignaturePrintNameClass } from '../utils/signatureFieldUtils';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

// Add CSS for hover effects
const assignmentHoverStyles = `
  .assignment-hover {
    transition: background-color 0.2s ease;
  }
  .assignment-hover:hover {
    background-color: var(--gray-100) !important;
  }

  .activity-timeline {
    position: relative;
    padding-left: 0;
    margin-bottom: 0;
  }

  .activity-timeline::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: repeating-linear-gradient(
      to bottom,
      var(--border-bg) 0px,
      var(--border-bg) 4px,
      transparent 4px,
      transparent 8px
    );
  }

  .activity-timeline-item {
    position: relative;
    padding-left: 48px;
    padding-bottom: 20px;
  }

  .activity-timeline-item:last-child {
    padding-bottom: 0;
  }

  .activity-timeline-icon {
    position: absolute;
    left: 0;
    top: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed var(--border-bg);
    outline: 5px solid var(--card-bg);
    transition: box-shadow 0.2s ease, transform 0.2s ease;
  }

  .activity-content {
    background: transparent;
    border-radius: 8px;
    padding: 0;
    border: none;
  }

  .activity-header {
    font-weight: 600;
    font-size: 14px;
    color: var(--gray-800);
  }

  .activity-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 12px;
    color: #6c757d;
    margin-top: 4px;
  }

  .activity-meta span:not(.activity-avatar) {
    color: #6c757d;
  }

  .onboarding-form-row-actions .dropdown-toggle::after {
    display: none !important;
  }
`;

function getPackageItemResponse(sectionAssignment, itemId) {
  return (sectionAssignment?.itemResponses || []).find((r) => r.itemId === itemId) || null;
}

/** Mirrors resident completion rules in DocumentCompletionPageV2 for dashboard counts. */
function packageItemIsCompleteByUser(sectionAssignment, item) {
  const r = getPackageItemResponse(sectionAssignment, item.id);
  if (!r) return false;
  if (r.status === 'approved') return true;
  if (r.status === 'rejected') return false;
  if (r.status !== 'draft' && r.status !== 'submitted') return false;
  if (item.itemType === 'info_form') {
    return Boolean(r.formData && Object.keys(r.formData).length > 0);
  }
  if (item.itemType === 'file_upload') {
    const schema = item.formSchema || {};
    const isReferenceFiles = schema.contentMode === 'reference_files' && Array.isArray(schema.referenceFiles);
    if (!isReferenceFiles) {
      return Boolean(r.filePath || r.fileName || r.webPath);
    }
    const acknowledged = r.formData?.acknowledged || {};
    const uploadedFor = r.formData?.uploadedFor || {};
    return schema.referenceFiles.every((rf) => {
      if (!rf?.id) return false;
      return rf.completionMode === 'upload_required'
        ? Boolean(uploadedFor[rf.id])
        : Boolean(acknowledged[rf.id]);
    });
  }
  return false;
}

function getPackageAssignmentItemProgress(packageAssignmentId, sectionAssignmentsList) {
  const related = (sectionAssignmentsList || []).filter(
    (sa) => Number(sa.packageAssignmentId) === Number(packageAssignmentId)
  );
  let total = 0;
  let complete = 0;
  for (const sa of related) {
    for (const item of sa?.section?.items || []) {
      total += 1;
      if (packageItemIsCompleteByUser(sa, item)) complete += 1;
    }
  }
  return { total, complete, remaining: Math.max(0, total - complete) };
}

/** Package/section/form accordion keys for the director submission review panel (one resident). */
function getSubmissionReviewPanelData(effectiveSelectedId, packageSectionAssignments, submissions, formSubmissions, packages) {
  const empty = {
    packageGroups: [],
    legacyGroups: [],
    selectedFormSubmissions: [],
    pkgEventKeys: [],
    legEventKeys: [],
    allAccordionKeys: [],
    hasContent: false
  };
  if (!effectiveSelectedId) return empty;

  const selectedSections = (packageSectionAssignments || []).filter(
    (sa) => sa.assignee && sa.assignee.id === effectiveSelectedId
  );
  const selectedSubmissions = (submissions || []).filter(
    (s) => s.submitter && s.submitter.id === effectiveSelectedId && s.status !== 'rejected'
  );
  const selectedFormSubmissions = (formSubmissions || []).filter((s) => {
    const submitterId = s.submitter?.id || s.assignment?.assignee?.id;
    return submitterId === effectiveSelectedId && s.status !== 'rejected';
  });

  if (selectedSections.length === 0 && selectedSubmissions.length === 0 && selectedFormSubmissions.length === 0) {
    return empty;
  }

  const groupedByPackageAssignment = selectedSections.reduce((acc, sa) => {
    const pkgAssignId = sa.packageAssignment?.id || sa.packageAssignmentId || null;
    if (!pkgAssignId) return acc;
    if (!acc[pkgAssignId]) acc[pkgAssignId] = { packageAssignment: sa.packageAssignment, sections: [] };
    acc[pkgAssignId].sections.push(sa);
    return acc;
  }, {});

  const packageGroups = Object.values(groupedByPackageAssignment).sort((a, b) => {
    const at = a.packageAssignment?.createdAt ? new Date(a.packageAssignment.createdAt).getTime() : 0;
    const bt = b.packageAssignment?.createdAt ? new Date(b.packageAssignment.createdAt).getTime() : 0;
    return bt - at;
  });

  const groupsMap = new Map();
  for (const s of selectedSubmissions) {
    const pkgId = (s.assignment && s.assignment.packageId) || (s.document && s.document.packageId);
    if (pkgId) {
      const customName = s.assignment?.customName || 'original';
      const key = `${pkgId}_${customName}`;
      if (!groupsMap.has(key)) groupsMap.set(key, []);
      groupsMap.get(key).push(s);
    } else {
      if (!groupsMap.has('standalone')) groupsMap.set('standalone', []);
      groupsMap.get('standalone').push(s);
    }
  }

  const legacyGroups = Array.from(groupsMap.entries()).map(([key, items]) => {
    if (key === 'standalone') {
      return { key, pkgId: 'standalone', pkg: null, items };
    }
    const [pkgId, customName] = key.split('_');
    const pkg = packages.find((p) => p.id === parseInt(pkgId, 10));
    return { key, pkgId, pkg, customName, items };
  });

  const allAccordionKeys = [];
  const pkgEventKeys = packageGroups.map((g, idx) => `pkg-${g.packageAssignment?.id ?? idx}`);
  pkgEventKeys.forEach((k) => allAccordionKeys.push(k));
  packageGroups.forEach((pkgGroup) => {
    const sortedSections = (pkgGroup.sections || []).slice().sort((a, b) => (a.section?.sortOrder || 0) - (b.section?.sortOrder || 0));
    sortedSections.forEach((sa) => {
      allAccordionKeys.push(`sec-${sa.id}`);
      (sa.section?.items || []).forEach((item) => {
        if (item.itemType === 'info_form') allAccordionKeys.push(`form-item-${item.id}`);
      });
    });
  });
  const legEventKeys = legacyGroups.map((_, idx) => `sub-${idx}`);
  legEventKeys.forEach((k) => allAccordionKeys.push(k));
  if (selectedFormSubmissions.length > 0) {
    allAccordionKeys.push('forms-submissions');
    selectedFormSubmissions.forEach((s) => allAccordionKeys.push(`form-submission-${s.id}`));
  }

  return {
    packageGroups,
    legacyGroups,
    selectedFormSubmissions,
    pkgEventKeys,
    legEventKeys,
    allAccordionKeys,
    hasContent: true
  };
}

const OnboardingPage = () => {
  const role = localStorage.getItem('role');
  const token = localStorage.getItem('token');
  const { hasModuleAccess } = useDelegations();
  const [packages, setPackages] = useState([]);
  const [myAssignments, setMyAssignments] = useState([]);
  const [allAssignments, setAllAssignments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [forms, setForms] = useState([]);
  const [formSubmissions, setFormSubmissions] = useState([]);
  const [myFormSubmissions, setMyFormSubmissions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [myCompletedSubmissions, setMyCompletedSubmissions] = useState([]);
  const [packageSectionAssignments, setPackageSectionAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showNewPackageModal, setShowNewPackageModal] = useState(false);
  const [newPackage, setNewPackage] = useState({ name: '', description: '', category: '' });
  const categoryOptions = useMemo(() => {
    const set = new Set();
    // Gather categories from existing packages and documents
    packages.forEach(p => { if (p.category) set.add(p.category); });
    documents.forEach(d => { if (d.category) set.add(d.category); });
    documentRequests.forEach(r => { if (r.category) set.add(r.category); });
    return Array.from(set);
  }, [packages, documents, documentRequests]);
  const [showDocumentRequestModal, setShowDocumentRequestModal] = useState(false);
  const [documentRequestPackageId, setDocumentRequestPackageId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeUploadAssignment, setActiveUploadAssignment] = useState(null);
  const [activeUploadPackageId, setActiveUploadPackageId] = useState(null);
  // Initialize selectedSubmitterId from URL hash or default
  const getInitialSelectedSubmitterId = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash.includes('resident=')) {
      const residentMatch = hash.match(/resident=(\d+)/);
      if (residentMatch) {
        return parseInt(residentMatch[1]);
      }
    }
    return null;
  };

  const [selectedSubmitterId, setSelectedSubmitterId] = useState(getInitialSelectedSubmitterId);
  const [layoutMode, setLayoutMode] = useState('default'); // 'default', 'package-assignments', 'document-assignments', or 'request-assignments'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  // Manage sub-tabs with URL hash support
  const getInitialManageState = () => {
    const hash = window.location.hash.replace('#', '');
    let sub = 'dashboard';
    let pkgId = null;
    const manageMatch = hash.match(/(?:^|&)manage=([a-zA-Z]+)/);
    const pkgMatch = hash.match(/(?:^|&)package=(\d+)/);
    if (manageMatch) {
      const candidate = manageMatch[1];
      if (['dashboard','packages','packageDetails','documents','requests','forms'].includes(candidate)) {
        sub = candidate;
      }
    }
    if (pkgMatch) {
      pkgId = parseInt(pkgMatch[1]);
      if (!Number.isFinite(pkgId)) pkgId = null;
    }
    return { sub, pkgId };
  };
  const initialManage = getInitialManageState();
  const [manageSubTab, setManageSubTab] = useState(initialManage.sub); // 'packages' | 'packageDetails' | 'documents' | 'requests' | 'forms'
  const [manageSelectedPackageId, setManageSelectedPackageId] = useState(initialManage.pkgId);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [openFormRowActionsId, setOpenFormRowActionsId] = useState(null);
  const [showCompleteFormModal, setShowCompleteFormModal] = useState(false);
  const [activeFormAssignment, setActiveFormAssignment] = useState(null);

  // Section/item builder state for package template editing
  const [packageSections, setPackageSections] = useState([]);
  const [packageSectionsLoading, setPackageSectionsLoading] = useState(false);
  const [newSectionDraft, setNewSectionDraft] = useState({ title: '', instructions: '' });
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionDraft, setEditingSectionDraft] = useState({ title: '', instructions: '' });

  const initialItemDraft = {
    title: '',
    instructions: '',
    itemType: 'file_upload',
    contentMode: 'request_upload', // request_upload | reference_files
    requiredFileTypes: '',
    formSchemaJson: '',
    formId: '',
    referenceFiles: [], // [{ id, originalName, webPath, completionMode }] (uploaded)
    referenceFilesPending: [] // [{ tmpId, file, completionMode }]
  };

  const [newItemSectionId, setNewItemSectionId] = useState(null);
  const [newItemDraft, setNewItemDraft] = useState(initialItemDraft);
  const [editingItemId, setEditingItemId] = useState(null);
  const [activePackageSectionId, setActivePackageSectionId] = useState(null);
  const [editingItemDraft, setEditingItemDraft] = useState(initialItemDraft);

  const [showPackageContentModal, setShowPackageContentModal] = useState(false);
  const [showAddSectionFields, setShowAddSectionFields] = useState(false);
  const [showNewItemForSectionId, setShowNewItemForSectionId] = useState(null);
  const [showPackageAssignmentsSidebar, setShowPackageAssignmentsSidebar] = useState(true);

  const packageResidentPreviewPayload = useMemo(() => {
    const pkg = packages.find((x) => x.id === manageSelectedPackageId);
    if (!pkg) return null;
    return {
      packageName: pkg.name,
      packageDescription: pkg.description || '',
      sections: packageSections
    };
  }, [packages, manageSelectedPackageId, packageSections]);

  const effectiveResidentSelection = useMemo(() => {
    const firstPackageAssignmentWithResident = packageSectionAssignments.find(
      (sa) => sa.assignee && sa.assignee.id
    );
    const firstSubmissionWithResident = submissions.find((s) => s.submitter?.id);
    const resolvedId =
      selectedSubmitterId ||
      firstPackageAssignmentWithResident?.assignee?.id ||
      firstSubmissionWithResident?.submitter?.id ||
      null;

    if (!resolvedId) {
      return { id: null, resident: null, displayName: null };
    }

    const residentFromSections = packageSectionAssignments.find(
      (sa) => sa.assignee && sa.assignee.id === resolvedId
    )?.assignee;
    const residentFromSubmissions = submissions.find(
      (s) => s.submitter && s.submitter.id === resolvedId
    )?.submitter;

    const resident =
      residentFromSections ||
      residentFromSubmissions ||
      firstPackageAssignmentWithResident?.assignee ||
      firstSubmissionWithResident?.submitter ||
      null;
    const rawName = resident ? `${resident.firstName || ''} ${resident.lastName || ''}`.trim() : '';

    return {
      id: resolvedId,
      resident,
      displayName: rawName || resident?.email || 'Unknown resident'
    };
  }, [selectedSubmitterId, packageSectionAssignments, submissions]);

  const effectiveSelectedId = effectiveResidentSelection.id;
  const effectiveSelectedResidentName = effectiveResidentSelection.displayName;

  const submissionReviewPanel = useMemo(
    () => getSubmissionReviewPanelData(effectiveSelectedId, packageSectionAssignments, submissions, formSubmissions, packages),
    [effectiveSelectedId, packageSectionAssignments, submissions, formSubmissions, packages]
  );

  const [submissionReviewAccordionKeys, setSubmissionReviewAccordionKeys] = useState([]);

  useEffect(() => {
    setSubmissionReviewAccordionKeys(submissionReviewPanel.allAccordionKeys);
  }, [effectiveSelectedId, submissionReviewPanel.allAccordionKeys]);

  const handleSubmissionReviewAccordionSelect = (eventKey) => {
    setSubmissionReviewAccordionKeys(
      Array.isArray(eventKey) ? eventKey : eventKey != null && eventKey !== '' ? [String(eventKey)] : []
    );
  };

  const getTabFromHash = (hash) => {
    const params = new URLSearchParams(hash);
    let tab = params.get('tab');
    if (!tab && (params.has('submissions') || hash === 'submissions' || hash.startsWith('submissions&'))) {
      tab = 'submissions';
    }
    return tab;
  };

  const buildOnboardingHash = (tab, options = {}) => {
    const params = new URLSearchParams();
    params.set('tab', tab);

    if (tab === 'manage') {
      const sub = options.manage ?? manageSubTab;
      const pkgId = options.packageId !== undefined ? options.packageId : manageSelectedPackageId;
      params.set('manage', sub);
      if (pkgId) params.set('package', String(pkgId));
    } else if (tab === 'submissions') {
      const residentId = options.residentId !== undefined ? options.residentId : selectedSubmitterId;
      if (residentId) params.set('resident', String(residentId));
    } else if (tab === 'my') {
      params.set('mySubTab', options.mySubTab ?? myAssignmentsSubTab);
      const selectedId = options.mySelected !== undefined ? options.mySelected : myAssignmentsSelectedId;
      if (selectedId) params.set('mySelected', String(selectedId));
    } else if (tab === 'completed') {
      params.set('completedSubTab', options.completedSubTab ?? completedSubTab);
      const selectedId = options.completedSelected !== undefined ? options.completedSelected : completedSelectedId;
      if (selectedId) params.set('completedSelected', selectedId);
    }

    return params.toString();
  };

  const syncOnboardingHash = (tab = activeTab, options = {}) => {
    const newHash = buildOnboardingHash(tab, options);
    if (window.location.hash !== `#${newHash}`) {
      window.location.hash = newHash;
    }
  };

  const updateManageHash = (sub, pkgId) => {
    syncOnboardingHash('manage', { manage: sub, packageId: pkgId });
  };

  const [showDeleteDocumentModal, setShowDeleteDocumentModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showDeletePackageModal, setShowDeletePackageModal] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [showDeleteDocumentRequestModal, setShowDeleteDocumentRequestModal] = useState(false);
  const [documentRequestToDelete, setDocumentRequestToDelete] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteType, setBulkDeleteType] = useState(null);
  const [bulkDeleteCount, setBulkDeleteCount] = useState(0);
  // Selection for always-on assignment panels
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  
  // Initialize My Assignments state from URL hash
  const getInitialMyAssignmentsState = () => {
    const hash = window.location.hash.replace('#', '');
    let subTab = 'packages';
    let selectedId = null;
    
    try {
      const params = new URLSearchParams(hash);
      const subTabParam = params.get('mySubTab');
      if (subTabParam && ['packages', 'standalone', 'forms'].includes(subTabParam)) {
        subTab = subTabParam;
      }
      
      const selectedParam = params.get('mySelected');
      if (selectedParam) {
        const parsed = parseInt(selectedParam);
        if (Number.isFinite(parsed)) {
          selectedId = parsed;
        }
      }
    } catch (e) {
      // Fallback to regex parsing if URLSearchParams fails
      if (hash.includes('mySubTab=')) {
        const subTabMatch = hash.match(/mySubTab=([a-zA-Z]+)/);
        if (subTabMatch && ['packages', 'standalone', 'forms'].includes(subTabMatch[1])) {
          subTab = subTabMatch[1];
        }
      }
      
      if (hash.includes('mySelected=')) {
        const selectedMatch = hash.match(/mySelected=(\d+)/);
        if (selectedMatch) {
          const parsed = parseInt(selectedMatch[1]);
          if (Number.isFinite(parsed)) {
            selectedId = parsed;
          }
        }
      }
    }
    
    return { subTab, selectedId };
  };
  
  const initialMyAssignments = getInitialMyAssignmentsState();
  const [myAssignmentsSubTab, setMyAssignmentsSubTab] = useState(initialMyAssignments.subTab);
  const [myAssignmentsSelectedId, setMyAssignmentsSelectedId] = useState(initialMyAssignments.selectedId);
  
  // Initialize Completed state from URL hash
  const getInitialCompletedState = () => {
    const hash = window.location.hash.replace('#', '');
    let subTab = 'packages';
    let selectedId = null;
    
    try {
      const params = new URLSearchParams(hash);
      const subTabParam = params.get('completedSubTab');
      if (subTabParam && ['packages', 'standalone', 'forms'].includes(subTabParam)) {
        subTab = subTabParam;
      }
      
      const selectedParam = params.get('completedSelected');
      if (selectedParam) {
        // For standalone, it might be "standalone-123" format
        if (selectedParam.startsWith('standalone-')) {
          const parsed = parseInt(selectedParam.replace('standalone-', ''));
          if (Number.isFinite(parsed)) {
            selectedId = `standalone-${parsed}`;
          }
        } else {
          // For packages, it's just the key like "123_original"
          selectedId = selectedParam;
        }
      }
    } catch (e) {
      // Fallback to regex parsing if URLSearchParams fails
      if (hash.includes('completedSubTab=')) {
        const subTabMatch = hash.match(/completedSubTab=([a-zA-Z]+)/);
        if (subTabMatch && ['packages', 'standalone', 'forms'].includes(subTabMatch[1])) {
          subTab = subTabMatch[1];
        }
      }
      
      if (hash.includes('completedSelected=')) {
        const selectedMatch = hash.match(/completedSelected=([^&]+)/);
        if (selectedMatch) {
          selectedId = decodeURIComponent(selectedMatch[1]);
        }
      }
    }
    
    return { subTab, selectedId };
  };
  
  const initialCompleted = getInitialCompletedState();
  const [completedSubTab, setCompletedSubTab] = useState(initialCompleted.subTab);
  const [completedSelectedId, setCompletedSelectedId] = useState(initialCompleted.selectedId);
  
  // State for collapsible sections in My Assignments
  const [expandedSections, setExpandedSections] = useState({
    pendingPackages: true,
    completedPackages: true,
    pendingDocuments: true,
    completedDocuments: true
  });
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Default selections for documents/requests tabs
  useEffect(() => {
    if (manageSubTab === 'documents') {
      const list = documents.filter(d => !d.packageId);
      if (list.length > 0 && !selectedDocumentId) {
        setSelectedDocumentId(list[0].id);
      }
    }
  }, [manageSubTab, documents, selectedDocumentId]);

  useEffect(() => {
    if (manageSubTab === 'requests') {
      const list = documentRequests.filter(r => !r.packageId);
      if (list.length > 0 && !selectedRequestId) {
        setSelectedRequestId(list[0].id);
      }
    }
  }, [manageSubTab, documentRequests, selectedRequestId]);

  useEffect(() => {
    if (manageSubTab === 'forms') {
      if (forms.length > 0 && !selectedFormId) {
        setSelectedFormId(forms[0].id);
      }
    }
  }, [manageSubTab, forms, selectedFormId]);

  useEffect(() => {
    if (manageSubTab !== 'documents') setSelectedDocumentId(null);
    if (manageSubTab !== 'requests') setSelectedRequestId(null);
    if (manageSubTab !== 'forms') setSelectedFormId(null);
  }, [manageSubTab]);

  // Inject CSS for hover effects
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = assignmentHoverStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [selectedPackageDocuments, setSelectedPackageDocuments] = useState(new Set());
  const [selectedStandaloneDocuments, setSelectedStandaloneDocuments] = useState(new Set());
  const [selectedDocumentRequests, setSelectedDocumentRequests] = useState(new Set());
  const [selectedPackageDocumentRequests, setSelectedPackageDocumentRequests] = useState(new Set());
  
  // New state for managing uploaded files before submission
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [uploadedFileCategories, setUploadedFileCategories] = useState({});
  const [openAccordions, setOpenAccordions] = useState(new Set());
  
  // Derive submitted files from actual submission data
  const getSubmittedFilesForAssignment = useMemo(() => {
    const submittedFilesMap = {};
    
    // Group submissions by assignment ID
    myCompletedSubmissions.forEach(submission => {
      const assignmentId = submission.assignmentId;
      if (assignmentId) {
        if (!submittedFilesMap[assignmentId]) {
          submittedFilesMap[assignmentId] = [];
        }
        
        // Only include approved or submitted (pending review) submissions
        // Rejected submissions should not show as "submitted" in the UI
        if (submission.status === 'approved' || submission.status === 'submitted') {
          // Create a file object that matches the structure expected by the UI
          const fileData = {
            id: submission.id,
            file: {
              name: submission.originalName || 'Submitted File',
              size: submission.fileSize || 0
            },
            category: submission.category || '',
            submittedAt: submission.createdAt
          };
          
          submittedFilesMap[assignmentId].push(fileData);
        }
      }
    });
    
    return submittedFilesMap;
  }, [myCompletedSubmissions]);

  const groupedAssignments = useMemo(() => {
    console.log('myAssignments:', myAssignments);
    console.log('myAssignments with customName:', JSON.stringify(myAssignments.map(a => ({
      id: a.id,
      customName: a.customName,
      packageId: a.packageId,
      documentId: a.documentId,
      package: a.package?.name,
      document: a.document?.title
    })), null, 2));
    const map = new Map();
          for (const a of myAssignments) {
        console.log('Processing assignment:', JSON.stringify({
          id: a.id,
          customName: a.customName,
          packageId: a.packageId,
          documentId: a.documentId,
          package: a.package?.name,
          document: a.document?.title,
          status: a.status
        }, null, 2));
        
        // If it's a document assignment (even from a package), treat it as standalone
        if (a.document) {
          const key = 'standalone';
          if (!map.has(key)) {
            map.set(key, { key, package: null, packageAssignment: null, documentAssignments: [], documentRequestAssignments: [], formAssignments: [] });
          }
          const group = map.get(key);
          group.documentAssignments.push(a);
          console.log('Added document assignment to standalone');
        } else if (a.documentRequest) {
          // Document requests from packages also go to standalone
          const key = 'standalone';
          if (!map.has(key)) {
            map.set(key, { key, package: null, packageAssignment: null, documentAssignments: [], documentRequestAssignments: [], formAssignments: [] });
          }
          const group = map.get(key);
          group.documentRequestAssignments.push(a);
          console.log('Added document request assignment to standalone');
        } else if (a.form) {
          if (a.package) {
            const pkgId = a.package.id;
            const customName = a.customName || 'original';
            const key = `${pkgId}_${customName}`;
            if (!map.has(key)) {
              map.set(key, {
                key,
                package: a.package,
                packageAssignment: a,
                documentAssignments: [],
                documentRequestAssignments: [],
                formAssignments: []
              });
            }
            const group = map.get(key);
            group.formAssignments.push(a);
            if (!group.packageAssignment) {
              group.packageAssignment = a;
            }
            console.log('Added form assignment to package group');
          } else {
            const key = 'standalone';
            if (!map.has(key)) {
              map.set(key, { key, package: null, packageAssignment: null, documentAssignments: [], documentRequestAssignments: [], formAssignments: [] });
            }
            const group = map.get(key);
            group.formAssignments.push(a);
            console.log('Added form assignment to standalone');
          }
        } else if (a.package && !a.document && !a.documentRequest) {
          // Create separate groups for package assignments with different customNames
          const pkgId = a.package.id;
          const customName = a.customName || 'original';
          const key = `${pkgId}_${customName}`;
          
          if (!map.has(key)) {
            map.set(key, { 
              key, 
              package: a.package, 
              packageAssignment: a, 
              documentAssignments: [], 
              documentRequestAssignments: [],
              formAssignments: []
            });
            console.log('Created new package assignment group:', key, 'with customName:', a.customName);
          } else {
            // If same key exists, update to most recent assignment
            const group = map.get(key);
            if (a.id > group.packageAssignment.id) {
              group.packageAssignment = a;
              console.log('Updated package assignment in group:', key, 'to:', a.id, 'with customName:', a.customName);
            }
          }
        }
      }
    const result = Array.from(map.values());
    console.log('Final grouped assignments:', JSON.stringify(result.map(g => ({
      key: g.key,
      packageAssignmentId: g.packageAssignment?.id,
      packageAssignmentCustomName: g.packageAssignment?.customName,
      packageName: g.package?.name,
      documentAssignmentsCount: g.documentAssignments.length,
      documentRequestAssignmentsCount: g.documentRequestAssignments.length,
      formAssignmentsCount: (g.formAssignments || []).length
    })), null, 2));
    return result;
  }, [myAssignments]);

  const isDirector = ['director', 'admin', 'attending'].includes(role) || hasModuleAccess('onboarding');
  // Initialize activeTab from URL hash or default
  const getInitialActiveTab = () => {
    const hash = window.location.hash.replace('#', '');
    const tab = getTabFromHash(hash);
    if (isDirector && ['manage', 'submissions', 'my', 'completed'].includes(tab)) {
      return tab;
    }
    if (!isDirector && ['my', 'completed'].includes(tab)) {
      return tab;
    }
    return isDirector ? 'manage' : 'my';
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab);

  // Keep the URL hash in sync with the current tab and its sub-state
  useEffect(() => {
    syncOnboardingHash(activeTab);
  }, [
    activeTab,
    selectedSubmitterId,
    manageSubTab,
    manageSelectedPackageId,
    myAssignmentsSubTab,
    myAssignmentsSelectedId,
    completedSubTab,
    completedSelectedId
  ]);

  // Listen for browser back/forward button clicks
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      const tab = getTabFromHash(hash);

      if (tab) {
        if (isDirector && ['manage', 'submissions', 'my', 'completed'].includes(tab)) {
          setActiveTab(tab);
        } else if (!isDirector && ['my', 'completed'].includes(tab)) {
          setActiveTab(tab);
        }
      }

      const currentTab = tab || activeTab;
      
      // Update manage sub-tab and package if we're on Manage
      if (currentTab === 'manage') {
        const manageMatch = hash.match(/(?:^|&)manage=([a-zA-Z]+)/);
        const pkgMatch = hash.match(/(?:^|&)package=(\d+)/);
        if (manageMatch) {
          const candidate = manageMatch[1];
          if (['dashboard','packages','packageDetails','documents','requests','forms'].includes(candidate)) {
            setManageSubTab(candidate);
          }
        }
        if (pkgMatch) {
          const parsed = parseInt(pkgMatch[1]);
          setManageSelectedPackageId(Number.isFinite(parsed) ? parsed : null);
        } else {
          setManageSelectedPackageId(null);
        }
      }
      
      // Update selected resident if we're on Submissions tab
      if (currentTab === 'submissions' && hash.includes('resident=')) {
        const residentMatch = hash.match(/resident=(\d+)/);
        if (residentMatch) {
          setSelectedSubmitterId(parseInt(residentMatch[1]));
        } else {
          setSelectedSubmitterId(null);
        }
      }
      
      // Update My Assignments sub-tab and selection if we're on My Assignments tab
      if (currentTab === 'my') {
        try {
          const params = new URLSearchParams(hash);
          const mySubTabParam = params.get('mySubTab');
          if (mySubTabParam && ['packages', 'standalone', 'forms'].includes(mySubTabParam)) {
            setMyAssignmentsSubTab(mySubTabParam);
          }
          
          const mySelectedParam = params.get('mySelected');
          if (mySelectedParam) {
            const parsed = parseInt(mySelectedParam);
            setMyAssignmentsSelectedId(Number.isFinite(parsed) ? parsed : null);
          } else {
            setMyAssignmentsSelectedId(null);
          }
        } catch (e) {
          // Fallback to regex parsing
          const mySubTabMatch = hash.match(/mySubTab=([a-zA-Z]+)/);
          const mySelectedMatch = hash.match(/mySelected=(\d+)/);
          
          if (mySubTabMatch) {
            const candidate = mySubTabMatch[1];
            if (['packages', 'standalone', 'forms'].includes(candidate)) {
              setMyAssignmentsSubTab(candidate);
            }
          }
          
          if (mySelectedMatch) {
            const parsed = parseInt(mySelectedMatch[1]);
            setMyAssignmentsSelectedId(Number.isFinite(parsed) ? parsed : null);
          } else {
            setMyAssignmentsSelectedId(null);
          }
        }
      }
      
      // Update Completed sub-tab and selection if we're on Completed tab
      if (currentTab === 'completed') {
        try {
          const params = new URLSearchParams(hash);
          const completedSubTabParam = params.get('completedSubTab');
          if (completedSubTabParam && ['packages', 'standalone'].includes(completedSubTabParam)) {
            setCompletedSubTab(completedSubTabParam);
          }
          
          const completedSelectedParam = params.get('completedSelected');
          if (completedSelectedParam) {
            setCompletedSelectedId(completedSelectedParam);
          } else {
            setCompletedSelectedId(null);
          }
        } catch (e) {
          // Fallback to regex parsing
          const completedSubTabMatch = hash.match(/completedSubTab=([a-zA-Z]+)/);
          const completedSelectedMatch = hash.match(/completedSelected=([^&]+)/);
          
          if (completedSubTabMatch) {
            const candidate = completedSubTabMatch[1];
            if (['packages', 'standalone'].includes(candidate)) {
              setCompletedSubTab(candidate);
            }
          }
          
          if (completedSelectedMatch) {
            setCompletedSelectedId(decodeURIComponent(completedSelectedMatch[1]));
          } else {
            setCompletedSelectedId(null);
          }
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [isDirector, activeTab]);

  const getFileIcon = (fileName, mimeType) => {
    const extension = fileName ? fileName.split('.').pop().toLowerCase() : '';
    const type = mimeType ? mimeType.toLowerCase() : '';
    
    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension) || type.includes('image/')) {
      return 'bi bi-image-fill text-info';
    }
    
    // PDF files
    if (extension === 'pdf' || type.includes('pdf')) {
      return 'bi bi-file-earmark-pdf-fill text-danger';
    }
    
    // Word documents
    if (['doc', 'docx'].includes(extension) || type.includes('word') || type.includes('document')) {
      return 'bi bi-file-earmark-word-fill text-primary';
    }
    
    // Excel files
    if (['xls', 'xlsx'].includes(extension) || type.includes('excel') || type.includes('spreadsheet')) {
      return 'bi bi-file-earmark-excel-fill text-success';
    }
    
    // PowerPoint files
    if (['ppt', 'pptx'].includes(extension) || type.includes('powerpoint') || type.includes('presentation')) {
      return 'bi bi-file-earmark-ppt-fill text-warning';
    }
    
    // Text files
    if (['txt', 'rtf'].includes(extension) || type.includes('text/')) {
      return 'bi bi-file-earmark-text-fill text-secondary';
    }
    
    // Default document icon
    return 'bi bi-file-earmark-text-fill text-warning';
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [pkgRes, myAssignRes, docsRes, requestsRes, formsRes, myCompletedRes, mySectionAssignRes, myFormSubsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.ONBOARDING}/packages`, { headers }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/assignments/me`, { headers }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/documents`, { headers }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/document-requests`, { headers }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/forms`, { headers }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/submissions/me`, { headers }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/package-section-assignments/me`, { headers }),
        fetch(`${API_ENDPOINTS.ONBOARDING}/form-submissions/me`, { headers })
      ]);
      if (!pkgRes.ok) throw new Error('Failed loading packages');
      if (!myAssignRes.ok) throw new Error('Failed loading assignments');
      if (!docsRes.ok) throw new Error('Failed loading documents');
      if (!requestsRes.ok) throw new Error('Failed loading document requests');
      if (!formsRes.ok) throw new Error('Failed loading forms');
      if (!myCompletedRes.ok) throw new Error('Failed loading completed submissions');
      const [pkgJson, myAssignJson, docsJson, requestsJson, formsJson, myCompletedJson, mySectionAssignJson, myFormSubsJson] = await Promise.all([
        pkgRes.json(),
        myAssignRes.json(),
        docsRes.json(),
        requestsRes.json(),
        formsRes.json(),
        myCompletedRes.json(),
        mySectionAssignRes.json(),
        myFormSubsRes.ok ? myFormSubsRes.json() : []
      ]);
      console.log('API Response - myAssignments:', myAssignJson);
      console.log('API Response - myAssignments with customName:', JSON.stringify(myAssignJson.map(a => ({
        id: a.id,
        customName: a.customName,
        packageId: a.packageId,
        documentId: a.documentId,
        package: a.package?.name,
        document: a.document?.title
      })), null, 2));
      // Fallback: if legacy /assignments/me is empty, use section assignments to populate "My Assignments".
      const derivedFromSections = (Array.isArray(mySectionAssignJson) ? mySectionAssignJson : [])
        .map(sa => sa?.packageAssignment)
        .filter(Boolean);
      const mergedMyAssignments =
        (Array.isArray(myAssignJson) && myAssignJson.length > 0)
          ? myAssignJson
          : derivedFromSections;

      setPackages(pkgJson);
      setMyAssignments(mergedMyAssignments);
      setDocuments(docsJson);
      setDocumentRequests(requestsJson);
      setForms(Array.isArray(formsJson) ? formsJson : []);
      setMyCompletedSubmissions(myCompletedJson);
      setMyFormSubmissions(Array.isArray(myFormSubsJson) ? myFormSubsJson : []);
      if (isDirector) {
          const [subsRes, allAssignRes, sectionAssignRes, formSubsRes] = await Promise.all([
            fetch(`${API_ENDPOINTS.ONBOARDING}/submissions`, { headers }),
            fetch(`${API_ENDPOINTS.ONBOARDING}/assignments`, { headers }),
            fetch(`${API_ENDPOINTS.ONBOARDING}/package-section-assignments`, { headers }),
            fetch(`${API_ENDPOINTS.ONBOARDING}/form-submissions`, { headers })
          ]);
        if (subsRes.ok) setSubmissions(await subsRes.json());
          if (allAssignRes.ok) setAllAssignments(await allAssignRes.json());
          if (sectionAssignRes.ok) setPackageSectionAssignments(await sectionAssignRes.json());
          if (formSubsRes.ok) setFormSubmissions(await formSubsRes.json());
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); // eslint-disable-next-line
  }, []);

  const fetchPackageSections = async (packageId) => {
    if (!packageId) return;
    try {
      setPackageSectionsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/packages/${packageId}/sections`, { headers });
      if (!res.ok) throw new Error('Failed to load package sections');
      const data = await res.json();
      setPackageSections(Array.isArray(data) ? data : []);
      const firstId = Array.isArray(data) && data.length > 0 ? data[0].id : null;
      setActivePackageSectionId(firstId);
      setNewItemSectionId(firstId);
    } catch (e) {
      setError(e.message);
      setPackageSections([]);
      setActivePackageSectionId(null);
      setNewItemSectionId(null);
    } finally {
      setPackageSectionsLoading(false);
    }
  };

  useEffect(() => {
    if (isDirector && manageSubTab === 'packageDetails' && manageSelectedPackageId) {
      fetchPackageSections(manageSelectedPackageId);
    } else {
      setPackageSections([]);
      setActivePackageSectionId(null);
      setEditingSectionId(null);
      setEditingItemId(null);
      setNewItemSectionId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirector, manageSubTab, manageSelectedPackageId]);

  useEffect(() => {
    if (!showPackageContentModal) {
      setShowAddSectionFields(false);
      setNewSectionDraft({ title: '', instructions: '' });
      setEditingSectionId(null);
      setEditingItemId(null);
      setNewItemSectionId(activePackageSectionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPackageContentModal]);

  const handleCreatePackage = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/packages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newPackage)
      });
      if (!res.ok) throw new Error('Failed to create package');
      const created = await res.json();
      setShowNewPackageModal(false);
      setNewPackage({ name: '', description: '', category: '' });
      if (created?.id) {
        setManageSelectedPackageId(created.id);
        setManageSubTab('packageDetails');
        updateManageHash('packageDetails', created.id);
        fetchPackageSections(created.id);
      }
      fetchData();
    } catch (e) { setError(e.message); }
  };

  const handleCreatePackageSection = async () => {
    if (!manageSelectedPackageId) return;
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const sortOrder = Math.max(0, ...packageSections.map(s => Number(s.sortOrder) || 0)) + 1;
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/packages/${manageSelectedPackageId}/sections`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...newSectionDraft, sortOrder })
      });
      if (!res.ok) throw new Error('Failed to create section');
      setNewSectionDraft({ title: '', instructions: '' });
      await fetchPackageSections(manageSelectedPackageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleUpdatePackageSection = async (sectionId) => {
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/package-sections/${sectionId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(editingSectionDraft)
      });
      if (!res.ok) throw new Error('Failed to update section');
      setEditingSectionId(null);
      setEditingSectionDraft({ title: '', instructions: '' });
      await fetchPackageSections(manageSelectedPackageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeletePackageSection = async (sectionId) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/package-sections/${sectionId}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete section');
      await fetchPackageSections(manageSelectedPackageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleCreatePackageItem = async () => {
    if (!newItemSectionId) return;
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      const targetSection = packageSections.find(s => s.id === newItemSectionId);
      const sortOrder = Math.max(0, ...(targetSection?.items || []).map(i => Number(i.sortOrder) || 0)) + 1;
      let parsedFormSchema = null;
      if (newItemDraft.itemType === 'info_form') {
        if (newItemDraft.formId) {
          const selectedForm = forms.find((f) => Number(f.id) === Number(newItemDraft.formId));
          parsedFormSchema = selectedForm?.schema || {};
        } else {
          parsedFormSchema = (() => { try { return newItemDraft.formSchemaJson ? JSON.parse(newItemDraft.formSchemaJson) : {}; } catch (_) { return null; } })();
        }
      } else if (newItemDraft.itemType === 'file_upload' && newItemDraft.contentMode === 'reference_files') {
        parsedFormSchema = {
          contentMode: 'reference_files',
          referenceFiles: []
        };
      }
      const { referenceFilesPending, ...draftWithoutPending } = newItemDraft;
      const itemPayload = {
        ...draftWithoutPending,
        formId: newItemDraft.itemType === 'info_form' ? (newItemDraft.formId || null) : null,
        sortOrder,
        // Backend expects JSON schema object, not raw text.
        formSchema: parsedFormSchema
      };
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/package-sections/${newItemSectionId}/items`, {
        method: 'POST',
        headers,
        body: JSON.stringify(itemPayload)
      });
      if (!res.ok) throw new Error('Failed to create item');
      const created = await res.json();

      // Upload reference files (for reference_files mode) immediately when saving the item.
      if (newItemDraft.itemType === 'file_upload' && newItemDraft.contentMode === 'reference_files' && created?.id) {
        const pending = Array.isArray(newItemDraft.referenceFilesPending) ? newItemDraft.referenceFilesPending : [];
        for (const rf of pending) {
          const fd = new FormData();
          fd.append('file', rf.file);
          fd.append('completionMode', rf.completionMode || 'acknowledge_read');
          const uploadRes = await fetch(`${API_ENDPOINTS.ONBOARDING}/package-items/${created.id}/template-files`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd
          });
          if (!uploadRes.ok) {
            const msg = await uploadRes.text().catch(() => '');
            throw new Error(`Failed to upload reference file: ${msg || uploadRes.statusText}`);
          }
        }
      }

      setNewItemDraft({ ...initialItemDraft, referenceFilesPending: [] });
      setNewItemSectionId(null);
      await fetchPackageSections(manageSelectedPackageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleUpdatePackageItem = async (itemId) => {
    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      let parsedFormSchema = null;
      if (editingItemDraft.itemType === 'info_form') {
        if (editingItemDraft.formId) {
          const selectedForm = forms.find((f) => Number(f.id) === Number(editingItemDraft.formId));
          parsedFormSchema = selectedForm?.schema || {};
        } else {
          parsedFormSchema = (() => { try { return editingItemDraft.formSchemaJson ? JSON.parse(editingItemDraft.formSchemaJson) : {}; } catch (_) { return null; } })();
        }
      } else if (editingItemDraft.itemType === 'file_upload' && editingItemDraft.contentMode === 'reference_files') {
        parsedFormSchema = {
          contentMode: 'reference_files',
          referenceFiles: editingItemDraft.referenceFiles || []
        };
      }
      const { referenceFilesPending, ...draftWithoutPending } = editingItemDraft;
      const itemPayload = {
        ...draftWithoutPending,
        formId: editingItemDraft.itemType === 'info_form' ? (editingItemDraft.formId || null) : null,
        formSchema: parsedFormSchema
      };
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/package-items/${itemId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(itemPayload)
      });
      if (!res.ok) throw new Error('Failed to update item');
      setEditingItemId(null);
      setEditingItemDraft({ ...initialItemDraft, referenceFiles: [], referenceFilesPending: [] });
      await fetchPackageSections(manageSelectedPackageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleUploadReferenceFile = async (itemId, file, completionMode = 'acknowledge_read', isEditing = false) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('completionMode', completionMode);
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/package-items/${itemId}/template-files`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error('Failed to upload reference file');
      const uploaded = await res.json();

      if (isEditing) {
        setEditingItemDraft(prev => ({
          ...prev,
          contentMode: 'reference_files',
          referenceFiles: [...(prev.referenceFiles || []), uploaded]
        }));
      } else {
        setNewItemDraft(prev => ({
          ...prev,
          contentMode: 'reference_files',
          referenceFiles: [...(prev.referenceFiles || []), uploaded]
        }));
      }
      await fetchPackageSections(manageSelectedPackageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeletePackageItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/package-items/${itemId}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error('Failed to delete item');
      await fetchPackageSections(manageSelectedPackageId);
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteAssignment = (assignmentId) => {
    setAssignmentToDelete(assignmentId);
    setShowDeleteModal(true);
  };

  const confirmDeleteAssignment = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/assignments/${assignmentToDelete}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete assignment');
      fetchData();
      setShowDeleteModal(false);
      setAssignmentToDelete(null);
    } catch (e) { 
      setError(e.message); 
      setShowDeleteModal(false);
      setAssignmentToDelete(null);
    }
  };

  const handleCreateDocumentRequest = async (requestData) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/document-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(requestData)
      });
      if (!res.ok) throw new Error('Failed to create document request');
      setShowDocumentRequestModal(false);
      fetchData();
    } catch (e) { setError(e.message); }
  };

  const handleUploadDocument = async (e, packageId = null) => {
    try {
      setUploadingDoc(true);
      const formData = new FormData();
      const file = e.target.files?.[0];
      if (!file) return;
      formData.append('file', file);
      formData.append('title', file.name);
      if (packageId) formData.append('packageId', packageId);
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/documents`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error('Failed to upload');
      fetchData();
    } catch (e) { setError(e.message); } finally { setUploadingDoc(false); e.target.value = ''; }
  };

  const openAssignModal = ({ packageId = null, documentId = null, documentRequestId = null, formId = null }) => {
    let target;
    if (packageId) {
      target = { type: 'package', id: packageId };
    } else if (documentId) {
      target = { type: 'document', id: documentId };
    } else if (documentRequestId) {
      target = { type: 'documentRequest', id: documentRequestId };
    } else if (formId) {
      target = { type: 'form', id: formId };
    }
    setAssignTarget(target);
    setShowAssignModal(true);
  };

  const handleSubmitFormAssignment = async (assignment, formData) => {
    const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/assignments/${assignment.id}/submit-form`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ formData })
    });
    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload?.error || 'Failed to submit form');
    }
    await fetchData();
  };

  const handleSubmitAssignment = async (assignmentId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/assignments/${assignmentId}/submit`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error('Failed to submit');
      fetchData();
    } catch (e) { setError(e.message); }
  };

  const openUploadModal = (assignment, packageId = null) => {
    setActiveUploadAssignment(assignment);
    setActiveUploadPackageId(packageId);
    setShowUploadModal(true);
  };

  const handleUploadMultiple = async (filesWithCategories) => {
    try {
      setUploading(true);
      
      if (activeUploadAssignment) {
        // Store files for assignment submission instead of uploading immediately
        const assignmentId = activeUploadAssignment.id;
        const existingFiles = uploadedFiles[assignmentId] || [];
        const newFiles = filesWithCategories.map(({ file, category }) => ({
          file,
          category: category || '',
          id: Date.now() + Math.random() // Generate unique ID for each file
        }));
        
        setUploadedFiles(prev => ({
          ...prev,
          [assignmentId]: newFiles // Replace existing files instead of adding to them
        }));
        
        // Clear existing categories for this assignment and initialize new ones
        setUploadedFileCategories(prev => {
          const newState = { ...prev };
          // Remove existing categories for this assignment
          Object.keys(newState).forEach(key => {
            const existingFiles = uploadedFiles[assignmentId] || [];
            if (existingFiles.some(f => f.id.toString() === key)) {
              delete newState[key];
            }
          });
          // Add new categories
          newFiles.forEach(({ id, category }) => {
            newState[id] = category;
          });
          return newState;
        });
        
        // Auto-open accordion for standalone document assignments
        if (activeUploadAssignment.document && !activeUploadAssignment.package) {
          // Find the correct index for this assignment in the grouped assignments
          let assignmentIndex = 0;
          for (const group of groupedAssignments) {
            if (group.key === 'standalone') {
              assignmentIndex = group.documentAssignments.findIndex(assignment => assignment.id === activeUploadAssignment.id);
              if (assignmentIndex !== -1) break;
            }
          }
          
          const accordionKey = `assign-${activeUploadAssignment.id}-${assignmentIndex}`;
          console.log('Opening accordion with key:', accordionKey);
          setOpenAccordions(prev => {
            const newSet = new Set([...prev, accordionKey]);
            console.log('Updated openAccordions:', Array.from(newSet));
            return newSet;
          });
        }
      } else {
        // Uploading new documents (this remains the same for document management)
        const uploadPromises = filesWithCategories.map(({ file, category }) => {
          const docFormData = new FormData();
          docFormData.append('file', file);
          docFormData.append('title', file.name);
          if (category) docFormData.append('category', category);
          if (activeUploadPackageId) docFormData.append('packageId', activeUploadPackageId);
          
          return fetch(`${API_ENDPOINTS.ONBOARDING}/documents`, { 
            method: 'POST', 
            headers: { Authorization: `Bearer ${token}` }, 
            body: docFormData 
          });
        });
        
        await Promise.all(uploadPromises);
        await fetchData();
      }
      
      setShowUploadModal(false);
    } catch (e) { setError(e.message); } finally { setUploading(false); }
  };

  const handleReview = async (submissionId, status) => {
    try {
      const reviewNotes = status === 'rejected' ? prompt('Reason for rejection?') : '';
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/submissions/${submissionId}/review`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ status, reviewNotes }) });
      if (!res.ok) throw new Error('Failed to review');
      
      // For rejected submissions, the server returns a message instead of the submission object
      const result = await res.json();
      console.log('Review result:', result);
      
      // Add a small delay to ensure server has processed the deletion
      if (status === 'rejected') {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Refresh data to get updated state
      await fetchData();
    } catch (e) { setError(e.message); }
  };

  const handleReviewFormSubmission = async (submissionId, status) => {
    try {
      const reviewNotes = status === 'rejected' ? prompt('Reason for rejection?') : '';
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/form-submissions/${submissionId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status, reviewNotes })
      });
      if (!res.ok) throw new Error('Failed to review form submission');
      await fetchData();
    } catch (e) {
      setError(e.message);
    }
  };

  const getSubmissionCellKey = (fieldKey, rowIndex, colIndex) => `${fieldKey}__cell_${rowIndex}_${colIndex}`;
  const getSubmissionContainerRows = (container) => {
    if (Array.isArray(container?.containerRows) && container.containerRows.length > 0) return container.containerRows;
    if (Array.isArray(container?.nestedFields) && container.nestedFields.length > 0) return [{ id: `row_${container.id || 'legacy'}`, fields: container.nestedFields }];
    return [];
  };
  const getSubmissionContainerStyle = (field) => {
    const style = {};
    const rawBg = String(field?.backgroundColor || '').trim().toLowerCase();
    if (rawBg && rawBg !== 'transparent' && rawBg !== 'none') style.backgroundColor = field.backgroundColor;
    const borderColor = field?.borderColor || 'var(--gray-400)';
    const sides = Array.isArray(field?.borderSides) ? field.borderSides : [];
    if (sides.includes('all')) style.border = `1px solid ${borderColor}`;
    else {
      if (sides.includes('top')) style.borderTop = `1px solid ${borderColor}`;
      if (sides.includes('right')) style.borderRight = `1px solid ${borderColor}`;
      if (sides.includes('bottom')) style.borderBottom = `1px solid ${borderColor}`;
      if (sides.includes('left')) style.borderLeft = `1px solid ${borderColor}`;
    }
    return style;
  };
  const renderSubmissionField = (field, answers, keyPrefix = 'field', parentContainer = null) => {
    const key = field?.key || field?.id || `${keyPrefix}_key`;
    const type = field?.type || 'text';
    const value = answers?.[key];
    const formatPhoneValue = (input) => {
      const digits = String(input || '').replace(/\D/g, '').slice(0, 10);
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    };
    const formatSsnValue = (input) => {
      const digits = String(input || '').replace(/\D/g, '').slice(0, 9);
      if (digits.length <= 3) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    };
    const maskSsnValue = (input) => {
      const formatted = formatSsnValue(input);
      if (!formatted) return '';
      const digits = formatted.replace(/\D/g, '');
      const last4 = digits.slice(-4);
      return last4 ? `***-**-${last4}` : '***-**-****';
    };
    const inputStyle = {
      border: 0,
      borderBottom: '1px solid var(--gray-400)',
      borderRadius: 0,
      backgroundColor: 'transparent',
      paddingLeft: 0,
      paddingRight: 0
    };

    if (type === 'header') {
      const Tag = field.headingLevel || 'h3';
      const alignment = field.headerAlignment || 'left';
      const alignmentClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-end' : 'text-start';
      return (
        <div className={alignmentClass}>
          <Tag className="mb-1">{field.label}</Tag>
          {field.headerSubtitle ? <div className="text-muted fw-semibold mb-1">{field.headerSubtitle}</div> : null}
          {field.headerDescription ? <p className="text-muted small mb-2">{field.headerDescription}</p> : null}
        </div>
      );
    }
    if (type === 'textinfo') return <div className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: field.textContent || '' }} />;
    if (type === 'header_logo_content') {
      const container = parentContainer || {};
      return (
        <div className={`d-flex flex-row gap-2 align-items-center ${container.alignment === 'center' ? 'justify-content-center' : container.alignment === 'right' ? 'justify-content-end' : 'justify-content-start'}`}>
          {container.logo ? <img src={container.logo} alt="Form logo" style={{ maxHeight: 48, objectFit: 'contain' }} /> : null}
          <div className="d-flex flex-column">
            {container.headerTitle ? <h5 className="mb-0 fw-semibold">{container.headerTitle}</h5> : null}
            {container.headerSubtitle ? <p className="text-muted mb-0">{container.headerSubtitle}</p> : null}
          </div>
        </div>
      );
    }
    if (type === 'divider') {
      const spacing = Number(field.dividerSpacing ?? 16);
      const dividerStyle = field.dividerStyle || 'solid';
      const dividerThickness = Number(field.dividerThickness || 1);
      const dividerColor = field.dividerColor || 'var(--gray-400)';
      return (
        <div style={{ paddingTop: spacing, paddingBottom: spacing }}>
          <hr className="m-0" style={{ borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}` }} />
        </div>
      );
    }
    if (type === 'spacer') return <div style={{ height: Number(field.spacerHeight || 24) }} />;
    if (type === 'signature') {
      const signed = value && typeof value === 'object' && value.status === 'signed';
      const displayStyle = value?.signatureDisplay?.style === 'typed' ? 'typed' : 'cursive';
      return (
        <Form.Group>
          <Form.Label className="fw-semibold mb-1">
            {field?.label || key} {field?.required ? <span className="text-danger">*</span> : null}
          </Form.Label>
          {!signed ? (
            <div className="text-muted small">Not signed</div>
          ) : (
            <>
              <div className={`mb-1 ${getSignaturePrintNameClass(displayStyle)}`}>{value.signerName || value.signatureDisplay?.renderedText}</div>
              <div className="small text-muted mb-2">
                Signed {value.signedAt ? new Date(value.signedAt).toLocaleString() : ''}
                {value.audit?.email ? ` · ${value.audit.email}` : ''}
              </div>
              {Array.isArray(value.consents) && value.consents.length > 0 && (
                <ul className="small mb-0 ps-3">
                  {value.consents.map((c) => (
                    <li key={c.id}>
                      <span className={c.checked ? 'text-success' : 'text-muted'}>{c.checked ? '✓' : '○'}</span> {c.text}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Form.Group>
      );
    }
    if (type === 'table' && Array.isArray(field.tableData)) {
      return (
        <div className="d-flex flex-column gap-1">
          {field.tableTitle ? <div className="fw-semibold">{field.tableTitle}</div> : null}
          {field.tableSubtitle ? <div className="text-muted fw-semibold">{field.tableSubtitle}</div> : null}
          {field.tableDescription ? <p className="text-muted small mb-1">{field.tableDescription}</p> : null}
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <tbody>
                {field.tableData.map((row, r) => (
                  <tr key={`${key}_row_${r}`}>
                    {row.map((cell, c) => {
                      const isHeaderOrFirstCol = r === 0 || c === 0;
                      const hasTemplateText = String(cell?.content || '').trim().length > 0;
                      const CellTag = r === 0 ? 'th' : 'td';
                      const cellKey = getSubmissionCellKey(key, r, c);
                      return (
                        <CellTag key={cell.id || `${key}_${r}_${c}`}>
                          {isHeaderOrFirstCol && hasTemplateText ? (
                            <div>{cell.content}</div>
                          ) : (
                            <Form.Control
                              value={answers?.[cellKey] ?? ''}
                              disabled
                              plaintext
                              readOnly
                              style={inputStyle}
                            />
                          )}
                        </CellTag>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    const isResidentChoiceField = type === 'checkbox' || type === 'dropdown' || type === 'select';
    const displayChoiceTitle = field?.choiceTitle || field?.label || key;

    return (
      <Form.Group>
        {!['header', 'textinfo', 'header_logo_content', 'divider', 'spacer', 'table', 'signature'].includes(type) && !isResidentChoiceField && (
          <Form.Label className="fw-semibold mb-1">
            {field?.label || key} {field?.required ? <span className="text-danger">*</span> : null}
          </Form.Label>
        )}
        {isResidentChoiceField && (displayChoiceTitle || field?.choiceDescription || field?.required) ? (
          <>
            {(displayChoiceTitle || field?.required) ? (
              <Form.Label className="fw-semibold mb-1">
                {displayChoiceTitle || ''} {field?.required ? <span className="text-danger">*</span> : null}
              </Form.Label>
            ) : null}
            {field?.choiceDescription ? (
              <div className="text-muted small mb-2">{field.choiceDescription}</div>
            ) : null}
          </>
        ) : null}
        {type === 'checkbox' ? (
          <Form.Check checked={Boolean(value)} disabled readOnly />
        ) : type === 'yes_no' || type === 'true_false' ? (
          <Form.Control
            value={value === true ? (type === 'yes_no' ? 'Yes' : 'True') : value === false ? (type === 'yes_no' ? 'No' : 'False') : ''}
            disabled
            plaintext
            readOnly
            style={inputStyle}
          />
        ) : type === 'dropdown' || type === 'select' ? (
          <Form.Control value={value ?? ''} disabled plaintext readOnly style={inputStyle} />
        ) : type === 'phone' ? (
          <Form.Control value={formatPhoneValue(value) ?? ''} disabled plaintext readOnly style={inputStyle} />
        ) : type === 'ssn' ? (
          <Form.Control value={maskSsnValue(value) ?? ''} disabled plaintext readOnly style={inputStyle} />
        ) : type === 'textarea' ? (
          <Form.Control as="textarea" rows={3} value={value ?? ''} disabled plaintext readOnly style={inputStyle} />
        ) : type === 'rich_textarea' ? (
          <div className="border rounded p-2 bg-light-subtle" dangerouslySetInnerHTML={{ __html: value || '<p class="text-muted mb-0">No response provided.</p>' }} />
        ) : (
          <Form.Control value={value ?? ''} disabled plaintext readOnly style={inputStyle} />
        )}
      </Form.Group>
    );
  };
  const renderSchemaAnswerPreview = (schema, answers) => {
    const fields = Array.isArray(schema?.fields) ? schema.fields : [];
    const evaluateConditionalRule = (rule) => {
      if (!rule?.sourceFieldId) return true;
      return answers?.[rule.sourceFieldId] === true;
    };
    if (!fields.length) {
      return (
        <div className="bg-light rounded-3 p-2 small">
          {Object.keys(answers).length > 0
            ? Object.entries(answers).map(([k, v]) => (
                <div key={`fallback-answer-${k}`}>
                  <span className="text-muted">{k}: </span>
                  <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))
            : <span className="text-muted">No submission content.</span>}
        </div>
      );
    }
    return (
      <div className="d-flex flex-column gap-3">
        {fields.map((field, idx) => (
          <div key={field?.id || `submission-field-${idx}`}>
            {!evaluateConditionalRule(field.conditionalRule) ? null : (!field?.isContainer ? (
              <div>{renderSubmissionField(field, answers, `top_${idx}`)}</div>
            ) : (
              <div
                className={`rounded ${((field.backgroundColor && !['transparent', 'none'].includes(String(field.backgroundColor).toLowerCase())) || (Array.isArray(field.borderSides) && field.borderSides.length > 0)) ? 'p-3' : ''}`}
                style={getSubmissionContainerStyle(field)}
              >
                {(field.sectionTitle || field.sectionSubtitle || field.sectionDescription) && (
                  <>
                    {field.sectionTitle && <h6 className="mb-1 fw-semibold">{field.sectionTitle}</h6>}
                    {field.sectionSubtitle && <div className="text-muted fw-semibold mb-1">{field.sectionSubtitle}</div>}
                    {field.sectionDescription && <p className="text-muted small mb-2">{field.sectionDescription}</p>}
                  </>
                )}
                <div className="d-flex flex-column gap-2">
                  {getSubmissionContainerRows(field).map((row, rowIdx) => {
                    if (!evaluateConditionalRule(row.conditionalRule)) return null;
                    const rowHasBooleanChoice = (row.fields || []).some((nf) => ['yes_no', 'true_false'].includes(nf?.type));
                    return (
                    <div key={row.id || `submission-row-${rowIdx}`}>
                      <div className={`d-flex gap-2 ${rowHasBooleanChoice ? 'align-items-end' : ''}`}>
                        {(row.fields || []).map((nf, nfIdx) => (
                          !evaluateConditionalRule(nf.conditionalRule) ? null : (
                          <div key={nf.id || `submission-nf-${nfIdx}`} style={{ flex: nf.flexBasis || 1 }}>
                            {renderSubmissionField(nf, answers, `nested_${idx}_${rowIdx}_${nfIdx}`, field)}
                          </div>
                          )
                        ))}
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };
  const renderFormSubmissionPreview = (submission) => {
    const resolvedSchema = submission?.form?.schema || submission?.assignment?.form?.schema || {};
    const answers = submission?.formData || {};
    return renderSchemaAnswerPreview(resolvedSchema, answers);
  };
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const formatPrintPhoneValue = (input) => {
    const digits = String(input || '').replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };
  const formatPrintSsnValue = (input) => {
    const digits = String(input || '').replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  };
  const maskPrintSsnValue = (input) => {
    const formatted = formatPrintSsnValue(input);
    if (!formatted) return '';
    const digits = formatted.replace(/\D/g, '');
    const last4 = digits.slice(-4);
    return last4 ? `***-**-${last4}` : '***-**-****';
  };

  const buildSubmissionFieldPrintHtml = (field, answers, keyPrefix = 'field', parentContainer = null) => {
    const key = field?.key || field?.id || `${keyPrefix}_key`;
    const type = field?.type || 'text';
    const value = answers?.[key];

    const inputLike = (innerHtml) =>
      `<div style="padding:8px 0;border-bottom:1px solid #d4e0ed;min-height:22px;color:#252F4A;">${innerHtml}</div>`;

    if (type === 'header') {
      const lvl = String(field?.headingLevel || 'h3').toLowerCase();
      const tag = /^h[1-6]$/.test(lvl) ? lvl : 'h3';
      const align = field?.headerAlignment === 'center' ? 'center' : field?.headerAlignment === 'right' ? 'right' : 'left';
      return `
        <div style="text-align:${align};margin-bottom:8px;">
          <${tag} style="margin:0 0 4px;font-size:${tag === 'h1' ? '1.75rem' : tag === 'h2' ? '1.5rem' : '1.25rem'};">${escapeHtml(field?.label ?? '')}</${tag}>
          ${field?.headerSubtitle ? `<div style="color:#78829D;font-weight:600;margin-bottom:4px;">${escapeHtml(field.headerSubtitle)}</div>` : ''}
          ${field?.headerDescription ? `<p style="color:#78829D;font-size:13px;margin:0 0 8px;">${escapeHtml(field.headerDescription)}</p>` : ''}
        </div>`;
    }
    if (type === 'textinfo') {
      return `<div style="color:#78829D;margin-bottom:12px;">${field?.textContent || ''}</div>`;
    }
    if (type === 'header_logo_content') {
      const container = parentContainer || {};
      const jc = container.alignment === 'center' ? 'center' : container.alignment === 'right' ? 'flex-end' : 'flex-start';
      return `
        <div style="display:flex;flex-direction:row;gap:10px;align-items:center;justify-content:${jc};margin-bottom:12px;">
          ${container.logo ? `<img src="${escapeHtml(container.logo)}" alt="" style="max-height:36px;object-fit:contain;" />` : ''}
          <div>
            ${container.headerTitle ? `<h5 style="margin:0;font-size:18px;font-weight:600;">${escapeHtml(container.headerTitle)}</h5>` : ''}
            ${container.headerSubtitle ? `<p style="margin:0;color:#78829D;font-size:14px;">${escapeHtml(container.headerSubtitle)}</p>` : ''}
          </div>
        </div>`;
    }
    if (type === 'divider') {
      const spacing = Number(field?.dividerSpacing ?? 16);
      const dividerStyle = field?.dividerStyle || 'solid';
      const dividerThickness = Number(field?.dividerThickness || 1);
      const dividerColor = field?.dividerColor || '#d4e0ed';
      return `<div style="padding-top:${spacing}px;padding-bottom:${spacing}px;"><hr style="margin:0;border:none;border-top:${dividerThickness}px ${dividerStyle} ${dividerColor};" /></div>`;
    }
    if (type === 'spacer') {
      return `<div style="height:${Number(field?.spacerHeight || 24)}px;"></div>`;
    }
    if (type === 'table' && Array.isArray(field.tableData)) {
      const rowsHtml = field.tableData.map((row, r) => {
        const cells = (row || []).map((cell, c) => {
          const isHeaderOrFirstCol = r === 0 || c === 0;
          const hasTemplateText = String(cell?.content || '').trim().length > 0;
          const cellKey = getSubmissionCellKey(key, r, c);
          const tag = r === 0 ? 'th' : 'td';
          const pad = '10px 12px';
          const border = '1px solid #eaedf2';
          if (isHeaderOrFirstCol && hasTemplateText) {
            return `<${tag} style="padding:${pad};border:${border};background:${r === 0 ? '#f8f9fb' : '#fff'};text-align:left;">${cell?.content || ''}</${tag}>`;
          }
          const v = answers?.[cellKey] ?? '';
          return `<${tag} style="padding:${pad};border:${border};text-align:left;">${escapeHtml(String(v))}</${tag}>`;
        }).join('');
        return `<tr>${cells}</tr>`;
      }).join('');
      return `
        <div style="margin-bottom:16px;">
          ${field.tableTitle ? `<div style="font-size:16px;font-weight:600;margin-bottom:4px;">${escapeHtml(field.tableTitle)}</div>` : ''}
          ${field.tableSubtitle ? `<div style="color:#78829D;font-size:14px;font-weight:600;">${escapeHtml(field.tableSubtitle)}</div>` : ''}
          ${field.tableDescription ? `<p style="color:#78829D;font-size:14px;margin:4px 0 8px;">${escapeHtml(field.tableDescription)}</p>` : ''}
          <table style="width:100%;border-collapse:collapse;font-size:14px;"><tbody>${rowsHtml}</tbody></table>
        </div>`;
    }
    if (type === 'signature') {
      const signed = value && typeof value === 'object' && value.status === 'signed';
      const displayStyle = value?.signatureDisplay?.style === 'typed' ? 'typed' : 'cursive';
      const nameClass = getSignaturePrintNameClass(displayStyle);
      const label = escapeHtml(field?.label || key);
      const reqMark = field?.required ? ' <span style="color:#F8285A;">*</span>' : '';
      if (!signed) {
        return `<div style="margin-bottom:16px;page-break-inside:avoid;">
          <div style="font-weight:600;margin-bottom:4px;color:#252F4A;">${label}${reqMark}</div>
          <div style="color:#78829D;">Not signed</div>
        </div>`;
      }
      const signerName = escapeHtml(value.signerName || value.signatureDisplay?.renderedText || '');
      const signedAtRaw = value.signedAt || value.audit?.signedAt;
      const signedAtStr = signedAtRaw ? escapeHtml(new Date(signedAtRaw).toLocaleString()) : '';
      const email = value.audit?.email ? escapeHtml(value.audit.email) : '';
      const ip = value.audit?.ipAddress ? escapeHtml(String(value.audit.ipAddress)) : '';
      const consentsHtml = (Array.isArray(value.consents) ? value.consents : [])
        .map((c) => {
          const mark = c.checked ? '☑' : '☐';
          const markColor = c.checked ? '#198754' : '#78829D';
          return `<li style="margin:0 0 6px;list-style:none;">
            <span style="color:${markColor};font-size:16px;margin-right:6px;">${mark}</span>
            <span>${escapeHtml(c.text || '')}</span>
          </li>`;
        })
        .join('');
      const metaParts = [`Signed ${signedAtStr}`];
      if (email) metaParts.push(email);
      if (ip) metaParts.push(`IP: ${ip}`);
      return `<div style="margin-bottom:16px;page-break-inside:avoid;border:1px solid #eaedf2;border-radius:8px;padding:12px;background:#fafbfc;">
        <div style="font-weight:600;margin-bottom:8px;color:#252F4A;">${label}${reqMark}</div>
        <div class="${nameClass}" style="margin-bottom:8px;color:#252F4A;">${signerName}</div>
        <div style="font-size:13px;color:#78829D;margin-bottom:${consentsHtml ? '10px' : '0'};">${metaParts.filter(Boolean).join(' · ')}</div>
        ${consentsHtml ? `<ul style="margin:0;padding:0;font-size:13px;color:#252F4A;">${consentsHtml}</ul>` : ''}
      </div>`;
    }

    const isResidentChoiceField = type === 'checkbox' || type === 'dropdown' || type === 'select';
    const displayChoiceTitle = field?.choiceTitle || field?.label || key;
    let labelBlock = '';
    if (!['header', 'textinfo', 'header_logo_content', 'divider', 'spacer', 'table', 'signature'].includes(type) && !isResidentChoiceField) {
      labelBlock = `
        <div style="font-weight:600;margin-bottom:4px;color:#252F4A;">
          ${escapeHtml(field?.label || key)}${field?.required ? ' <span style="color:#F8285A;">*</span>' : ''}
        </div>`;
    } else if (isResidentChoiceField && (displayChoiceTitle || field?.choiceDescription || field?.required)) {
      labelBlock = `
        ${(displayChoiceTitle || field?.required)
          ? `<div style="font-weight:600;margin-bottom:4px;">${escapeHtml(displayChoiceTitle || '')}${field?.required ? ' <span style="color:#F8285A;">*</span>' : ''}</div>`
          : ''}
        ${field?.choiceDescription ? `<div style="color:#78829D;font-size:14px;margin-bottom:8px;">${escapeHtml(field.choiceDescription)}</div>` : ''}`;
    }

    let valueHtml = '';
    if (type === 'checkbox') {
      valueHtml = `<span style="font-size:18px;">${Boolean(value) ? '☑' : '☐'}</span>`;
    } else if (type === 'yes_no' || type === 'true_false') {
      const t = value === true ? (type === 'yes_no' ? 'Yes' : 'True') : value === false ? (type === 'yes_no' ? 'No' : 'False') : '';
      valueHtml = inputLike(escapeHtml(t));
    } else if (type === 'dropdown' || type === 'select') {
      valueHtml = inputLike(escapeHtml(String(value ?? '')));
    } else if (type === 'phone') {
      valueHtml = inputLike(escapeHtml(formatPrintPhoneValue(value)));
    } else if (type === 'ssn') {
      valueHtml = inputLike(escapeHtml(maskPrintSsnValue(value)));
    } else if (type === 'textarea') {
      valueHtml = `<div style="padding:8px 0;border:1px solid #eaedf2;border-radius:6px;padding:10px;white-space:pre-wrap;min-height:60px;background:#fafbfc;">${escapeHtml(String(value ?? ''))}</div>`;
    } else if (type === 'rich_textarea') {
      valueHtml = `<div style="border:1px solid #eaedf2;border-radius:6px;padding:10px;background:#f8f9fb;">${value || '<p style="color:#78829D;margin:0;">No response provided.</p>'}</div>`;
    } else {
      valueHtml = inputLike(escapeHtml(String(value ?? '')));
    }

    return `<div style="margin-bottom:16px;page-break-inside:avoid;">${labelBlock}${valueHtml}</div>`;
  };

  const buildSchemaFormPrintHtml = (schema, answers) => {
    const fields = Array.isArray(schema?.fields) ? schema.fields : [];
    const evaluateConditionalRule = (rule) => {
      if (!rule?.sourceFieldId) return true;
      return answers?.[rule.sourceFieldId] === true;
    };
    if (!fields.length) {
      const entries = Object.entries(answers || {});
      if (!entries.length) return '<p style="color:#78829D;">No submission content.</p>';
      return `<div style="display:flex;flex-direction:column;gap:20px;">${entries.map(([k, v]) => {
        const display = typeof v === 'object' ? escapeHtml(JSON.stringify(v)) : escapeHtml(String(v ?? ''));
        return `<div><span style="color:#78829D;">${escapeHtml(k)}: </span><span>${display || '—'}</span></div>`;
      }).join('')}</div>`;
    }
    return fields.map((field, idx) => {
      if (!evaluateConditionalRule(field?.conditionalRule)) return '';
      if (!field?.isContainer) {
        return buildSubmissionFieldPrintHtml(field, answers, `top_${idx}`);
      }
      const contStyle = getSubmissionContainerStyle(field);
      const styleStr = [
        contStyle.backgroundColor ? `background-color:${contStyle.backgroundColor};` : '',
        contStyle.border ? `border:${contStyle.border};` : '',
        contStyle.borderTop ? `border-top:${contStyle.borderTop};` : '',
        contStyle.borderRight ? `border-right:${contStyle.borderRight};` : '',
        contStyle.borderBottom ? `border-bottom:${contStyle.borderBottom};` : '',
        contStyle.borderLeft ? `border-left:${contStyle.borderLeft};` : '',
        'border-radius:8px;padding:12px;'
      ].join('');
      const headerHtml = (field.sectionTitle || field.sectionSubtitle || field.sectionDescription)
        ? `${field.sectionTitle ? `<h6 style="margin:0 0 4px;font-size:16px;font-weight:600;">${escapeHtml(field.sectionTitle)}</h6>` : ''}
           ${field.sectionSubtitle ? `<div style="color:#7829D; font-size:14px;font-weight:600;margin-bottom:4px;">${escapeHtml(field.sectionSubtitle)}</div>` : ''}
           ${field.sectionDescription ? `<p style="color:#7829D;font-size:14px;margin:0 0 10px;">${escapeHtml(field.sectionDescription)}</p>` : ''}`
        : '';
      const rowsHtml = getSubmissionContainerRows(field).map((row, rowIdx) => {
        if (!evaluateConditionalRule(row?.conditionalRule)) return '';
        const cells = (row.fields || []).map((nf, nfIdx) => {
          if (!evaluateConditionalRule(nf?.conditionalRule)) return '';
          const flex = nf?.flexBasis != null ? Number(nf.flexBasis) || 1 : 1;
          const inner = buildSubmissionFieldPrintHtml(nf, answers, `nested_${idx}_${rowIdx}_${nfIdx}`, field);
          return `<div style="flex:${flex};min-width:0;">${inner}</div>`;
        }).filter(Boolean).join('');
        return `<div><div style="display:flex;gap:20px;align-items:flex-end;flex-wrap:wrap;">${cells}</div></div>`;
      }).join('');
      return `<div style="${styleStr}">${headerHtml}${rowsHtml}</div>`;
    }).join('');
  };

  const handlePrintFormSubmission = ({
    title = 'Form Submission',
    description = '',
    formData = {},
    schema = null,
    submittedAt = null
  }) => {
    const resolvedSchema = schema && typeof schema === 'object' ? schema : {};
    const formBody = buildSchemaFormPrintHtml(resolvedSchema, formData || {});
    const content = `
      <div class="print-section page-break-avoid">
        <div class="form-print-body" style="font-size:14px;line-height:1.45;">
          ${formBody}
        </div>
      </div>
    `;
    const html = buildPrintDocument({
      title: `${title} - Export`,
      headerTitle: title,
      headerSubtitle: 'Onboarding Form Submission',
      content,
      showHeader: true,
      showPrintButton: true,
      additionalHeaderInfo: submittedAt ? [{ label: 'Submitted', value: new Date(submittedAt).toLocaleString() }] : []
    });
    openPrintWindow(html);
  };

  const safePdfFieldName = (value) => String(value || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .slice(0, 80);

  const handleExportFillablePdfForm = async (formDef) => {
    try {
      const schema = formDef?.schema || {};
      const fields = Array.isArray(schema?.fields) ? schema.fields : [];
      const pdfDoc = await PDFDocument.create();
      const form = pdfDoc.getForm();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      const pageWidth = 612;
      const pageHeight = 792;
      const margin = 48;
      let page = pdfDoc.addPage([pageWidth, pageHeight]);
      let y = pageHeight - margin;
      let fieldCounter = 0;


      const ensureSpace = (needed = 34) => {
        if (y - needed > margin) return;
        page = pdfDoc.addPage([pageWidth, pageHeight]);
        y = pageHeight - margin;
      };
      const resolvePdfColor = (value, fallback = rgb(0.6, 0.64, 0.72)) => {
        const color = String(value || '').trim().toLowerCase();
        const varMap = {
          'var(--gray-400)': rgb(0.83, 0.88, 0.93),
          'var(--gray-500)': rgb(0.6, 0.63, 0.72),
          'var(--gray-600)': rgb(0.47, 0.51, 0.62),
          'var(--gray-700)': rgb(0.29, 0.34, 0.46),
          'var(--gray-800)': rgb(0.15, 0.19, 0.29),
          'var(--gray-900)': rgb(0.03, 0.08, 0.22),
          'var(--red)': rgb(0.97, 0.16, 0.35),
          'var(--green)': rgb(0.0, 0.85, 0.68),
          'var(--blue)': rgb(0.0, 0.63, 0.83)
        };
        if (varMap[color]) return varMap[color];
        const hex = color.replace('#', '');
        if (/^[0-9a-f]{6}$/i.test(hex)) {
          const r = parseInt(hex.slice(0, 2), 16) / 255;
          const g = parseInt(hex.slice(2, 4), 16) / 255;
          const b = parseInt(hex.slice(4, 6), 16) / 255;
          return rgb(r, g, b);
        }
        if (/^[0-9a-f]{3}$/i.test(hex)) {
          const r = parseInt(hex[0] + hex[0], 16) / 255;
          const g = parseInt(hex[1] + hex[1], 16) / 255;
          const b = parseInt(hex[2] + hex[2], 16) / 255;
          return rgb(r, g, b);
        }
        return fallback;
      };
      const normalizeText = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
      const makeFieldName = (key) => `${safePdfFieldName(key || `field_${fieldCounter}`)}_${fieldCounter++}`;
      const drawBottomLine = (x, width, lineY) => {
        page.drawLine({
          start: { x, y: lineY },
          end: { x: x + width, y: lineY },
          thickness: 0.5,
          color: resolvePdfColor('var(--gray-600)')
        });
      };
      const setFieldFontSizeSafely = (fieldRef, size = 12) => {
        if (!fieldRef || typeof fieldRef.setFontSize !== 'function') return;
        try {
          fieldRef.setFontSize(size);
        } catch (_) {
          // ignore DA issues; appearances are finalized by updateFieldAppearances()
        }
      };
      const estimateInputHeight = (field) => {
        const type = String(field?.type || 'text').toLowerCase();
        if (type === 'textarea' || type === 'rich_textarea') return 48;
        if (type === 'table' && Array.isArray(field?.tableData)) return Math.max(40, field.tableData.length * 24 + 16);
        return 20;
      };
      const drawFillableField = (field, x, width) => {
        const type = String(field?.type || 'text').toLowerCase();
        const key = field?.key || field?.id || `field_${fieldCounter}`;
        const label = normalizeText(field?.choiceTitle || field?.label || key || 'Field');
        const isChoice = type === 'checkbox' || type === 'dropdown' || type === 'select';
        const isMultiline = type === 'textarea' || type === 'rich_textarea';
        const inputHeight = estimateInputHeight(field);

        const choiceDescription = normalizeText(field?.choiceDescription || '');
        ensureSpace(inputHeight + (choiceDescription ? 44 : 30));
        if (type !== 'table') {
          page.drawText(label, {
            x,
            y,
            size: 10,
            font,
            color: resolvePdfColor('var(--gray-800)')
          });
        }
        y -= 5;
        if (isChoice && choiceDescription) {
          page.drawText(choiceDescription.slice(0, 140), {
            x,
            y,
            size: 9,
            font,
            color: resolvePdfColor('var(--gray-600)')
          });
          y -= 10;
        }

        const fieldY = y - inputHeight;
        if (type === 'checkbox') {
          const checkbox = form.createCheckBox(makeFieldName(key));
          checkbox.addToPage(page, {
            x,
            y: fieldY + 2,
            width: 14,
            height: 14,
            borderColor: resolvePdfColor('var(--gray-600)'),
            borderWidth: 0.5
          });
        } else if (type === 'yes_no' || type === 'true_false') {
          const radio = form.createRadioGroup(makeFieldName(key));
          const trueLabel = type === 'yes_no' ? 'Yes' : 'True';
          const falseLabel = type === 'yes_no' ? 'No' : 'False';
          const optY = fieldY + 5;
          radio.addOptionToPage('true', page, { x, y: optY, width: 12, height: 12, borderColor: resolvePdfColor('var(--gray-600)'), borderWidth: 1 });
          page.drawText(trueLabel, { x: x + 18, y: optY + 1, size: 10, font });
          radio.addOptionToPage('false', page, { x: x + 90, y: optY, width: 12, height: 12, borderColor: resolvePdfColor('var(--gray-600)'), borderWidth: 1 });
          page.drawText(falseLabel, { x: x + 108, y: optY + 1, size: 10, font });
        } else if (type === 'dropdown' || type === 'select') {
          const dropdown = form.createDropdown(makeFieldName(key));
          const rawOptions = Array.isArray(field?.options) ? field.options : [];
          const optionValues = rawOptions
            .map((option) => (typeof option === 'string' ? option : option?.label || option?.value))
            .filter(Boolean)
            .map((val) => String(val));
          if (optionValues.length > 0) dropdown.addOptions(optionValues);
          dropdown.addToPage(page, {
            x,
            y: fieldY,
            width,
            height: 20,
            borderColor: rgb(1, 1, 1),
            borderWidth: 0
          });
          setFieldFontSizeSafely(dropdown, 12);
          drawBottomLine(x, width, fieldY - 1);
        } else if (type === 'table' && Array.isArray(field?.tableData)) {
          const rowH = 24;
          field.tableData.forEach((row, r) => {
            const cols = (row || []).length || 1;
            const colW = width / cols;
            (row || []).forEach((cell, c) => {
              const cellX = x + c * colW;
              const cellTopY = y - r * rowH;
              const cellBottomY = cellTopY - rowH;
              const isHeaderOrFirstCol = r === 0 || c === 0;
              const hasTemplateText = String(cell?.content || '').trim().length > 0;
              const headerRowFill = rgb(0.973, 0.976, 0.98);
              const stubColFill = rgb(0.96, 0.97, 0.99);
              const cellFill = r === 0
                ? headerRowFill
                : (isHeaderOrFirstCol && hasTemplateText ? stubColFill : undefined);
              page.drawRectangle({
                x: cellX,
                y: cellBottomY,
                width: colW,
                height: rowH,
                color: cellFill,
                borderColor: resolvePdfColor('var(--gray-600)'),
                borderWidth: 0.5
              });
              if (isHeaderOrFirstCol && hasTemplateText) {
                page.drawText(String(cell.content).slice(0, 40), {
                  x: cellX + 4,
                  y: cellTopY - 14,
                  size: 9,
                  font: boldFont,
                  color: resolvePdfColor('var(--gray-800)')
                });
                return;
              }
              const cellKey = `${key}__cell_${r}_${c}`;
              const textField = form.createTextField(makeFieldName(cellKey));
              textField.addToPage(page, {
                x: cellX + 4,
                y: cellTopY - rowH + 5,
                width: colW - 8,
                height: rowH - 10,
          
              });
              setFieldFontSizeSafely(textField, 12);
              drawBottomLine(cellX + 4, colW - 8, cellTopY - rowH + 3);
            });
          });
          y = y - (field.tableData.length * rowH) - 6;
          return;
        } else {
          const textField = form.createTextField(makeFieldName(key));
          if (isMultiline || type === 'textarea') textField.enableMultiline();
          textField.addToPage(page, {
            x,
            y: fieldY,
            width,
            height: inputHeight,
            borderColor: rgb(1, 1, 1),
            borderWidth: 0
          });
          setFieldFontSizeSafely(textField, 12);
          drawBottomLine(x, width, fieldY - 1);
        }
        y = fieldY - 8;
      };

      const drawFieldBlock = (field, idx, x, width, parentContainer = null) => {
        if (!field) return;
        const type = String(field?.type || 'text').toLowerCase();
        if (type === 'header') {
          ensureSpace(34);
          const heading = normalizeText(field.label || '');
          const align = String(field?.headerAlignment || 'left').toLowerCase();
          const textWidth = boldFont.widthOfTextAtSize(heading, 14);
          const getAlignedX = (text, size = 10, useBold = false) => {
            const targetFont = useBold ? boldFont : font;
            const widthAtSize = targetFont.widthOfTextAtSize(String(text || ''), size);
            return align === 'center'
              ? x + Math.max(0, (width - widthAtSize) / 2)
              : align === 'right'
                ? x + Math.max(0, width - widthAtSize)
                : x;
          };
          const drawX = align === 'center'
            ? x + Math.max(0, (width - textWidth) / 2)
            : align === 'right'
              ? x + Math.max(0, width - textWidth)
              : x;
          page.drawText(heading, { x: drawX, y, size: 12, font: boldFont, color: resolvePdfColor('var(--gray-800)') });
          y -= 14;
          if (field?.headerSubtitle) {
            const subtitle = normalizeText(field.headerSubtitle);
            const subX = getAlignedX(subtitle, 10, false);
            page.drawText(subtitle, { x: subX, y, size: 10, font, color: resolvePdfColor('var(--gray-600)') });
            y -= 10;
          }
          if (field?.headerDescription) {
            const description = normalizeText(field.headerDescription).slice(0, 220);
            const descX = getAlignedX(description, 10, false);
            page.drawText(description, { x: descX, y, size: 10, font, color: resolvePdfColor('var(--gray-600)') });
            y -= 14;
          }
          return;
        }
        if (type === 'textinfo') {
          ensureSpace(20);
          page.drawText(normalizeText(field.textContent || '').slice(0, 220), { x, y, size: 10, font, color: resolvePdfColor('var(--gray-600)') });
          y -= 18;
          return;
        }
        if (type === 'divider') {
          const spacing = Number(field?.dividerSpacing ?? 16);
          ensureSpace(spacing + 8);
          y -= Math.max(6, Math.floor(spacing / 2));
          page.drawLine({
            start: { x, y },
            end: { x: x + width, y },
            thickness: Number(field?.dividerThickness || 1),
            color: resolvePdfColor(field?.dividerColor || 'var(--gray-400)')
          });
          y -= Math.max(6, Math.ceil(spacing / 2));
          return;
        }
        if (type === 'spacer') {
          y -= Number(field?.spacerHeight || 24);
          return;
        }
        if (type === 'header_logo_content') {
          if (parentContainer?.headerTitle) {
            ensureSpace(10);
            page.drawText(normalizeText(parentContainer.headerTitle), { x, y, size: 12, font: boldFont, color: rgb(0.1, 0.15, 0.3) });
            y -= 14;
          }
          if (parentContainer?.headerSubtitle) {
            ensureSpace(16);
            page.drawText(normalizeText(parentContainer.headerSubtitle), { x, y, size: 10, font, color: rgb(0.35, 0.4, 0.5) });
            y -= 10;
          }
          return;
        }
        drawFillableField(field, x, width);
      };

      const drawFormRows = (list, x, width, parentContainer = null) => {
        (list || []).forEach((field, idx) => {
          if (!field) return;
          if (field.isContainer) {
            const sides = Array.isArray(field?.borderSides) ? field.borderSides : [];
            const hasContainerBorder = sides.includes('all') || sides.includes('top') || sides.includes('right') || sides.includes('bottom') || sides.includes('left');
            const containerPadding = hasContainerBorder ? 10 : 0;
            const contentX = x + containerPadding;
            const contentWidth = Math.max(40, width - containerPadding * 2);
            if (field.sectionTitle || field.sectionSubtitle || field.sectionDescription) {
              ensureSpace(34);
              if (field.sectionTitle) {
                page.drawText(normalizeText(field.sectionTitle), { x: contentX, y, size: 12, font: boldFont, color: resolvePdfColor('var(--gray-800)') });
                y -= 16;
              }
              if (field.sectionSubtitle) {
                page.drawText(normalizeText(field.sectionSubtitle), { x: contentX, y, size: 10, font, color: resolvePdfColor('var(--gray-600)') });
                y -= 14;
              }
              if (field.sectionDescription) {
                page.drawText(normalizeText(field.sectionDescription).slice(0, 220), { x: contentX, y, size: 10, font, color: resolvePdfColor('var(--gray-600)') });
                y -= 16;
              }
            }
            getSubmissionContainerRows(field).forEach((row) => {
              const rowFields = (row?.fields || []).filter(Boolean);
              if (!rowFields.length) return;
              const rowEstimatedHeight = Math.max(
                ...rowFields.map((rf) => {
                  const t = String(rf?.type || 'text').toLowerCase();
                  const base = estimateInputHeight(rf);
                  const hasChoiceDescription = ['checkbox', 'dropdown', 'select'].includes(t) && normalizeText(rf?.choiceDescription || '').length > 0;
                  return base + (hasChoiceDescription ? 38 : 26);
                }),
                36
              );
              ensureSpace(rowEstimatedHeight + 12);
              const totalFlex = rowFields.reduce((sum, rf) => sum + (Number(rf?.flexBasis) || 1), 0);
              let cursorX = contentX;
              const startY = y;
              let rowBottomY = startY;
              const gap = 12;
              rowFields.forEach((rf, rfIdx) => {
                const flex = Number(rf?.flexBasis) || 1;
                const w = ((contentWidth - (rowFields.length - 1) * gap) * flex) / totalFlex;
                y = startY;
                drawFieldBlock(rf, rfIdx, cursorX, w, field);
                rowBottomY = Math.min(rowBottomY, y);
                cursorX += w + gap;
              });
              const borderColor = resolvePdfColor(field?.borderColor || 'var(--gray-400)');
              const rowTopY = startY + 6;
              const rowBottomBorderY = rowBottomY + 8;
              if (sides.includes('all') || sides.includes('top')) {
                page.drawLine({ start: { x, y: rowTopY }, end: { x: x + width, y: rowTopY }, thickness: 1, color: borderColor });
              }
              if (sides.includes('all') || sides.includes('right')) {
                page.drawLine({ start: { x: x + width, y: rowTopY }, end: { x: x + width, y: rowBottomBorderY }, thickness: 1, color: borderColor });
              }
              if (sides.includes('all') || sides.includes('bottom')) {
                page.drawLine({ start: { x, y: rowBottomBorderY }, end: { x: x + width, y: rowBottomBorderY }, thickness: 1, color: borderColor });
              }
              if (sides.includes('all') || sides.includes('left')) {
                page.drawLine({ start: { x, y: rowTopY }, end: { x, y: rowBottomBorderY }, thickness: 1, color: borderColor });
              }
              y = rowBottomY - 10;
            });
            y -= 12;
            return;
          }
          drawFieldBlock(field, idx, x, width, parentContainer);
        });
      };

      if (!fields.length) {
        page.drawText('No fillable fields were found in this form schema.', {
          x: margin,
          y: y - 20,
          size: 11,
          font,
          color: rgb(0.4, 0.4, 0.4)
        });
      } else {
        drawFormRows(fields, margin, pageWidth - margin * 2, null);
      }

      form.updateFieldAppearances(font);
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(formDef?.title || 'form').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_') || 'form'}_fillable.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Failed to export fillable PDF.');
    }
  };

  const getLatestMyFormSubmissionForAssignment = (assignmentId) => {
    const list = (myFormSubmissions || []).filter((s) => Number(s.assignmentId) === Number(assignmentId));
    if (!list.length) return null;
    return [...list].sort((a, b) => new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0))[0];
  };
  const getLatestNonRejectedMyFormSubmissionForAssignment = (assignmentId) => {
    const list = (myFormSubmissions || []).filter(
      (s) => Number(s.assignmentId) === Number(assignmentId) && String(s.status || '').toLowerCase() !== 'rejected'
    );
    if (!list.length) return null;
    return [...list].sort((a, b) => new Date(b.createdAt || b.updatedAt || 0) - new Date(a.createdAt || a.updatedAt || 0))[0];
  };

  // Review a package section (approve/reject). This feeds the new section-based flow.
  const handleSectionReview = async (sectionAssignmentId, status) => {
    try {
      const reviewNotes = status === 'rejected' ? prompt('Reason for rejection?') : '';
      const res = await fetch(
        `${API_ENDPOINTS.ONBOARDING}/package-section-assignments/${sectionAssignmentId}/review`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ status, reviewNotes })
        }
      );
      if (!res.ok) throw new Error('Failed to review section');
      await fetchData();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDeleteDocument = (documentId) => {
    setDocumentToDelete(documentId);
    setShowDeleteDocumentModal(true);
  };

  const confirmDeleteDocument = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/documents/${documentToDelete}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete document');
      fetchData();
      setShowDeleteDocumentModal(false);
      setDocumentToDelete(null);
    } catch (e) { 
      setError(e.message); 
      setShowDeleteDocumentModal(false);
      setDocumentToDelete(null);
    }
  };

  const handleDeletePackage = (packageId) => {
    setPackageToDelete(packageId);
    setShowDeletePackageModal(true);
  };

  const confirmDeletePackage = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/packages/${packageToDelete}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete package');
      fetchData();
      setShowDeletePackageModal(false);
      setPackageToDelete(null);
    } catch (e) { 
      setError(e.message); 
      setShowDeletePackageModal(false);
      setPackageToDelete(null);
    }
  };

  const handleDeleteDocumentRequest = (requestId) => {
    setDocumentRequestToDelete(requestId);
    setShowDeleteDocumentRequestModal(true);
  };

  const confirmDeleteDocumentRequest = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/document-requests/${documentRequestToDelete}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to delete document request');
      fetchData();
      setShowDeleteDocumentRequestModal(false);
      setDocumentRequestToDelete(null);
    } catch (e) { 
      setError(e.message); 
      setShowDeleteDocumentRequestModal(false);
      setDocumentRequestToDelete(null);
    }
  };

  const togglePackageDocumentSelection = (documentId) => {
    setSelectedPackageDocuments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(documentId)) {
        newSet.delete(documentId);
      } else {
        newSet.add(documentId);
      }
      return newSet;
    });
  };

  const toggleStandaloneDocumentSelection = (documentId) => {
    setSelectedStandaloneDocuments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(documentId)) {
        newSet.delete(documentId);
      } else {
        newSet.add(documentId);
      }
      return newSet;
    });
  };

  const handleBulkDeletePackageDocuments = () => {
    if (selectedPackageDocuments.size === 0) return;
    setBulkDeleteType('package-documents');
    setBulkDeleteCount(selectedPackageDocuments.size);
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDeletePackageDocuments = async () => {
    try {
      const deletePromises = Array.from(selectedPackageDocuments).map(documentId =>
        fetch(`${API_ENDPOINTS.ONBOARDING}/documents/${documentId}`, { 
          method: 'DELETE', 
          headers: { Authorization: `Bearer ${token}` } 
        })
      );
      
      await Promise.all(deletePromises);
      setSelectedPackageDocuments(new Set());
      fetchData();
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    } catch (e) { 
      setError(e.message); 
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    }
  };

  const handleBulkDeleteStandaloneDocuments = () => {
    if (selectedStandaloneDocuments.size === 0) return;
    setBulkDeleteType('standalone-documents');
    setBulkDeleteCount(selectedStandaloneDocuments.size);
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDeleteStandaloneDocuments = async () => {
    try {
      const deletePromises = Array.from(selectedStandaloneDocuments).map(documentId =>
        fetch(`${API_ENDPOINTS.ONBOARDING}/documents/${documentId}`, { 
          method: 'DELETE', 
          headers: { Authorization: `Bearer ${token}` } 
        })
      );
      
      await Promise.all(deletePromises);
      setSelectedStandaloneDocuments(new Set());
      fetchData();
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    } catch (e) { 
      setError(e.message); 
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    }
  };

  const toggleDocumentRequestSelection = (requestId) => {
    setSelectedDocumentRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const handleBulkDeleteDocumentRequests = () => {
    if (selectedDocumentRequests.size === 0) return;
    setBulkDeleteType('document-requests');
    setBulkDeleteCount(selectedDocumentRequests.size);
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDeleteDocumentRequests = async () => {
    try {
      const deletePromises = Array.from(selectedDocumentRequests).map(requestId =>
        fetch(`${API_ENDPOINTS.ONBOARDING}/document-requests/${requestId}`, { 
          method: 'DELETE', 
          headers: { Authorization: `Bearer ${token}` } 
        })
      );
      
      await Promise.all(deletePromises);
      setSelectedDocumentRequests(new Set());
      fetchData();
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    } catch (e) { 
      setError(e.message); 
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    }
  };

  const togglePackageDocumentRequestSelection = (requestId) => {
    setSelectedPackageDocumentRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const handleBulkDeletePackageDocumentRequests = () => {
    if (selectedPackageDocumentRequests.size === 0) return;
    setBulkDeleteType('package-document-requests');
    setBulkDeleteCount(selectedPackageDocumentRequests.size);
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDeletePackageDocumentRequests = async () => {
    try {
      const deletePromises = Array.from(selectedPackageDocumentRequests).map(requestId =>
        fetch(`${API_ENDPOINTS.ONBOARDING}/document-requests/${requestId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      
      await Promise.all(deletePromises);
      setSelectedPackageDocumentRequests(new Set());
      fetchData();
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    } catch (e) { 
      setError(e.message); 
      setShowBulkDeleteModal(false);
      setBulkDeleteType(null);
      setBulkDeleteCount(0);
    }
  };

  // New functions for managing uploaded files
  const removeUploadedFile = (assignmentId, fileId) => {
    setUploadedFiles(prev => {
      const assignmentFiles = prev[assignmentId] || [];
      const updatedFiles = assignmentFiles.filter(f => f.id !== fileId);
      if (updatedFiles.length === 0) {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      }
      return { ...prev, [assignmentId]: updatedFiles };
    });
    
    setUploadedFileCategories(prev => {
      const newState = { ...prev };
      delete newState[fileId];
      return newState;
    });
  };

  const submitUploadedFiles = async (assignmentId) => {
    try {
      const files = uploadedFiles[assignmentId] || [];
      if (files.length === 0) return;
      
      setUploading(true);
      const formData = new FormData();
      files.forEach(({ file }) => formData.append('files', file));
      
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/assignments/${assignmentId}/submit-multi`, { 
        method: 'POST', 
        headers: { Authorization: `Bearer ${token}` }, 
        body: formData 
      });
      
      if (!res.ok) throw new Error('Failed to submit files');
      
      // Store submitted files for display (this is now handled by server data)
      // The submitted files will be available in myCompletedSubmissions after fetchData()
      
      // Clear uploaded files for this assignment
      setUploadedFiles(prev => {
        const newState = { ...prev };
        delete newState[assignmentId];
        return newState;
      });
      
      // Clear categories for this assignment's files
      setUploadedFileCategories(prev => {
        const newState = { ...prev };
        files.forEach(({ id }) => delete newState[id]);
        return newState;
      });
      
      // Close accordion for standalone document assignments after submission
      let assignmentIndex = 0;
      for (const group of groupedAssignments) {
        if (group.key === 'standalone') {
          assignmentIndex = group.documentAssignments.findIndex(assignment => assignment.id === assignmentId);
          if (assignmentIndex !== -1) break;
        }
      }
      
      const accordionKey = `assign-${assignmentId}-${assignmentIndex}`;
      setOpenAccordions(prev => {
        const newSet = new Set(prev);
        newSet.delete(accordionKey);
        return newSet;
      });
      
      await fetchData();
    } catch (e) { setError(e.message); } finally { setUploading(false); }
  };

  return (
    <div>
      <PageTitle title="Onboarding" description="Manage and complete onboarding documents" />
      <Navbar pageTitle="Onboarding" />

      <div className="bg-white shadow-sm custom-sticky-top">
      <Container className="mb-4">

        <Row>
          <Col lg={2} md={6} sm={6} className=" d-flex py-lg-0 py-md-3 py-3 align-items-center justify-content-start">
            <h6 className="mb-0 fw-semibold">Onboarding</h6>
          </Col>
          <Col lg={10} md={12} sm={12}>
            <Nav variant="tabs" className="scrollable-tabs" activeKey={activeTab} onSelect={(k) => {
              setActiveTab(k);
              // Clear resident selection when switching away from submissions tab
              if (k !== 'submissions') {
                setSelectedSubmitterId(null);
              }
            }} style={{marginBottom: '-1px', overflow: 'auto hidden', flexWrap: 'nowrap', whiteSpace: 'nowrap'}}>
              {isDirector && (
                <Nav.Item>
                  <Nav.Link eventKey="manage">Manage</Nav.Link>
                </Nav.Item>
              )}
              {isDirector && (
                <Nav.Item>
                  <Nav.Link eventKey="submissions">Submissions</Nav.Link>
                </Nav.Item>
              )}
              <Nav.Item>
                <Nav.Link eventKey="my">My Assignments</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="completed">Completed</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
        </Row>
      </Container>
    </div>

      <Container className="mb-4">
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <>
            {isDirector && activeTab === 'manage' && (
              <>
                <Row className="mb-3">
                  {/* Mobile sub-tab menu as accordion */}
                  <Col md={12} className="d-md-none mb-3">
                    <Card className="bg-white shadow-sm border-0 p-0">
                      <Accordion>
                        <Accordion.Item eventKey="menu" className="border-0 custom-accordian-border">
                          <Accordion.Header>
                            <span className="fw-semibold">Menu</span>
                          </Accordion.Header>
                          <Accordion.Body className="p-0">
                            <Nav variant="pills" className="flex-column mb-2">
                              <Nav.Item>
                                <Nav.Link eventKey="dashboard" active={manageSubTab==='dashboard'} onClick={() => { setManageSubTab('dashboard'); updateManageHash('dashboard', null); }}>Dashboard</Nav.Link>
                              </Nav.Item>
                              <Nav.Item>
                                <Nav.Link eventKey="packages" active={manageSubTab==='packages'} onClick={() => { setManageSubTab('packages'); updateManageHash('packages', null); }}>Packages</Nav.Link>
                              </Nav.Item>
                              <Nav.Item>
                                <Nav.Link eventKey="documents" active={manageSubTab==='documents'} onClick={() => { setManageSubTab('documents'); updateManageHash('documents', null); }}>Standalone Documents</Nav.Link>
                              </Nav.Item>
                              <Nav.Item>
                                <Nav.Link eventKey="requests" active={manageSubTab==='requests'} onClick={() => { setManageSubTab('requests'); updateManageHash('requests', null); }}>Document Requests</Nav.Link>
                              </Nav.Item>
                              <Nav.Item>
                                <Nav.Link eventKey="forms" active={manageSubTab==='forms'} onClick={() => { setManageSubTab('forms'); updateManageHash('forms', null); }}>Forms</Nav.Link>
                              </Nav.Item>
                              {manageSubTab === 'packageDetails' && (
                                <Nav.Item>
                                  <Nav.Link eventKey="packageDetails" active>Package Details</Nav.Link>
                                </Nav.Item>
                              )}
                            </Nav>
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </Card>
                  </Col>
                  <Col lg={2} md={3} className="d-none d-md-block mb-3">
                    <Card className="bg-white shadow-sm border-0 p-0 py-2 custom-sticky-top" style={{ top: '5rem' }}>
                      <Nav variant="pills" className="flex-column">
                        <Nav.Item>
                          <Nav.Link className="disabled fw-semibold">Menu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="dashboard" active={manageSubTab==='dashboard'} onClick={() => { setManageSubTab('dashboard'); updateManageHash('dashboard', null); }}>Dashboard</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="packages" active={manageSubTab==='packages'} onClick={() => { setManageSubTab('packages'); updateManageHash('packages', null); }}>Packages</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="documents" active={manageSubTab==='documents'} onClick={() => { setManageSubTab('documents'); updateManageHash('documents', null); }}>Standalone Documents</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="requests" active={manageSubTab==='requests'} onClick={() => { setManageSubTab('requests'); updateManageHash('requests', null); }}>Document Requests</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                          <Nav.Link eventKey="forms" active={manageSubTab==='forms'} onClick={() => { setManageSubTab('forms'); updateManageHash('forms', null); }}>Forms</Nav.Link>
                        </Nav.Item>
                        {manageSubTab === 'packageDetails' && (
                          <Nav.Item>
                            <Nav.Link eventKey="packageDetails" active>Package Details</Nav.Link>
                          </Nav.Item>
                        )}
                      </Nav>
                    </Card>
                  </Col>
                  <Col lg={10} md={9}>
                    {manageSubTab === 'dashboard' && (
                      <>
                        {/* Statistics Cards */}
                        <Row>
                          <Col lg={3} md={6} sm={6} className="mb-3 col-6">
                            <Card className="h-100 border-0 shadow-sm package-bg-undraw-card">
                              <Card.Body className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <h6 className="mb-0 fw-semibold">Total Packages</h6>
                                  </div>
                                  <h3 className="mb-0 fw-bold">{packages.length}</h3>
                                  <small className="opacity-75">Active packages</small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col lg={3} md={6} sm={6} className="mb-3 col-6">
                            <Card className="h-100 border-0 shadow-sm document-bg-undraw-card">
                              <Card.Body className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <h6 className="mb-0 fw-semibold">Total Documents</h6>
                                  </div>
                                  <h3 className="mb-0 fw-bold">{documents.length}</h3>
                                  <small className="opacity-75">Uploaded files</small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col lg={3} md={6} sm={6} className="mb-3 col-6">
                            <Card className="h-100 border-0 shadow-sm assignment-bg-undraw-card">
                              <Card.Body className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <h6 className="mb-0 fw-semibold">Active Assignments</h6>
                                  </div>
                                  <h3 className="mb-0 fw-bold">{allAssignments.length}</h3>
                                  <small className="opacity-75">Pending submissions</small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col lg={3} md={6} sm={6} className="mb-3 col-6">
                            <Card className="h-100 border-0 shadow-sm request-bg-undraw-card">
                              <Card.Body className="d-flex align-items-center">
                                <div className="flex-grow-1">
                                  <div className="d-flex align-items-center mb-2">
                                    <h6 className="mb-0 fw-semibold">Document Requests</h6>
                                  </div>
                                  <h3 className="mb-0 fw-bold">{documentRequests.length}</h3>
                                  <small className="opacity-75">Pending requests</small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        {/* Get Started */}
                        <Row className="mb-4">
                          <Col md={12}>
                            <Card className="border-0 shadow-sm p-3">
                              <Card.Header className="border-0 mb-3">
                                <h6 className="mb-0 fw-semibold d-flex align-items-center">
                                  Get Started
                                </h6>
                              </Card.Header>
                              <Card.Body className="p-0">
                                <Row>
                                  <Col lg={3} md={6} sm={6} className="mb-3 col-6 quick-actions-onboarding">
                                    <Button 
                                      size="sm"
                                      variant="outline-primary" 
                                      className="w-100 h-100 d-flex flex-row gap-3 align-items-center justify-content-start p-3"
                                      onClick={() => setShowNewPackageModal(true)}
                                      style={{borderStyle:'dashed', borderWidth:'1px'}}
                                    >
                                      <i className="bi bi-folder-plus fs-2 text-primary mb-2"></i>
                                      <div className="d-flex flex-column text-start">
                                      <span className="fw-semibold">Create Package</span>
                                      <small className="text-muted">Group documents</small>
                                      </div>
                                    </Button>
                                  </Col>
                                  <Col lg={3} md={6} sm={6} className="mb-3 col-6 quick-actions-onboarding">
                                    <Button 
                                      size="sm"
                                      variant="outline-success" 
                                      className="w-100 h-100 d-flex flex-row gap-4 align-items-center justify-content-start p-3" 
                                      style={{borderStyle:'dashed', borderWidth:'1px'}}
                                      onClick={() => openUploadModal()}
                                    >
                                      <i className="bi bi-file-earmark-plus fs-2 text-success mb-2"></i>
                                      <div className="d-flex flex-column text-start">
                                      <span className="fw-semibold">Upload Document</span>
                                      <small className="text-muted">Add standalone file</small>
                                      </div>
                                    </Button>
                                  </Col>
                                  <Col lg={3} md={6} sm={6} className="mb-3 col-6 quick-actions-onboarding">
                                    <Button 
                                      size="sm"
                                      variant="outline-secondary" 
                                      className="w-100 h-100 d-flex flex-row gap-3 align-items-center justify-content-start p-3"
                                      style={{borderStyle:'dashed', borderWidth:'1px'}}
                                      onClick={() => { setDocumentRequestPackageId(null); setShowDocumentRequestModal(true); }}
                                    >
                                      <i className="bi bi-file-earmark-text fs-2 text-muted mb-2"></i>
                                      <div className="d-flex flex-column text-start">
                                      <span className="fw-semibold">Request Document</span>
                                      <small className="text-muted">Create document request</small>
                                      </div>
                                    </Button>
                                  </Col>
                                  <Col lg={3} md={6} sm={6} className="mb-3 col-6 quick-actions-onboarding">
                                    <Button 
                                      size="sm"
                                      variant="outline-info" 
                                      className="w-100 h-100 d-flex flex-row gap-3 align-items-center justify-content-start p-3"
                                      style={{borderStyle:'dashed', borderWidth:'1px'}}
                                      onClick={() => { window.location.href = '/onboarding/forms/new'; }}                                    >
                                      <i className="bi bi-ui-checks-grid fs-2 text-info mb-2"></i>
                                      <div className="d-flex flex-column text-start">
                                      <span className="fw-semibold">Create Form</span>
                                      <small className="text-muted">Build a new form</small>
                                      </div>
                                    </Button>
                                  </Col>
                                </Row>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        {/* Recent Activity */}
                        <Row>
                          <Col lg={6} className="mb-4">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="p-4 border-bottom">
                                  <h6 className="mb-0 fw-semibold d-flex align-items-center">
                                    Recent Activity
                                  </h6>
                                </Card.Header>
                                <Card.Body className="p-3 mb-2" style={{maxHeight: '680px', overflowY: 'auto'}}>
                                  {(() => {
                                    // Combine all recent items with timestamps
                                    const allItems = [
                                      ...packages.map(pkg => {
                                        const templateItems = Number(pkg.itemCount) || 0;
                                        const legacyPackageDocs = documents.filter(d => d.packageId === pkg.id).length;
                                        const itemBadge = templateItems > 0 ? templateItems : legacyPackageDocs;
                                        const usingTemplateItems = templateItems > 0;
                                        return {
                                          id: `pkg-${pkg.id}`,
                                          type: 'package',
                                          title: pkg.name,
                                          date: new Date(pkg.createdAt),
                                          icon: 'bi-folder-fill text-warning',
                                          badge: itemBadge,
                                          countSingular: usingTemplateItems ? 'item' : 'doc',
                                          countPlural: usingTemplateItems ? 'items' : 'docs'
                                        };
                                      }),
                                      ...documents.map(doc => ({
                                        id: `doc-${doc.id}`,
                                        type: 'document',
                                        title: doc.title,
                                        date: new Date(doc.createdAt),
                                        icon: getFileIcon(doc.originalName, doc.mimeType),
                                        badge: doc.packageId ? 'Package' : 'Document',
                                        badgeText: doc.packageId ? 'Package' : 'Document'
                                      })),
                                      ...documentRequests.map(req => ({
                                        id: `req-${req.id}`,
                                        type: 'request',
                                        title: req.title,
                                        date: new Date(req.createdAt),
                                        icon: 'bi-file-earmark-plus-fill bg-light',
                                        badge: req.packageId ? 'Package' : 'Request',
                                        badgeText: req.packageId ? 'Package' : 'Request'
                                      })),
                                      ...forms.map(form => ({
                                        id: `form-${form.id}`,
                                        type: 'form',
                                        title: form.title,
                                        date: new Date(form.createdAt),
                                        icon: 'bi-ui-checks-grid text-info',
                                        badge: 'Form',
                                        badgeText: 'Form'
                                      }))
                                    ];

                                    // Sort by date (most recent first) and take top 10
                                    const recentItems = allItems
                                      .sort((a, b) => b.date - a.date)
                                      .slice(0, 10);

                                    if (recentItems.length === 0) {
                                      return (
                                        <div className="text-center py-4 text-muted">
                                          <i className="bi bi-inbox fs-3 mb-2 d-block"></i>
                                          <small>No recent activity</small>
                                        </div>
                                      );
                                    }

                                    const currentYear = new Date().getFullYear();
                                    const now = new Date();
                                    const yesterday = new Date(now);
                                    yesterday.setDate(yesterday.getDate() - 1);

                                    return (
                                      <div className="activity-timeline">
                                        {recentItems.map(item => {
                                          const handleItemClick = () => {
                                            if (item.type === 'package') {
                                              const pkgId = parseInt(item.id.replace('pkg-', ''), 10);
                                              setManageSelectedPackageId(pkgId);
                                              setManageSubTab('packageDetails');
                                              updateManageHash('packageDetails', pkgId);
                                            } else if (item.type === 'document') {
                                              setManageSubTab('documents');
                                              updateManageHash('documents', null);
                                              const docId = parseInt(item.id.replace('doc-', ''), 10);
                                              setSelectedDocumentId(docId);
                                            } else if (item.type === 'request') {
                                              setManageSubTab('requests');
                                              updateManageHash('requests', null);
                                              const reqId = parseInt(item.id.replace('req-', ''), 10);
                                              setSelectedRequestId(reqId);
                                            } else if (item.type === 'form') {
                                              setManageSubTab('forms');
                                              updateManageHash('forms', null);
                                              const formId = parseInt(item.id.replace('form-', ''), 10);
                                              setSelectedFormId(formId);
                                            }
                                          };

                                          const bgColor = 'var(--card-bg)';
                                          const borderColor = 'var(--border-bg)';
                                          const badgeVariant = item.type === 'package'
                                            ? 'primary'
                                            : item.type === 'document'
                                              ? 'success'
                                              : item.type === 'request'
                                                ? 'secondary'
                                                : item.type === 'form'
                                                  ? 'info'
                                                : 'secondary';
                                          let dateLabel;
                                          if (item.date.toDateString() === now.toDateString()) {
                                            dateLabel = 'Today';
                                          } else if (item.date.toDateString() === yesterday.toDateString()) {
                                            dateLabel = 'Yesterday';
                                          } else {
                                            dateLabel = item.date.toLocaleDateString('en-US', {
                                              month: 'short',
                                              day: 'numeric',
                                              year: item.date.getFullYear() !== currentYear ? 'numeric' : undefined
                                            });
                                          }
                                          const formattedTime = item.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                          const typeLabel = item.type.charAt(0).toUpperCase() + item.type.slice(1);
                                          const badgeLabel = typeof item.badge === 'number'
                                            ? `${item.badge} ${item.badge === 1 ? (item.countSingular || 'item') : (item.countPlural || 'items')}`
                                            : (item.badgeText || item.badge || typeLabel);

                                          return (
                                            <div
                                              key={item.id}
                                              className="activity-timeline-item assignment-hover"
                                              style={{ cursor: 'pointer' }}
                                              onClick={handleItemClick}
                                            >
                                              <div
                                                className="activity-timeline-icon"
                                                style={{
                                                  backgroundColor: bgColor,
                                                  border: `1px dashed ${borderColor}`
                                                }}
                                              >
                                                <i className={`bi ${item.icon}`} style={{ fontSize: '18px' }}></i>
                                              </div>
                                              <div className="activity-content ps-3 d-flex justify-content-between align-items-center gap-3">
                                                <div >
                                                  <div className="activity-header">{item.title}</div>
                                                  <div className="activity-meta">
                                                    <span>{dateLabel}</span>
                                                    <span>•</span>
                                                    <span>{formattedTime}</span>
                                                    <span>•</span>
                                                    <span>{typeLabel}</span>
                                                  </div>
                                                </div>
                                                <Badge bg={badgeVariant} className="rounded-pill text-capitalize">
                                                  {badgeLabel}
                                                </Badge>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </Card.Body>
                            </Card>
                          </Col>
                          <Col lg={6} className="mb-4">
                            <Card className="border-0 shadow-sm h-100">
                              <Card.Header className="p-4 border-bottom">
                                <h6 className="mb-0 fw-semibold d-flex align-items-center">
                                  Assignment Status
                                </h6>
                              </Card.Header>
                              <Card.Body className="p-0 mb-2">
                                {allAssignments.length === 0 ? (
                                  <div className="text-center py-4 text-muted">
                                    <i className="bi bi-person-check fs-3 mb-2 d-block"></i>
                                    <small>No assignments yet</small>
                                  </div>
                                ) : (
                                  <div className="list-group list-group-flush">
                                    {allAssignments.slice(0, 5).map(assignment => {
                                      const isPackageAssignment = Boolean(
                                        assignment.packageId && !assignment.documentId && !assignment.documentRequestId
                                      );
                                      const isFormAssignment = Boolean(assignment.formId || assignment.form);
                                      const sectionRowsForPkgAssign = isPackageAssignment
                                        ? packageSectionAssignments.filter(
                                            (sa) => Number(sa.packageAssignmentId) === Number(assignment.id)
                                          )
                                        : [];
                                      const hasDocumentSubmission = submissions.some((s) =>
                                        Number(s.assignmentId) === Number(assignment.id) &&
                                        ['completed', 'submitted', 'approved'].includes(String(s.status || '').toLowerCase())
                                      );
                                      const hasFormSubmission = formSubmissions.some((s) =>
                                        Number(s.assignmentId) === Number(assignment.id) &&
                                        ['submitted', 'approved', 'completed'].includes(String(s.status || '').toLowerCase())
                                      );
                                      let isCompleted =
                                        String(assignment.status || '').toLowerCase() === 'completed' ||
                                        hasDocumentSubmission ||
                                        hasFormSubmission;
                                      let itemsToCompleteLabel = '';
                                      const assignmentTypeLabel = assignment.packageId
                                        ? 'Package'
                                        : assignment.documentId
                                          ? 'Document'
                                          : isFormAssignment
                                            ? 'Form'
                                            : 'Request';
                                      const assignmentTypeBadgeBg = assignment.packageId
                                        ? 'warning'
                                        : assignment.documentId
                                          ? 'primary'
                                          : isFormAssignment
                                            ? 'info'
                                            : 'secondary';
                                      if (isPackageAssignment) {
                                        if (sectionRowsForPkgAssign.length > 0) {
                                          isCompleted = sectionRowsForPkgAssign.every((sa) => sa.status === 'approved');
                                          const { total, remaining } = getPackageAssignmentItemProgress(
                                            assignment.id,
                                            packageSectionAssignments
                                          );
                                          if (total > 0) {
                                            itemsToCompleteLabel =
                                              remaining === 0
                                                ? `${total} item${total === 1 ? '' : 's'} complete`
                                                : `${remaining} item${remaining === 1 ? '' : 's'} to complete`;
                                          }
                                        }
                                      }
                                      return (
                                        <div key={assignment.id} className="list-group-item  d-flex align-items-center justify-content-between" style={{borderBottomStyle:'dashed'}}>
                                          <div className="d-flex align-items-center gap-2">
                                          {(isFormAssignment || isPackageAssignment) ? (
                                            <UserAvatar
                                              user={assignment.assignee}
                                              size={34}
                                              className="me-3 border"
                                              alt={`${assignment.assignee?.firstName || ''} ${assignment.assignee?.lastName || ''}`.trim() || 'Resident avatar'}
                                            />
                                          ) : (
                                            <Badge bg="light" className="rounded-2 p-2 me-3">
                                              <i className={`bi ${
                                                assignment.packageId ?
                                                  'bi-folder-fill text-warning' :
                                                  assignment.documentId ?
                                                  getFileIcon(documents.find(d => d.id === assignment.documentId)?.originalName, documents.find(d => d.id === assignment.documentId)?.mimeType) :
                                                  'bi-file-earmark-plus-fill'
                                              } fs-5`}></i>
                                            </Badge>
                                          )}
                                          <div className="d-flex flex-column">
                                            <div className="fw-semibold small d-flex align-items-center gap-2">
                                              <span>
                                                {assignment.packageId ?
                                                  packages.find(p => p.id === assignment.packageId)?.name || 'Package Assignment' :
                                                  assignment.documentId ?
                                                  documents.find(d => d.id === assignment.documentId)?.title || 'Document Assignment' :
                                                  isFormAssignment ?
                                                  (assignment.customName || assignment.form?.title || forms.find((f) => Number(f.id) === Number(assignment.formId))?.title || 'Form Assignment') :
                                                  'Document Request'
                                                }
                                              </span>
                                              <Badge bg={assignmentTypeBadgeBg} pill className="fw-normal">
                                                {assignmentTypeLabel}
                                              </Badge>
                                            </div>
                                            <span className="text-muted smaller" >
                                              Resident: {assignment.assignee ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}` : 'Unknown'} • Assigned: {new Date(assignment.createdAt).toLocaleDateString()}
                                              {itemsToCompleteLabel ? ` • ${itemsToCompleteLabel}` : ''}
                                            </span>
                                          </div>
                                          </div>
                                          <Badge bg={isCompleted ? 'success' : 'warning'} className="rounded-pill">
                                            {isCompleted ? 'Done' : 'Pending'}
                                          </Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </>
                    )}

                    {manageSubTab === 'packages' && (
                      <Row className="mb-3">
                        <Col md={12}>
                    <Card className="shadow-sm border-0 p-0 h-100">
                      <Card.Header className="px-4 py-3 d-flex flex-row align-items-center justify-content-between">
                        <div className="d-flex flex-column">
                          <h6 className="mb-0 fw-semibold">Packages</h6>
                          <p className="text-muted small mb-0">showing {packages.length} packages</p>
                        </div>
                        <div className="d-flex gap-2">
                          <Button size="sm" variant="light-solid" onClick={() => setShowNewPackageModal(true)}><i className="bi bi-folder-plus"></i></Button>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-0">
                        {packages.length === 0 ? (
                          <div className="text-center py-5">
                            <div className="text-muted mb-3">
                              <i className="bi bi-folder-plus fs-1"></i>
                            </div>
                            <h6 className="text-muted">No packages yet</h6>
                            <p className="text-muted">Create a package to group onboarding documents and requests.</p>
                          </div>
                        ) : (
                                <Table hover responsive className="mb-0">
                                  <thead>
                                    <tr className="custom-header-bg">
                                      <th className="ps-3">Type</th>
                                      <th className="text-start">Name</th>
                                      <th>Category</th>
                                      <th>Files</th>
                                      <th>Assigned</th>
                                      <th>Created</th>
                                      <th className="text-end">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {packages.map(p => {
                              const packageDocs = documents.filter(d => d.packageId === p.id);
                                      const packageRequests = documentRequests.filter(r => r.packageId === p.id);
                              return (
                                        <tr key={`pkg-row-${p.id}`} className="assignment-hover">
                                          <td className="ps-3">
                                            <Badge bg="secondary" className="rounded-2 p-2"><i className="bi bi-folder-fill text-warning fs-5"></i></Badge>
                                          </td>
                                          <td className="text-start" onClick={() => { setManageSelectedPackageId(p.id); setManageSubTab('packageDetails'); updateManageHash('packageDetails', p.id); }} style={{cursor:'pointer'}}>
                                            <div className="fw-semibold">{p.name}</div>
                                          </td>
                                          <td>{p.category || '—'}</td>
                                          <td>
                                            {(() => {
                                              const filesCount = packageDocs.length;
                                              return <Badge bg="secondary">{filesCount}</Badge>;
                                            })()}
                                          </td>
                                          <td>
                                            {(() => {
                                              const uniqueResidents = new Set(
                                                allAssignments
                                                  .filter(a => a.packageId === p.id && !a.documentId && !a.documentRequestId)
                                                  .map(a => a.assignedToId)
                                              );
                                              return <Badge bg="primary">{uniqueResidents.size}</Badge>;
                                            })()}
                                          </td>
                                          <td>{new Date(p.createdAt).toLocaleString()}</td>
                                          <td className="text-end">
                                            <div className="d-inline-flex gap-2">
                                              <Button size="sm" variant="outline-primary" onClick={() => openAssignModal({ packageId: p.id })}><i className="bi bi-person-plus"></i></Button>
                                              <Button size="sm" variant="outline-danger" onClick={() => handleDeletePackage(p.id)}><i className="bi bi-trash"></i></Button>
                                        </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {manageSubTab === 'packageDetails' && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <Card className="shadow-sm border-0 p-0 h-100">
                            <Card.Header className="border-0 px-4 py-3 border-bottom d-flex justify-content-between align-items-center" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                              <div className="d-flex align-items-center gap-2">
                                <Button size="sm" variant="light-solid" className="py-1 px-2" onClick={() => { setManageSelectedPackageId(null); setManageSubTab('packages'); updateManageHash('packages', null); }}>
                                  <i className="bi bi-arrow-left"></i>
                                          </Button>
                                <h5 className="mb-0">{(() => {
                                  const p = packages.find(x => x.id === manageSelectedPackageId);
                                  return p?.name || 'Package Details';
                                })()}</h5>
                                        </div>
                              <div className="d-flex gap-2">
                                {manageSelectedPackageId && (
                                  <>
                                    
                                    <Button
                                      size="sm"
                                      variant="light-solid"
                                      className="d-flex align-items-center text-nowrap"
                                      onClick={() => {
                                        setShowPackageContentModal(true);
                                        setShowAddSectionFields(false);
                                        fetchPackageSections(manageSelectedPackageId);
                                      }}
                                    >
                                      <i className={`bi ${packageSections.length > 0 ? 'bi-pencil-square' : 'bi-plus-circle'}`}></i>
                                      <span className="d-none d-md-inline ms-1">{packageSections.length > 0 ? 'Edit content' : 'Add content'}</span>
                                    </Button>
                                    <Button size="sm" variant="light-solid" className="d-flex align-items-center text-nowrap" onClick={() => openAssignModal({ packageId: manageSelectedPackageId })}><i className="bi bi-person-plus"></i> <span className="d-none d-md-inline ms-1">Assign package</span></Button>
                                  <Button
                                      size="sm"
                                      variant="light-solid"
                                      className="d-flex align-items-center text-nowrap"
                                      onClick={() => setShowPackageAssignmentsSidebar((prev) => !prev)}
                                    >
                                      <i className={`bi ${showPackageAssignmentsSidebar ? 'bi-layout-sidebar-inset' : 'bi-layout-sidebar-inset-reverse'}`}></i>
                                    </Button>
                                  </>
                                      )}
                                      </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                              <Row>
                                <Col lg={showPackageAssignmentsSidebar ? 8 : 12} md={12} className={`no-border-end ${showPackageAssignmentsSidebar ? 'border-end pe-lg-0' : ''}`}>
                              {(() => {
                                const pkg = packages.find(x => x.id === manageSelectedPackageId);
                                if (!pkg) {
                                  return <div className="text-muted p-3">Select a package from Packages tab.</div>;
                                }
                                return (
                                  <div className="p-3">
                                    <small className="mb-2 fw-semibold">Description</small>
                                    {pkg.description && <div className="mb-3 text-muted">{pkg.description}</div>}

                                    <div className="mb-3 d-flex align-items-center justify-content-between">
                                      <small className="fw-semibold">Package preview</small>
                                      <Badge bg="secondary">{packageSections.length}</Badge>
                                    </div>

                                    {packageSectionsLoading ? (
                                      <div className="text-center py-4">
                                        <Spinner animation="border" variant="primary" />
                                      </div>
                                    ) : packageSections.length === 0 ? (
                                      <div className="text-muted p-3 border rounded-3 bg-white">
                                        No sections yet. Use <strong>Add content</strong> to build sections and items. The resident preview on this page will match what assignees see in <strong>My Assignments</strong> once you add content.
                                      </div>
                                    ) : (
                                      <div className="rounded-3 border bg-body p-3">
                                        {packageResidentPreviewPayload && (
                                          <DocumentCompletionPageV2
                                            embedded
                                            packageAssignmentId={ONBOARDING_ADMIN_PREVIEW_PACKAGE_ASSIGNMENT_ID}
                                            adminPreview={packageResidentPreviewPayload}
                                            hideNavbar
                                            showContinueToApp={false}
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                              <hr className="d-md-none d-block"/>
                              </Col>                       
                              {showPackageAssignmentsSidebar && (
                              <Col lg={4} md={12} className="ps-lg-0">
                                <Card className="border-0 p-0 h-100">
                                  <Card.Header className="border-0 px-2 py-3" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                                    <div className="d-flex align-items-center justify-content-between px-2">
                                      <div className="d-flex align-items-center gap-2">
                                        <h6 className="mb-0 fw-semibold">Assignments</h6>
                                      </div>
                                      <Badge bg="secondary" className="px-2 py-1 rounded-pill">
                                        {allAssignments.filter(a => a.packageId === manageSelectedPackageId).length}
                                      </Badge>
                                    </div>
                                  </Card.Header>
                                  <Card.Body className="p-0" style={{maxHeight: '750px', overflowY: 'auto'}}>
                                    {(() => {
                                      const relevantAssignments = allAssignments.filter(a => a.packageId === manageSelectedPackageId);
                                      if (relevantAssignments.length === 0) {
                                        return (
                                          <div className="text-center py-5 px-4">
                                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                                              <i className="bi bi-inbox text-muted fs-1"></i>
                                            </div>
                                            <h6 className="text-muted fw-semibold mb-2">No assignments</h6>
                                            <p className="text-muted small mb-0">No assignments found for this package.</p>
                                          </div>
                                        );
                                      }
                                      return (
                                        <div className="list-group list-group-flush">
                                          {relevantAssignments.map((assignment, i) => (
                                            <div key={`assignment-${assignment.id}-${i}`} className="list-group-item p-2 assignment-hover" style={{borderBottomStyle:'dashed'}}>
                                              <div className="d-flex justify-content-between align-items-center px-2">
                                                <div className="flex-grow-1">
                                                  <div className="d-flex flex-column align-items-start">
                                                    <small className="fw-semibold mb-0">
                                                      {assignment.assignee ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}` : 'Unknown'}
                                                    </small>
                                                    <span className="text-muted" style={{fontSize: '0.75rem'}}> Assigned {new Date(assignment.createdAt).toLocaleDateString()}</span>
                                                  </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-3">
                                                  <div>
                                                    {assignment.status === 'completed' && (
                                                      <i className="bi bi-check-circle-fill text-success"></i>
                                                    )}
                                                    {assignment.status === 'overdue' && (
                                                      <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                                    )}
                                                    {assignment.status !== 'completed' && assignment.status !== 'overdue' && (
                                                      <i className="bi bi-hourglass-split text-warning"></i>
                                                    )}
                                                  </div>
                                                  <Dropdown align="end">
                                                    <Dropdown.Toggle
                                                      variant="light-lite"
                                                      size="sm"
                                                      className="px-2 py-1 no-arrow"
                                                      aria-label="Assignment actions"
                                                    >
                                                      <i className="bi bi-three-dots-vertical"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                      <Dropdown.Item onClick={() => handleDeleteAssignment(assignment.id)}>
                                                        <i className="bi bi-x me-2 text-danger"></i> Remove Assignment
                                                      </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                  </Dropdown>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      );
                                    })()}
                                  </Card.Body>
                                </Card>
                              </Col>
                              )}
                            </Row>
                            </Card.Body>
                          </Card>
                        </Col>
 
                        
                      </Row>
                    )}

                    {manageSubTab === 'documents' && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <Card className="shadow-sm border-0 p-0 h-100 overflow-hidden">
                            <Card.Header className="px-4 py-3 d-flex flex-row align-items-center justify-content-between">
                              <div className="d-flex flex-column">
                                <h6 className="mb-0 fw-semibold">Standalone Documents</h6>
                                <p className="text-muted small mb-0">showing {documents.filter(d => !d.packageId).length} documents</p>
                              </div>
                              <div className="d-flex gap-2">
                                {selectedStandaloneDocuments.size > 0 && (
                                  <Button size="sm" variant="outline-danger" onClick={handleBulkDeleteStandaloneDocuments}>
                                    <i className="bi bi-trash"></i> ({selectedStandaloneDocuments.size})
                                  </Button>
                                )}
                                <Button size="sm" variant="light-solid" onClick={() => openUploadModal(null)}><i className="bi bi-file-earmark-plus"></i></Button>
                              </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                              <Row>
                                <Col lg={8} md={12} className="no-border-end border-end pe-lg-0 ">
                              {documents.filter(d => !d.packageId).length === 0 ? (
                                <div className="text-center py-5">
                                  <div className="text-muted mb-3"><i className="bi bi-file-earmark fs-1"></i></div>
                                  <h6 className="text-muted">No standalone documents</h6>
                                  <p className="text-muted">Upload documents that are not part of a package.</p>
                                </div>
                              ) : (
                                <Table hover responsive className="mb-0">
                                  <thead>
                                    <tr className="custom-header-bg">
                                            <th className="ps-3">Type</th>
                                            <th className="text-start">Title</th>
                                            <th>Category</th>
                                            <th>Uploaded</th>
                                            <th className="text-end">Actions</th>
                                            <th>
                                              <Form.Check
                                                type="checkbox"
                                          checked={documents.filter(d => !d.packageId).length > 0 && documents.filter(d => !d.packageId).every(d => selectedStandaloneDocuments.has(d.id))}
                                                onChange={() => {
                                            const list = documents.filter(d => !d.packageId);
                                            if (list.every(d => selectedStandaloneDocuments.has(d.id))) {
                                              setSelectedStandaloneDocuments(new Set());
                                                  } else {
                                              setSelectedStandaloneDocuments(new Set(list.map(d => d.id)));
                                                  }
                                                }}
                                              />
                                            </th>
                                          </tr>
                                        </thead>
                                <tbody>
                                    {documents.filter(d => !d.packageId).map(d => (
                                      <tr key={`doc-row-${d.id}`} className={`assignment-hover ${selectedDocumentId === d.id ? 'table-active' : ''}`} onClick={() => setSelectedDocumentId(d.id)} style={{cursor:'pointer'}}>
                                        <td className="ps-3"><Badge bg="secondary" className="rounded-2 p-2"><i className={`${getFileIcon(d.originalName, d.mimeType)} fs-5`}></i></Badge></td>
                                              <td className="text-start">
                                                <div className="fw-semibold">{d.title}</div>
                                          <small className="text-muted">Uploaded {new Date(d.createdAt).toLocaleString()}</small>
                                              </td>
                                              <td>{d.category || '—'}</td>
                                              <td>{new Date(d.createdAt).toLocaleString()}</td>
                                              <td className="text-end">
                                          <div className="d-inline-flex gap-2">
                                                <Button size="sm" variant="light" onClick={() => { const url = `${API_ENDPOINTS.PROFILE_PICS}${d.webPath || d.filePath}`; fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(blob => { const dl = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = dl; a.download = d.originalName || d.title; a.click(); URL.revokeObjectURL(dl); }); }}><i className="bi bi-download"></i></Button>
                                            <Button size="sm" variant="outline-primary" onClick={() => openAssignModal({ documentId: d.id })}><i className="bi bi-person-plus"></i></Button>
                                          </div>
                                              </td>
                                               <td>
                                          <Form.Check type="checkbox" checked={selectedStandaloneDocuments.has(d.id)} onChange={() => toggleStandaloneDocumentSelection(d.id)} />
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                              )}
                              <hr className="d-md-none d-block"/>
                              </Col>
                              <Col lg={4} md={12} className="ps-lg-0">
                                <Card className="border-0 p-0 h-100">
                                  <Card.Header className="border-0 px-2 py-3" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                                    <div className="d-flex align-items-center justify-content-between px-2">
                                      <div className="d-flex align-items-center gap-2">
                                        <h6 className="mb-0 fw-semibold">Assignments</h6>
                                      </div>
                                      <Badge bg="secondary" className="px-2 py-1 rounded-pill">
                                        {allAssignments.filter(a => a.documentId && a.documentId === selectedDocumentId).length}
                                      </Badge>
                                    </div>
                                  </Card.Header>
                                  <Card.Body className="p-0" style={{maxHeight: '750px', overflowY: 'auto'}}>
                                    {(() => {
                                      const relevantAssignments = allAssignments.filter(a => a.documentId && a.documentId === selectedDocumentId);
                                      if (relevantAssignments.length === 0) {
                                        return (
                                          <div className="text-center py-5 px-4">
                                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                                              <i className="bi bi-inbox text-muted fs-1"></i>
                                            </div>
                                            <h6 className="text-muted fw-semibold mb-2">No assignments</h6>
                                            <p className="text-muted small mb-0">No document assignments found.</p>
                                          </div>
                                        );
                                      }
                                      return (
                                        <>
                                          <div className="list-group list-group-flush">
                                            {relevantAssignments.map((assignment, i) => (
                                              <div key={`assignment-${assignment.id}-${i}`} className="list-group-item p-2 assignment-hover" style={{borderBottomStyle:'dashed'}}>
                                                <div className="d-flex justify-content-between align-items-center px-2">
                                                  <div className="flex-grow-1">
                                                    <div className="d-flex flex-column align-items-start">
                                                      <small className="fw-semibold mb-0">{assignment.assignee ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}` : 'Unknown'}</small>
                                                      <span className="text-muted" style={{fontSize: '0.75rem'}}> Assigned {new Date(assignment.createdAt).toLocaleDateString()}</span>
                                                    </div>
                                                  </div>
                                                  <div className="d-flex align-items-center gap-3">
                                                    <div>
                                                      {assignment.status === 'completed' && (
                                                        <i className="bi bi-check-circle-fill text-success"></i>
                                                      )}
                                                      {assignment.status === 'overdue' && (
                                                        <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                                      )}
                                                      {assignment.status !== 'completed' && assignment.status !== 'overdue' && (
                                                        <i className="bi bi-hourglass-split text-warning"></i>
                                                      )}
                                                    </div>
                                                    <Dropdown align="end">
                                                      <Dropdown.Toggle
                                                        variant="light-lite"
                                                        size="sm"
                                                        className="px-2 py-1 no-arrow"
                                                        aria-label="Assignment actions"
                                                      >
                                                        <i className="bi bi-three-dots-vertical"></i>
                                                      </Dropdown.Toggle>
                                                      <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => handleDeleteAssignment(assignment.id)}>
                                                          <i className="bi bi-x me-2 text-danger"></i> Remove Assignment
                                                        </Dropdown.Item>
                                                      </Dropdown.Menu>
                                                    </Dropdown>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </Card.Body>
                                </Card>
                              </Col>
                              
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>

                      </Row>
                    )}

                    {manageSubTab === 'requests' && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <Card className="shadow-sm border-0 p-0 h-100 overflow-hidden">
                            <Card.Header className="px-4 py-3 d-flex flex-row align-items-center justify-content-between">
                              <div className="d-flex flex-column">
                                <h6 className="mb-0 fw-semibold">Document Requests</h6>
                                <p className="text-muted small mb-0">showing {documentRequests.filter(r => !r.packageId).length} requests</p>
                              </div>
                              <div className="d-flex gap-2">
                                {selectedDocumentRequests.size > 0 && (
                                  <Button size="sm" variant="outline-danger" onClick={handleBulkDeleteDocumentRequests}>
                                    <i className="bi bi-trash"></i> ({selectedDocumentRequests.size})
                                  </Button>
                                            )}
                                <Button size="sm" variant="light-solid" onClick={() => { setDocumentRequestPackageId(null); setShowDocumentRequestModal(true); }}><i className="bi bi-plus-lg"></i></Button>
                                          </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                              <Row>
                                <Col lg={8} md={12} className="no-border-end border-end pe-lg-0">
                              {documentRequests.filter(r => !r.packageId).length === 0 ? (
                                <div className="text-center py-5">
                                  <div className="text-muted mb-3"><i className="bi bi-file-earmark-plus fs-1"></i></div>
                                  <h6 className="text-muted">No document requests</h6>
                                  <p className="text-muted">Create document requests for residents to submit.</p>
                                </div>
                              ) : (
                                <Table hover responsive className="mb-0">
                                              <thead>
                                                <tr className="custom-header-bg">
                                                  <th className="ps-3">Type</th>
                                                  <th className="text-start">Request</th>
                                                  <th>Category</th>
                                                  <th>Created</th>
                                                  <th className="text-end">Actions</th>
                                                  <th>
                                                    <Form.Check
                                                      type="checkbox"
                                          checked={documentRequests.filter(r => !r.packageId).length > 0 && documentRequests.filter(r => !r.packageId).every(r => selectedDocumentRequests.has(r.id))}
                                                      onChange={() => {
                                            const list = documentRequests.filter(r => !r.packageId);
                                            if (list.every(r => selectedDocumentRequests.has(r.id))) {
                                              setSelectedDocumentRequests(new Set());
                                                        } else {
                                              setSelectedDocumentRequests(new Set(list.map(r => r.id)));
                                                        }
                                                      }}
                                                    />
                                                  </th>
                                                </tr>
                                              </thead>
                                <tbody>
                                    {documentRequests.filter(r => !r.packageId).map(r => (
                                      <tr key={`req-row-${r.id}`} className={`assignment-hover ${selectedRequestId === r.id ? 'table-active' : ''}`} onClick={() => setSelectedRequestId(r.id)} style={{cursor:'pointer'}}>
                                        <td className="ps-3"><Badge bg="secondary" className="rounded-2 p-2"><i className="bi bi-file-earmark-plus-fill fs-5"></i></Badge></td>
                                                    <td className="text-start">
                                                      <div className="fw-semibold">{r.title}</div>
                                          <small className="text-muted">Created {new Date(r.createdAt).toLocaleString()}</small>
                                                    </td>
                                                    <td>{r.category || '—'}</td>
                                                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                                        <td className="text-end">
                                          <Button size="sm" variant="outline-primary" onClick={() => openAssignModal({ documentRequestId: r.id })}><i className="bi bi-person-plus"></i></Button>
                                                    </td>
                                                    <td>
                                          <Form.Check type="checkbox" checked={selectedDocumentRequests.has(r.id)} onChange={() => toggleDocumentRequestSelection(r.id)} />
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                </Table>
                              )}
                              <hr className="d-md-none d-block"/>
                              </Col>
                              <Col lg={4} md={12} className="ps-lg-0">
                                <Card className="border-0 p-0 h-100">
                                  <Card.Header className="border-0 px-2 py-3" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                                    <div className="d-flex align-items-center justify-content-between px-2">
                                      <div className="d-flex align-items-center gap-2">
                                        <h6 className="mb-0 fw-semibold">Assignments</h6>
                                      </div>
                                      <Badge bg="secondary" className="px-2 py-1 rounded-pill">{allAssignments.filter(a => a.documentRequestId && a.documentRequestId === selectedRequestId).length}</Badge>
                                    </div>
                                  </Card.Header>
                                  <Card.Body className="p-0" style={{maxHeight: '750px', overflowY: 'auto'}}>
                                    {(() => {
                                      const relevantAssignments = allAssignments.filter(a => a.documentRequestId && a.documentRequestId === selectedRequestId);
                                      if (relevantAssignments.length === 0) {
                                        return (
                                          <div className="text-center py-5 px-4">
                                            <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{width: '80px', height: '80px'}}>
                                              <i className="bi bi-inbox text-muted fs-1"></i>
                                            </div>
                                            <h6 className="text-muted fw-semibold mb-2">No assignments</h6>
                                            <p className="text-muted small mb-0">No document request assignments found.</p>
                                          </div>
                                        );
                                      }
                                      return (
                                        <>
                                        
                                        <div className="list-group list-group-flush">
                                          {relevantAssignments.map((assignment, i) => (
                                            <div key={`assignment-${assignment.id}-${i}`} className="list-group-item p-2 assignment-hover" style={{borderBottomStyle:'dashed'}}>
                                              <div className="d-flex justify-content-between align-items-center px-2">
                                                <div className="flex-grow-1">
                                                  <div className="d-flex flex-column align-items-start">
                                                    <small className="fw-semibold mb-0">{assignment.assignee ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}` : 'Unknown'}</small>
                                                    <span className="text-muted" style={{fontSize: '0.75rem'}}> Assigned {new Date(assignment.createdAt).toLocaleDateString()}</span>
                                                  </div>
                                                </div>
                                                <div className="d-flex align-items-center gap-3">
                                                  <div>
                                                  {assignment.status === 'completed' && (
                                                      <i className="bi bi-check-circle-fill text-success"></i>
                                                  )}
                                                  {assignment.status === 'overdue' && (
                                                      <i className="bi bi-exclamation-circle-fill text-danger"></i>
                                                  )}
                                                  {assignment.status !== 'completed' && assignment.status !== 'overdue' && (
                                                      <i className="bi bi-hourglass-split text-warning"></i>
                                                  )}
                                                  </div>
                                                  <Dropdown align="end">
                                                    <Dropdown.Toggle
                                                      variant="light-lite"
                                                      size="sm"
                                                      className="px-2 py-1 no-arrow"
                                                      aria-label="Assignment actions"
                                                    >
                                                      <i className="bi bi-three-dots-vertical"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                      <Dropdown.Item onClick={() => handleDeleteAssignment(assignment.id)}>
                                                        <i className="bi bi-x me-2 text-danger"></i> Remove Assignment
                                                      </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                  </Dropdown>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </>);
                                    })()}
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                            </Card.Body>
                          </Card>
                        </Col>

                        
                      </Row>
                    )}
                    {manageSubTab === 'forms' && (
                      <Row className="mb-3">
                        <Col md={12}>
                          <Card className="shadow-sm border-0 p-0 h-100 overflow-hidden">
                            <Card.Header className="px-4 py-3 d-flex flex-row align-items-center justify-content-between">
                              <div className="d-flex flex-column">
                                <h6 className="mb-0 fw-semibold">Forms</h6>
                                <p className="text-muted small mb-0">showing {forms.length} forms</p>
                              </div>
                              <Button size="sm" variant="primary" onClick={() => { window.location.href = '/onboarding/forms/new'; }}>
                                <i className="bi bi-plus-lg me-1"></i>
                                Create a Form
                              </Button>
                            </Card.Header>
                            <Card.Body className="p-0">
                              <Row>
                                <Col lg={8} md={12} className="no-border-end border-end pe-lg-0">
                                  {forms.length === 0 ? (
                                    <div className="text-center py-5">
                                      <div className="text-muted mb-3"><i className="bi bi-ui-checks-grid fs-1"></i></div>
                                      <h6 className="text-muted">No forms yet</h6>
                                      <p className="text-muted mb-0">Create a form in the form builder, then assign it to users here.</p>
                                    </div>
                                  ) : (
                                    <Table hover responsive className="mb-0">
                                      <thead>
                                        <tr className="custom-header-bg">
                                          <th className="ps-3">Type</th>
                                          <th className="text-start">Title</th>
                                          <th>Updated</th>
                                          <th>Status</th>
                                          <th className="text-end">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {forms.map((f) => {
                                          const selectThisForm = () => {
                                            setSelectedFormId(f.id);
                                            setOpenFormRowActionsId(null);
                                          };
                                          const statusVariant = f.status === 'published' ? 'success' : f.status === 'archived' ? 'secondary' : 'warning';
                                          return (
                                            <tr key={`form-row-${f.id}`} className={`assignment-hover ${selectedFormId === f.id ? 'table-active' : ''}`}>
                                              <td className="ps-3" style={{ cursor: 'pointer' }} onClick={selectThisForm}>
                                                <Badge bg="secondary" className="rounded-2 p-2"><i className="bi bi-ui-checks-grid fs-5"></i></Badge>
                                              </td>
                                              <td className="text-start" style={{ cursor: 'pointer' }} onClick={selectThisForm}>
                                                <div className="fw-semibold">{f.title}</div>
                                                <small className="text-muted">{f.description || 'No description'}</small>
                                              </td>
                                              <td style={{ cursor: 'pointer' }} onClick={selectThisForm}>
                                                {new Date(f.updatedAt || f.createdAt).toLocaleString()}
                                              </td>
                                              <td>
                                                <Badge bg={statusVariant}>{f.status || 'draft'}</Badge>
                                              </td>
                                              <td className="text-end">
                                                <div className="d-inline-block">
                                                  <DropdownButton
                                                    id={`onboarding-form-actions-${f.id}`}
                                                    className="onboarding-form-row-actions"
                                                    variant="light-lite"
                                                    size="sm"
                                                    align="end"
                                                    title={<i className="bi bi-three-dots-vertical"></i>}
                                                    show={openFormRowActionsId === f.id}
                                                    onToggle={(nextShow) => setOpenFormRowActionsId(nextShow ? f.id : null)}
                                                  >
                                                    <Dropdown.Item onClick={() => handleExportFillablePdfForm(f)}>
                                                      <i className="bi bi-file-earmark-pdf me-2 text-primary"></i> Export PDF
                                                    </Dropdown.Item>
                                                    <Dropdown.Item onClick={() => { window.location.href = `/onboarding/forms/${f.id}/edit`; }}>
                                                      <i className="bi bi-pencil me-2"></i> Edit
                                                    </Dropdown.Item>
                                                    {f.status === 'published' && (
                                                      <Dropdown.Item onClick={() => openAssignModal({ formId: f.id })}>
                                                        <i className="bi bi-person-plus me-2 text-success"></i> Assign
                                                      </Dropdown.Item>
                                                    )}
                                                  </DropdownButton>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </Table>
                                  )}
                                </Col>
                                <Col lg={4} md={12} className="ps-lg-0">
                                  <Card className="border-0 p-0 h-100">
                                    <Card.Header className="border-0 px-2 py-3" style={{ backgroundColor: 'var(--bs-body-bg)' }}>
                                      <div className="d-flex align-items-center justify-content-between px-2">
                                        <h6 className="mb-0 fw-semibold">Assignments</h6>
                                        <Badge bg="secondary" className="px-2 py-1 rounded-pill">{allAssignments.filter(a => a.formId && a.formId === selectedFormId).length}</Badge>
                                      </div>
                                    </Card.Header>
                                    <Card.Body className="p-0" style={{ maxHeight: '750px', minHeight: '111px', overflowY: 'auto' }}>
                                      {allAssignments.filter(a => a.formId && a.formId === selectedFormId).map((assignment) => (
                                        <div key={`form-assignment-${assignment.id}`} className="list-group-item p-2 assignment-hover" style={{ borderBottom: '1px dashed var(--gray-400)' }}>
                                          <div className="d-flex justify-content-between align-items-center px-2">
                                            <div className="d-flex flex-column align-items-start">
                                              <small className="fw-semibold mb-0">{assignment.assignee ? `${assignment.assignee.firstName} ${assignment.assignee.lastName}` : 'Unknown'}</small>
                                              <span className="text-muted" style={{ fontSize: '0.75rem' }}>Assigned {new Date(assignment.createdAt).toLocaleDateString()}</span>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                              <Badge bg={assignment.status === 'completed' ? 'success' : 'secondary'}>{assignment.status}</Badge>
                                              <Dropdown align="end">
                                                <Dropdown.Toggle
                                                  variant="light"
                                                  size="sm"
                                                  className="border-0 p-1 no-arrow"
                                                  style={{ lineHeight: 1 }}
                                                >
                                                  <i className="bi bi-three-dots-vertical"></i>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                  <Dropdown.Item onClick={() => handleDeleteAssignment(assignment.id)}>
                                                    <i className="bi bi-x me-2 text-danger"></i> Remove Assignment
                                                  </Dropdown.Item>
                                                </Dropdown.Menu>
                                              </Dropdown>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </Card.Body>
                                  </Card>
                                </Col>
                              </Row>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </>
            )}
            {isDirector && activeTab === 'submissions' && (
              <Row>
                <Col md={2}>
                  <Card className="mb-3 shadow-sm border-0 p-0">
                    <Card.Header className="border-0 px-4 py-3 border-bottom" style={{ backgroundColor: 'var(--bs-body-bg)' }}>
                      <h6 className="mb-0 fw-semibold">Residents</h6>
                    </Card.Header>
                    <Card.Body className="p-0 mb-2">
                      {(() => {
                        const uidSet = new Set();
                        (packageSectionAssignments || []).forEach((sa) => {
                          const id = sa.assignee?.id ?? sa.assignedToId;
                          if (id != null && id !== '') uidSet.add(id);
                        });
                        (submissions || []).forEach((s) => {
                          if (s.submitter?.id != null && s.submitter.id !== '') uidSet.add(s.submitter.id);
                        });

                        if (uidSet.size === 0) {
                          return <div className="text-muted p-3">No residents with package sections or document submissions.</div>;
                        }

                        const entries = Array.from(uidSet).map((uid) => {
                          const sections = (packageSectionAssignments || []).filter(
                            (sa) => (sa.assignee?.id ?? sa.assignedToId) === uid
                          );
                          const subItems = (submissions || []).filter((s) => s.submitter?.id === uid);
                          const resident =
                            sections[0]?.assignee ||
                            subItems[0]?.submitter ||
                            { id: uid, firstName: 'Unknown', lastName: '', year: 'Unknown' };
                          return { uid, resident, sections, subItems };
                        });

                        const normalizePgyKey = (y) => {
                          if (y === null || y === undefined || y === '' || y === 'Unknown') return 'unknown';
                          const n = Number(y);
                          return Number.isFinite(n) ? String(n) : String(y);
                        };

                        const byPgy = entries.reduce((acc, entry) => {
                          const key = normalizePgyKey(entry.resident?.year);
                          if (!acc[key]) acc[key] = [];
                          acc[key].push(entry);
                          return acc;
                        }, {});

                        const pgyKeys = Object.keys(byPgy).sort((a, b) => {
                          if (a === 'unknown') return 1;
                          if (b === 'unknown') return -1;
                          const na = Number(a);
                          const nb = Number(b);
                          if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
                          return a.localeCompare(b);
                        });

                        pgyKeys.forEach((k) => {
                          byPgy[k].sort((a, b) => {
                            const aName = `${a.resident?.lastName || ''} ${a.resident?.firstName || ''}`.trim();
                            const bName = `${b.resident?.lastName || ''} ${b.resident?.firstName || ''}`.trim();
                            return aName.localeCompare(bName) || (a.resident?.id || 0) - (b.resident?.id || 0);
                          });
                        });

                        // Single key: opening one PGY group closes the others (array would open all).
                        const defaultPgyAccordionKey = pgyKeys.length > 0 ? 'pgy-0' : undefined;

                        return (
                          <Accordion flush defaultActiveKey={defaultPgyAccordionKey}>
                            {pgyKeys.map((pgyKey, idx) => {
                              const residents = byPgy[pgyKey];
                              const headerLabel = pgyKey === 'unknown' ? 'PGY —' : `PGY-${pgyKey}`;
                              return (
                                <Accordion.Item key={`pgy-group-${pgyKey}`} eventKey={`pgy-${idx}`} className="custom-accordion-border">
                                  <Accordion.Header>
                                    <span className="fw-semibold">{headerLabel}</span>
                                  </Accordion.Header>
                                  <Accordion.Body className="p-0">
                                    <ListGroup variant="flush">
                                      {residents.map((entry) => {
                                        const residentId = entry.resident?.id || null;
                                        const newSections = entry.sections.filter((s) => s.status === 'submitted').length;
                                        const newSubs = entry.subItems.filter((s) => s.status === 'submitted').length;
                                        const newCount = newSections + newSubs;
                                        const countBadge = entry.sections.length + entry.subItems.length;
                                        return (
                                          <ListGroup.Item
                                            key={`res-${residentId}`}
                                            action
                                            onClick={() => setSelectedSubmitterId(residentId)}
                                            active={selectedSubmitterId === residentId}
                                            className="border-0 py-2 px-3"
                                            style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                                          >
                                            <div className="d-flex justify-content-between w-100 align-items-center">
                                              <div className="d-flex flex-column text-start">
                                                <span>
                                                  {entry.resident?.firstName || 'Unknown'} {entry.resident?.lastName || ''}
                                                </span>
                                              </div>
                                              <div className="d-flex flex-row align-items-end gap-1">
                                                {newCount > 0 && <Badge bg="success-solid" className="rounded-2 p-1">New</Badge>}
                                                {countBadge > 0 && (
                                                  <Badge bg="secondary" className="rounded-2 py-1 px-2">
                                                    {countBadge}
                                                  </Badge>
                                                )}
                                              </div>
                                            </div>
                                          </ListGroup.Item>
                                        );
                                      })}
                                    </ListGroup>
                                  </Accordion.Body>
                                </Accordion.Item>
                              );
                            })}
                          </Accordion>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={10}>
                  <Card className="mb-3 shadow-sm border-0 p-0 h-100">
                    <Card.Header className="border-0 px-4 py-3">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-2">
                        <div>
                          <h6 className="mb-0 fw-semibold">
                            Submission reviews for
                            {effectiveSelectedResidentName && (
                              <span className="ms-1 fw-semibold text-primary">{effectiveSelectedResidentName}</span>
                            )}
                          </h6>
                          <p className="text-muted small mb-0">
                            {effectiveSelectedId
                              ? 'Package sections and document or request uploads for this resident'
                              : 'Select a resident from the list'}
                          </p>
                        </div>
                        {effectiveSelectedId && submissionReviewPanel.hasContent && (
                          <div className="d-flex gap-2 flex-shrink-0">
                            {/*<Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => setSubmissionReviewAccordionKeys(submissionReviewPanel.allAccordionKeys)}
                            >
                              <i className="bi bi-arrows-expand me-1" />
                              Expand all
                            </Button>*/}
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => setSubmissionReviewAccordionKeys([])}
                            >
                              <i className="bi bi-arrows-collapse me-1" />
                              Collapse all
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                      {(() => {
                        if (!effectiveSelectedId) {
                          return (
                            <div className="text-center py-5">
                              <div className="text-muted mb-3"><i className="bi bi-person-fill fs-1"></i></div>
                              <h6 className="text-muted">Nothing to review yet</h6>
                              <p className="text-muted">There are no package section assignments or document submissions.</p>
                            </div>
                          );
                        }

                        if (!submissionReviewPanel.hasContent) {
                          return <div className="text-muted p-3">No section assignments or submissions for this resident.</div>;
                        }

                        const {
                          packageGroups,
                          legacyGroups,
                          selectedFormSubmissions,
                          pkgEventKeys,
                          legEventKeys
                        } = submissionReviewPanel;

                        const formatFileSize = (bytes) => {
                          if (bytes == null || bytes === '') return '—';
                          const n = Number(bytes);
                          if (!Number.isFinite(n) || n < 0) return '—';
                          if (n < 1024) return `${n} B`;
                          if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
                          return `${(n / (1024 * 1024)).toFixed(1)} MB`;
                        };

                        const downloadFile = async (webPath, downloadName) => {
                          if (!webPath) return;
                          const path = webPath.startsWith('/') ? webPath : `/${webPath}`;
                          const url = `${API_ENDPOINTS.PROFILE_PICS}${path}`;
                          const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                          const blob = await r.blob();
                          const dl = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = dl;
                          a.download = downloadName || 'download';
                          a.click();
                          URL.revokeObjectURL(dl);
                        };

                        return (
                          <div>
                            <Accordion
                              alwaysOpen
                              activeKey={submissionReviewAccordionKeys}
                              onSelect={handleSubmissionReviewAccordionSelect}
                            >
                              {packageGroups.map((pkgGroup, idx) => {
                                const pkgAssign = pkgGroup.packageAssignment;
                                const pkgTitle = pkgAssign?.customName || pkgAssign?.package?.name || 'Package';
                                const eventKey = pkgEventKeys[idx];
                                  const sortedSections = (pkgGroup.sections || []).slice().sort((a, b) => (a.section?.sortOrder || 0) - (b.section?.sortOrder || 0));
                                  const submittedCount = sortedSections.filter(s => s.status === 'submitted').length;
                                  const rejectedCount = sortedSections.filter(s => s.status === 'rejected').length;
                                  const approvedCount = sortedSections.filter(s => s.status === 'approved').length;

                                  return (
                                    <Accordion.Item key={eventKey} eventKey={eventKey} className="border-0  rounded-0 mb-2">
                                      <Accordion.Header>
                                        <div className="d-flex w-100 gap-2 justify-content-start align-items-center">
                                          <span className="bg-secondary rounded-2 p-2">
                                            <i className="bi bi-folder-fill text-warning"></i>
                                          </span>
                                          <div className="fw-semibold">{pkgTitle}</div>
                                          <Badge bg={submittedCount > 0 ? 'info' : rejectedCount > 0 ? 'danger' : approvedCount > 0 ? 'success' : 'secondary'}>
                                            {submittedCount > 0 ? `${submittedCount} submitted` : rejectedCount > 0 ? 'Needs work' : approvedCount === sortedSections.length ? 'Approved' : 'In progress'}
                                          </Badge>
                                        </div>
                                      </Accordion.Header>
                                      <Accordion.Body>
                                        {pkgAssign?.package?.description && (
                                          <div className="text-muted small mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                                            {pkgAssign.package.description}
                                          </div>
                                        )}

                                        <Accordion
                                          alwaysOpen
                                          activeKey={submissionReviewAccordionKeys}
                                          onSelect={handleSubmissionReviewAccordionSelect}
                                        >
                                          {sortedSections.map((sa, sectionIdx) => {
                                            const sectionItems = sa.section?.items || [];
                                            const itemResponses = sa.itemResponses || [];

                                            return (
                                              <Accordion.Item key={sa.id} eventKey={`sec-${sa.id}`} className="border rounded-3 mb-2 overflow-hidden">
                                                <Accordion.Header>
                                                  <div className="d-flex w-100 flex-wrap align-items-center justify-content-between gap-2 pe-2">
                                                    <div className="d-flex align-items-start gap-2 flex-grow-1 min-w-0">
                                                        <div className="d-flex flex-row align-items-center gap-2">
                                                          <Badge bg="secondary" className="rounded-2">Section {sectionIdx + 1}.</Badge> 
                                                          <span className="fw-semibold">{sa.section?.title || 'Section'}</span>
                                                        </div>
                                                     
                                                    </div>
                                                    <div
                                                      className="d-flex flex-row align-items-center gap-2"
                                                      onClick={(e) => e.stopPropagation()}
                                                      onKeyDown={(e) => e.stopPropagation()}
                                                      role="presentation"
                                                    >
                                                      <Badge
                                                        bg={
                                                          sa.status === 'approved' ? 'success' :
                                                          sa.status === 'rejected' ? 'danger' :
                                                          sa.status === 'submitted' ? 'info' :
                                                          'secondary'
                                                        }
                                                      >
                                                        {sa.status}
                                                      </Badge>
                                                      {sa.status === 'submitted' && (
                                                        <>
                                                          <Button size="sm" variant="success" onClick={() => handleSectionReview(sa.id, 'approved')}>Approve</Button>
                                                          <Button size="sm" variant="danger" onClick={() => handleSectionReview(sa.id, 'rejected')}>Reject</Button>
                                                        </>
                                                      )}
                                                    </div>
                                                  </div>
                                                </Accordion.Header>
                                                <Accordion.Body className="pt-2">
                                                  {sa.section?.instructions && (
                                                    <div className="text-muted small mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                                                      {sa.section.instructions}
                                                    </div>
                                                  )}

                                                  <div className="d-flex flex-column gap-3">
                                                    {sectionItems.map((item, itemIdx) => {
                                                      const resp = itemResponses.find(r => r.itemId === item.id);
                                                      const respStatus = resp?.status || 'draft';
                                                      const fileSchema = item.formSchema || {};
                                                      const isReferenceFilesMode = item.itemType === 'file_upload' && fileSchema.contentMode === 'reference_files' && Array.isArray(fileSchema.referenceFiles);
                                                      const acknowledged = resp?.formData?.acknowledged || {};
                                                      const uploadedFor = resp?.formData?.uploadedFor || {};

                                                      return (
                                                        <div key={item.id} className="p-3 rounded-3 bg-light">
                                                          <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                                            <div className="flex-grow-1">
                                                              <div className="fw-semibold">{itemIdx + 1}. {item.title}</div>
                                                              {item.instructions ? (
                                                                <div className="text-muted small mt-1" style={{ whiteSpace: 'pre-wrap' }}>{item.instructions}</div>
                                                              ) : null}
                                                            </div>
                                                            <Badge
                                                              bg={respStatus === 'approved' ? 'success' : respStatus === 'rejected' ? 'danger' : respStatus === 'submitted' ? 'info' : 'warning'}
                                                              className="rounded-pill"
                                                            >
                                                              {respStatus}
                                                            </Badge>
                                                          </div>

                                                          {item.itemType === 'info_form' && (
                                                            <Accordion
                                                              className="mt-2"
                                                              alwaysOpen
                                                              activeKey={submissionReviewAccordionKeys}
                                                              onSelect={handleSubmissionReviewAccordionSelect}
                                                            >
                                                              <Accordion.Item eventKey={`form-item-${item.id}`} className="border rounded-2 overflow-hidden">
                                                                <Accordion.Header>
                                                                  <div className="d-flex align-items-center justify-content-between w-100 gap-2 pe-2">
                                                                    <span className="small fw-semibold">View submitted form</span>
                                                                    <Button
                                                                      size="sm"
                                                                      variant="light"
                                                                      onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handlePrintFormSubmission({
                                                                          title: item.title || 'Form submission',
                                                                          description: item.instructions || '',
                                                                          formData: resp?.formData || {},
                                                                          schema: item.formSchema || {},
                                                                          submittedAt: resp?.updatedAt || resp?.createdAt || null
                                                                        });
                                                                      }}
                                                                    >
                                                                      <i className="bi bi-printer me-1"></i>
                                                                      Export
                                                                    </Button>
                                                                  </div>
                                                                </Accordion.Header>
                                                                <Accordion.Body className="small p-2 bg-white">
                                                                  {renderSchemaAnswerPreview(item.formSchema || {}, resp?.formData || {})}
                                                                </Accordion.Body>
                                                              </Accordion.Item>
                                                            </Accordion>
                                                          )}

                                                          {item.itemType === 'file_upload' && !isReferenceFilesMode && (resp?.webPath || resp?.filePath) && (
                                                            <div className="mt-2 p-2 bg-white rounded-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                                                              <div>
                                                                <div className="fw-semibold small">{resp.originalName || resp.fileName || 'Uploaded file'}</div>
                                                                <div className="text-muted small">Size: {formatFileSize(resp.fileSize)}</div>
                                                              </div>
                                                              <Button
                                                                size="sm"
                                                                variant="light-solid"
                                                                onClick={() => downloadFile(resp.webPath || resp.filePath, resp.originalName || resp.fileName || item.title)}
                                                              >
                                                                <i className="bi bi-download" ></i>
                                                              </Button>
                                                            </div>
                                                          )}

                                                          {item.itemType === 'file_upload' && isReferenceFilesMode && (
                                                            <div className="d-flex flex-column gap-2 mt-2">
                                                              {fileSchema.referenceFiles.map((rf) => {
                                                                const refId = String(rf.id);
                                                                const isAck = rf.completionMode !== 'upload_required';
                                                                const acked = Boolean(acknowledged[rf.id] ?? acknowledged[refId]);
                                                                const up = uploadedFor[rf.id] || uploadedFor[refId];

                                                                return (
                                                                  <div key={rf.id || refId} className="p-2 rounded-3 bg-white">
                                                                    <div className="fw-semibold small">{rf.originalName || rf.fileName || 'Reference document'}</div>
                                                                    {!isAck && up?.webPath && (
                                                                      <div className="mt-2 d-flex flex-wrap align-items-center justify-content-between gap-2">
                                                                        <div>
                                                                          <div className="small fw-semibold">{up.originalName || 'Uploaded file'}</div>
                                                                          {Number.isFinite(Number(up.fileSize)) && Number(up.fileSize) >= 0 ? (
                                                                            <div className="text-muted small">({formatFileSize(up.fileSize)})</div>
                                                                          ) : null}
                                                                        </div>
                                                                        <Button
                                                                          size="sm"
                                                                          variant="light-solid"
                                                                          onClick={() => downloadFile(up.webPath, up.originalName || 'download')}
                                                                        >
                                                                          <i className="bi bi-download me-1"></i>
                                                                          Download
                                                                        </Button>
                                                                      </div>
                                                                    )}
                                                                    {!isAck && !up?.webPath && (
                                                                      <div className="text-muted small mt-1">No Files uploaded yet.</div>
                                                                    )}
                                                                    {isAck && acked && (
                                                                      <Alert variant="light" className="border-0 mt-2 mb-0 py-2 small">
                                                                        The resident acknowledged that they have read this document.
                                                                      </Alert>
                                                                    )}
                                                                    {isAck && !acked && (
                                                                      <div className="text-muted small mt-1">Not yet acknowledged.</div>
                                                                    )}
                                                                  </div>
                                                                );
                                                              })}
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                </Accordion.Body>
                                              </Accordion.Item>
                                            );
                                          })}
                                        </Accordion>
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  );
                                })}
                              {legacyGroups.map((g, legIdx) => {
                                const eventKey = legEventKeys[legIdx];
                                const headerTitle = (g.customName && g.customName !== 'original') ? g.customName : (g.pkg ? g.pkg.name : 'Standalone submissions');
                                return (
                                  <Accordion.Item className="border-0 border-top rounded-0 mb-2" eventKey={eventKey} key={eventKey}>
                                    <Accordion.Header>
                                      <div className="d-flex w-100 gap-2 justify-content-start align-items-center">
                                        <span className="bg-secondary rounded-2 p-2">
                                          <i className="bi bi-file-earmark-text"></i>
                                        </span>
                                        <div className="fw-semibold">{headerTitle}</div>
                                        <Badge bg="secondary">{g.items.length}</Badge>
                                      </div>
                                    </Accordion.Header>
                                    <Accordion.Body className="px-3">
                                      {g.pkg && g.pkg.description && (
                                        <p className="text-muted d-block px-3 pt-3 mb-0">{g.pkg.description}</p>
                                      )}
                                      <div>
                                        {g.items.map((s) => (
                                          <div
                                            key={s.id}
                                            className="border-alt-light custom-hover-bg-alt rounded-3 mb-2 p-3 d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start"                                          >
                                            <div className="d-flex align-items-center">
                                              <Badge bg="light" className="rounded-2 p-2 me-3 flex-shrink-0">
                                                <i className={`${getFileIcon(s.originalName, s.mimeType)} fs-5`}></i>
                                              </Badge>
                                              <div className="min-w-0 flex-grow-1">
                                                <div className="d-flex flex-wrap align-items-baseline column-gap-2 row-gap-0">
                                                  <span className="fw-semibold text-truncate">{s.originalName || '—'}</span>
                                                  {(() => {
                                                    const n = Number(s.fileSize);
                                                    if (!Number.isFinite(n) || n < 0) return null;
                                                    return (
                                                      <span className="text-muted fw-normal small text-nowrap">
                                                        {formatFileSize(s.fileSize)}
                                                      </span>
                                                    );
                                                  })()}
                                                </div>
                                                <div className="text-muted small text-truncate">
                                                 <div className="d-flex flex-row align-items-center gap-2 small"> 
                                                  <span className="fw-semibold">Uploaded for:</span> {s.assignment?.documentRequest?.title || s.document?.title || '—'} • 
                                                  <span className="fw-semibold">Submitted on:</span> {new Date(s.createdAt).toLocaleString()}
                                                 </div>
                                                </div>
                                              </div>
                                            </div>
                                           
                                              <div className="d-flex flex-row align-items-center gap-2">
                                                  <span className="text-muted small">
                                                    {s.document?.category
                                                      ? s.document.category
                                                      : s.assignment?.documentRequest?.category
                                                        ? s.assignment.documentRequest.category
                                                        : '—'}
                                                  </span>
                                                  <Badge bg={s.status === 'approved' ? 'success' : s.status === 'rejected' ? 'danger' : 'secondary'} className="me-2">
                                                    {s.status}
                                                  </Badge>
                                                  {s.status !== 'rejected' && (
                                                  <Button
                                                    size="sm"
                                                    variant="light-solid"
                                                    onClick={async (e) => {
                                                      e.preventDefault();
                                                      const url = `${API_ENDPOINTS.PROFILE_PICS}${s.webPath || s.filePath}`;
                                                      const r = await fetch(url, {
                                                        headers: { Authorization: `Bearer ${token}` },
                                                      });
                                                      const blob = await r.blob();
                                                      const dl = URL.createObjectURL(blob);
                                                      const a = document.createElement('a');
                                                      a.href = dl;
                                                      a.download = s.originalName;
                                                      a.click();
                                                      URL.revokeObjectURL(dl);
                                                    }}
                                                  >
                                                    <i className="bi bi-download"></i>
                                                  </Button>
                                                )}
                                                {s.status === 'submitted' && (
                                                  <Dropdown align="end">
                                                    <Dropdown.Toggle
                                                      variant="light-lite"
                                                      size="sm"
                                                      id={`dropdown-actions-${s.id}`}
                                                      className="no-arrow"
                                                    >
                                                      <i className="bi bi-three-dots-vertical"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                      <Dropdown.Item
                                                        onClick={() => handleReview(s.id, 'approved')}
                                                      >
                                                        <i className="bi bi-check-circle text-success me-2"></i>
                                                        Approve
                                                      </Dropdown.Item>
                                                      <Dropdown.Item
                                                        onClick={() => handleReview(s.id, 'rejected')}
                                                      >
                                                        <i className="bi bi-x-circle text-danger me-2"></i>
                                                        Reject
                                                      </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                  </Dropdown>
                                                )}
                                              
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </Accordion.Body>
                                  </Accordion.Item>
                                );
                              })}
                              {selectedFormSubmissions.length > 0 && (
                                <Accordion.Item className="border-0 border-top rounded-0 mb-2" eventKey="forms-submissions">
                                  <Accordion.Header>
                                    <div className="d-flex w-100 gap-2 justify-content-start align-items-center">
                                      <span className="bg-secondary rounded-2 p-2">
                                        <i className="bi bi-ui-checks-grid"></i>
                                      </span>
                                      <div className="fw-semibold">Forms</div>
                                      <Badge bg="secondary">{selectedFormSubmissions.length}</Badge>
                                    </div>
                                  </Accordion.Header>
                                  <Accordion.Body className="px-3">
                                    <Accordion
                                      className="d-flex flex-column gap-2"
                                      alwaysOpen
                                      activeKey={submissionReviewAccordionKeys}
                                      onSelect={handleSubmissionReviewAccordionSelect}
                                    >
                                      {selectedFormSubmissions.map((s) => (
                                        <Accordion.Item key={`form-sub-${s.id}`} eventKey={`form-submission-${s.id}`} className="border rounded-3 overflow-hidden">
                                          <Accordion.Header>
                                            <div className="d-flex w-100 justify-content-between align-items-start pe-2">
                                              <div>
                                                <div className="fw-semibold">{s.form?.title || s.assignment?.form?.title || 'Form submission'}</div>
                                                <div className="small text-muted">{new Date(s.createdAt).toLocaleString()}</div>
                                              </div>
                                              <div className="d-flex align-items-center gap-2">
                                                <Badge bg={s.status === 'approved' ? 'success' : s.status === 'submitted' ? 'info' : 'warning'}>{s.status}</Badge>
                                                <Button
                                                  size="sm"
                                                  variant="light"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handlePrintFormSubmission({
                                                      title: s.form?.title || s.assignment?.form?.title || 'Form submission',
                                                      description: s.form?.description || s.assignment?.form?.description || '',
                                                      formData: s.formData || {},
                                                      schema: s.form?.schema || s.assignment?.form?.schema || {},
                                                      submittedAt: s.createdAt || null
                                                    });
                                                  }}
                                                >
                                                  <i className="bi bi-printer me-1"></i>
                                                  Export
                                                </Button>
                                                {s.status === 'submitted' && (
                                                  <>
                                                    <Button size="sm" variant="success" onClick={(e) => { e.stopPropagation(); handleReviewFormSubmission(s.id, 'approved'); }}>Approve</Button>
                                                    <Button size="sm" variant="danger" onClick={(e) => { e.stopPropagation(); handleReviewFormSubmission(s.id, 'rejected'); }}>Reject</Button>
                                                  </>
                                                )}
                                              </div>
                                            </div>
                                          </Accordion.Header>
                                          <Accordion.Body className="d-flex flex-column gap-2">
                                            {renderFormSubmissionPreview(s)}
                                          </Accordion.Body>
                                        </Accordion.Item>
                                      ))}
                                    </Accordion>
                                  </Accordion.Body>
                                </Accordion.Item>
                              )}
                              </Accordion>
                            </div>
                          );
                        })()}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
            )}
            {activeTab === 'my' && (
              <Row>
                <Col lg={2} md={3} className="d-none d-md-block mb-3">
                  <Card className="bg-white shadow-sm border-0 p-0 py-2 custom-sticky-top" style={{ top: '5rem' }}>
                   <Card.Body className="pe-0 ps-2 pt-0 pb-2">
                    <Nav variant="pills" className="flex-column">
                      <Nav.Item>
                        <Nav.Link className="disabled fw-semibold">Menu</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="packages" active={myAssignmentsSubTab === 'packages'} onClick={() => { setMyAssignmentsSubTab('packages'); setMyAssignmentsSelectedId(null); }}>
                          Packages
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="standalone" active={myAssignmentsSubTab === 'standalone'} onClick={() => { setMyAssignmentsSubTab('standalone'); setMyAssignmentsSelectedId(null); }}>
                          Standalone Assignments
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="forms" active={myAssignmentsSubTab === 'forms'} onClick={() => { setMyAssignmentsSubTab('forms'); setMyAssignmentsSelectedId(null); }}>
                          Forms
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card.Body>
                  </Card>
                </Col>
                <Col md={12} className="d-md-none mb-3">
                  <Card className="bg-white shadow-sm border-0 p-0">
                    <Accordion>
                      <Accordion.Item eventKey="menu" className="border-0 custom-accordian-border">
                        <Accordion.Header>
                          <span className="fw-semibold">Menu</span>
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                          <Nav variant="pills" className="flex-column mb-2">
                            <Nav.Item>
                              <Nav.Link eventKey="packages" active={myAssignmentsSubTab === 'packages'} onClick={() => { setMyAssignmentsSubTab('packages'); setMyAssignmentsSelectedId(null); }}>
                                Packages
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link eventKey="standalone" active={myAssignmentsSubTab === 'standalone'} onClick={() => { setMyAssignmentsSubTab('standalone'); setMyAssignmentsSelectedId(null); }}>
                                Standalone Assignments
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link eventKey="forms" active={myAssignmentsSubTab === 'forms'} onClick={() => { setMyAssignmentsSubTab('forms'); setMyAssignmentsSelectedId(null); }}>
                                Forms
                              </Nav.Link>
                            </Nav.Item>
                          </Nav>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Card>
                </Col>
                <Col lg={10} md={9}>
                  {(() => {
                    // Get packages and standalone documents from grouped assignments
                    const packageGroups = groupedAssignments.filter(
                      g => g.package && g.packageAssignment
                    );
                    const standaloneGroups = groupedAssignments.filter(
                      g =>
                        !g.package &&
                        (g.documentAssignments.length > 0 ||
                          g.documentRequestAssignments.length > 0)
                    );
                    const standaloneFormAssignments = groupedAssignments
                      .filter((g) => !g.package)
                      .flatMap((g) => (g.formAssignments || []));

                    if (groupedAssignments.length === 0) {
                      return (
                        <Card className="shadow-sm border-0 p-0">
                          <Card.Header className="border-0 px-4 py-3">
                            <div className="d-flex flex-column">
                              <h6 className="mb-0 fw-semibold">My Assignments</h6>
                              <p className="text-muted small mb-0">
                                {myAssignmentsSubTab === 'packages'
                                  ? `showing ${packageGroups.length} package assignments`
                                  : myAssignmentsSubTab === 'forms'
                                    ? `showing ${standaloneFormAssignments.length} form assignments`
                                    : `showing ${standaloneGroups.flatMap(g => [...g.documentAssignments, ...g.documentRequestAssignments]).length} standalone assignments`}
                              </p>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            <div className="text-center py-5">
                              <div className="text-muted mb-3">
                                <i className="bi bi-inbox-fill fs-1"></i>
                              </div>
                              <h6 className="text-muted">No assignments yet</h6>
                              <p className="text-muted">
                                You haven't been assigned any onboarding documents, forms, or packages.
                              </p>
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    }

                    // If no selection, show accordion card list view
                    if (!myAssignmentsSelectedId) {
                      if (myAssignmentsSubTab === 'packages') {
                        if (packageGroups.length === 0) {
                          return (
                            <div className="text-center py-5">
                              <div className="text-muted mb-3">
                                <i className="bi bi-folder fs-1"></i>
                              </div>
                              <h6 className="text-muted">No package assignments</h6>
                              <p className="text-muted">You haven't been assigned any packages.</p>
                            </div>
                          );
                        }

                        // Separate into pending & completed
                        const pendingPackages = packageGroups.filter(g => {
                          const assignment = g.packageAssignment;
                          const hasSubmittedFiles = (getSubmittedFilesForAssignment[assignment?.id] || []).length > 0;
                          const hasSubmittedForms = (g.formAssignments || []).some((fa) => Boolean(getLatestNonRejectedMyFormSubmissionForAssignment(fa.id)));
                          return assignment?.status !== 'completed' && !hasSubmittedFiles && !hasSubmittedForms;
                        });
                        const completedPackages = packageGroups.filter(g => {
                          const assignment = g.packageAssignment;
                          const hasSubmittedFiles = (getSubmittedFilesForAssignment[assignment?.id] || []).length > 0;
                          const hasSubmittedForms = (g.formAssignments || []).some((fa) => Boolean(getLatestNonRejectedMyFormSubmissionForAssignment(fa.id)));
                          return assignment?.status === 'completed' || hasSubmittedFiles || hasSubmittedForms;
                        });

                        return (
                          <>
                            <div className="bg-inverse rounded-3 shadow-sm">
                            <div className="d-flex flex-column p-3 px-4">
                            <h6 className="fw-semibold mb-0">My Package Assignments</h6>
                            <p className="text-muted small mb-0">
                              {myAssignmentsSubTab === 'packages'
                                ? `showing ${packageGroups.length} package assignments`
                                : myAssignmentsSubTab === 'forms'
                                  ? `showing ${standaloneFormAssignments.length} form assignments`
                                  : `showing ${standaloneGroups.flatMap(g => [...g.documentAssignments, ...g.documentRequestAssignments]).length} standalone assignments`}
                              </p>
                            </div>
                            <Accordion activeKey={expandedSections.pendingPackages ? 'pending' : null} onSelect={k => toggleSection('pendingPackages')} className="mb-1">
                              <Accordion.Item eventKey="pending" className="border-0">
                                <Accordion.Header>
                                  <span className="fw-semibold p-2">
                                    <i className="bi bi-folder-fill text-warning me-2"></i>
                                    New/Pending Packages
                                    <Badge bg="secondary" className="ms-2">{pendingPackages.length}</Badge>
                                  </span>
                                </Accordion.Header>
                                <Accordion.Body className="pt-0 rounded-3">
                                  {pendingPackages.length === 0 ? (
                                    <div className="text-center py-5">
                                      <div className="text-muted mb-2 bg-secondary rounded-2 p-2" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                                        <i className="bi bi-inbox fs-2"></i>
                                      </div>
                                      <h6 className="text-muted mb-1">No pending packages</h6>
                                      <small className="text-muted">All packages have been submitted or completed.</small>
                                    </div>
                                  ) : (
                                    pendingPackages.map(g => {
                                      const assignment = g.packageAssignment;
                                      return (
                                        <div
                                          key={`pkg-pending-${g.package.id}`}
                                          className="border-alt-light custom-hover-bg-alt rounded-3 mb-2 p-3 gap-2 d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start"
                                          onClick={() => setMyAssignmentsSelectedId(g.package.id)}
                                          style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex flex-row align-items-center gap-3">
                                              <Badge bg="secondary" className="rounded-2 p-2">
                                                <i className="bi bi-folder-fill text-warning fs-4"></i>
                                              </Badge>
                                              <div>
                                                <div className="d-flex flex-column align-items-start">
                                                <span className="fw-semibold">{assignment?.customName || g.package.name}</span>
                                                {g.package.description && g.package.description.length <= 80 && (
                                                  <small className="text-muted">{g.package.description}</small>
                                                )}
                                                {g.package.description && g.package.description.length > 80 && (
                                                  <small className="text-muted">{g.package.description.slice(0, 80)}...</small>
                                                )}
                                                </div>
                                              </div>
                                            </div>
                                              <div className="d-flex flex-row justify-content-between align-items-md-center gap-3">
                                                
                                                <small className="text-muted">
                                                  <span className="fw-semibold">Due:</span> {assignment?.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}
                                                </small><Badge bg={assignment?.status === 'completed' ? 'success' : 'secondary'}>
                                                  {assignment?.status || 'assigned'}
                                                </Badge>
                                              </div>
                                          </div>
                                      );
                                    })
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                            <Accordion activeKey={expandedSections.completedPackages ? 'completed' : null} onSelect={k => toggleSection('completedPackages')} className="mb-3">
                              <Accordion.Item eventKey="completed" className="border-0">
                                <Accordion.Header>
                                  <span className="fw-semibold p-2">
                                    <i className="bi bi-folder-check text-success me-2"></i>
                                    Submitted/Completed Packages
                                    <Badge bg="secondary" className="ms-2">{completedPackages.length}</Badge>
                                  </span>
                                </Accordion.Header>
                                <Accordion.Body className="pt-0 rounded-3">
                                  {completedPackages.length === 0 ? (
                                    <div className="text-center py-5">
                                      <div className="text-muted mb-2 bg-secondary rounded-2 p-2" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                                        <i className="bi bi-check-circle fs-2"></i>
                                      </div>
                                      <h6 className="text-muted mb-1">No completed packages</h6>
                                      <small className="text-muted">No packages have been submitted or completed yet.</small>
                                    </div>
                                  ) : (
                                    completedPackages.map(g => {
                                      const assignment = g.packageAssignment;
                                      return (
                                        <div
                                          key={`pkg-completed-${g.package.id}`}
                                          className="border-alt-light custom-hover-bg-alt rounded-3 mb-2 p-3 gap-2 d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start"
                                          onClick={() => setMyAssignmentsSelectedId(g.package.id)}
                                          style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex flex-row align-items-center gap-3">
                                              <Badge bg="secondary" className="rounded-2 p-2">
                                                <i className="bi bi-folder-fill text-warning fs-4"></i>
                                              </Badge>
                                              <div>
                                                <div className="fw-semibold">{assignment?.customName || g.package.name}</div>
                                                {g.package.description && g.package.description.length <= 80 && (
                                                  <small className="text-muted">{g.package.description}</small>
                                                )}
                                                {g.package.description && g.package.description.length > 80 && (
                                                  <small className="text-muted">{g.package.description.slice(0, 80)}...</small>
                                                )}
                                              </div>
                                           
                                            </div>   
                                            <div className="d-flex flex-row align-items-center gap-2">
                                            {g.package.category && (
                                                <Badge bg="secondary">{g.package.category}</Badge>
                                              )}
                                            </div>

                                              <div className="d-flex flex-row justify-content-between align-items-md-center gap-3">
                                              <small className="text-muted">
                                                <span className="fw-semibold">Due:</span> {assignment?.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}
                                              </small>
                                              <Badge bg={assignment?.status === 'completed' ? 'success' : 'secondary'}>
                                                {assignment?.status || 'assigned'}
                                              </Badge>
                                            </div>
                                          </div>
                                      );
                                    })
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                            </div>
                          </>
                        );
                      } else {
                        // Standalone/forms: show as accordion cards
                        const allStandaloneAssignments = myAssignmentsSubTab === 'forms'
                          ? standaloneFormAssignments
                          : standaloneGroups.flatMap(g => [
                              ...g.documentAssignments,
                              ...g.documentRequestAssignments
                            ]);
                        
                        // Separate into pending and completed
                        const pendingDocuments = allStandaloneAssignments.filter(a => {
                          const hasSubmittedFiles = (getSubmittedFilesForAssignment[a.id] || []).length > 0;
                          return a.status !== 'completed' && !hasSubmittedFiles;
                        });
                        const completedDocuments = allStandaloneAssignments.filter(a => {
                          const hasSubmittedFiles = (getSubmittedFilesForAssignment[a.id] || []).length > 0;
                          return a.status === 'completed' || hasSubmittedFiles;
                        });
                        
                        return (
                          <>
                            <div className="bg-inverse rounded-3 shadow-sm">
                            <div className="d-flex flex-column p-3 px-4">
                            <h6 className="fw-semibold mb-0">{myAssignmentsSubTab === 'forms' ? 'My Form Assignments' : 'My Document Assignments'}</h6>
                            <p className="text-muted small mb-0">
                              {myAssignmentsSubTab === 'packages'
                                ? `showing ${packageGroups.length} package assignments`
                                : myAssignmentsSubTab === 'forms'
                                  ? `showing ${standaloneFormAssignments.length} form assignments`
                                  : `showing ${standaloneGroups.flatMap(g => [...g.documentAssignments, ...g.documentRequestAssignments]).length} standalone assignments`}
                            </p>
                            </div>
                            <Accordion activeKey={expandedSections.pendingDocuments ? 'pending' : null} onSelect={k => toggleSection('pendingDocuments')} className="mb-1">
                              <Accordion.Item eventKey="pending" className="border-0">
                                <Accordion.Header className="">
                                  <span className="fw-semibold p-2">
                                    <i className={`${myAssignmentsSubTab === 'forms' ? 'bi bi-ui-checks-grid' : 'bi bi-file-earmark-text'} me-2`}></i>
                                    {myAssignmentsSubTab === 'forms' ? 'New/Pending Forms' : 'New/Pending Assignments'}
                                    <Badge bg="secondary" className="ms-2">{pendingDocuments.length}</Badge>
                                  </span>
                                </Accordion.Header>
                                <Accordion.Body className="pt-0 rounded-3">
                                  {pendingDocuments.length === 0 ? (
                                    <div className="text-center py-5">
                                      <div className="text-muted mb-2 bg-secondary rounded-2 p-2" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                                        <i className={`${myAssignmentsSubTab === 'forms' ? 'bi bi-ui-checks-grid' : 'bi bi-file-earmark-text'} fs-2`}></i>
                                      </div>
                                      <h6 className="text-muted mb-1">{myAssignmentsSubTab === 'forms' ? 'No pending forms' : 'No pending assignments'}</h6>
                                      <small className="text-muted">{myAssignmentsSubTab === 'forms' ? 'All forms have been submitted or completed.' : 'All assignments have been submitted or completed.'}</small>
                                    </div>
                                  ) : (
                                    pendingDocuments.map(a => (
                                      <div
                                        key={`doc-pending-${a.id}`}
                                        className="border-alt-light custom-hover-bg-alt rounded-3 mb-2 p-3 gap-2 d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start"
                                        onClick={() => setMyAssignmentsSelectedId(a.id)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                          <div className="d-flex flex-row align-items-center gap-3">
                                            <Badge bg="secondary" className="rounded-2 p-2">
                                              <i className={`${(a.form || a.formId) ? 'bi bi-ui-checks-grid' : a.documentRequest ? 'bi bi-file-earmark-plus-fill' : getFileIcon(a.document?.originalName, a.document?.mimeType)} fs-4`}></i>
                                            </Badge>
                                            <div>
                                                <div className="gap-2 d-flex flex-row align-items-center">
                                                <span className="fw-semibold">{a.customName || a.form?.title || a.document?.title || a.documentRequest?.title || ((a.form || a.formId) ? 'Form' : 'Document')}</span> 
                                                {getSubmittedFilesForAssignment[a.id]?.map((fileData) => (
                                                    <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                                  ))}
                                              </div>
                                              
                                            </div>
                                          </div>
                                          <div className="d-flex flex-row align-items-center gap-2">
                                          {(a.document?.category || a.form?.status || a.formId || a.form) && (
                                                <Badge bg="secondary">{(a.form || a.formId) ? 'Form' : (a.document?.category || a.form?.status || 'No category')}</Badge>
                                              )}
                                          </div>
                                          <div className="d-flex flex-row justify-content-between align-items-md-center gap-3">
                                            <Badge bg={a.status === 'completed' ? 'success' : 'secondary'}>{a.status}</Badge>
                                            <small className="text-muted">
                                              <span className="fw-semibold">Due:</span> {a.dueDate ? new Date(a.dueDate).toLocaleDateString( 'en-US', { day: 'numeric', month: 'short', year: 'numeric' } ) : 'No due date'}
                                            </small>
                                          </div>
                                        </div>
                                    ))
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                            <Accordion activeKey={expandedSections.completedDocuments ? 'completed' : null} onSelect={k => toggleSection('completedDocuments')} className="mb-3">
                              <Accordion.Item eventKey="completed" className="border-0">
                                <Accordion.Header>
                                  <span className="fw-semibold p-2">
                                    <i className={`${myAssignmentsSubTab === 'forms' ? 'bi bi-ui-checks-grid' : 'bi bi-file-earmark-check'} text-success me-2`}></i>
                                    {myAssignmentsSubTab === 'forms' ? 'Submitted/Completed Forms' : 'Submitted/Completed Assignments'}
                                    <Badge bg="secondary" className="ms-2">{completedDocuments.length}</Badge>
                                  </span>
                                </Accordion.Header>
                                <Accordion.Body className="pt-0 rounded-3">
                                  {completedDocuments.length === 0 ? (
                                    <div className="text-center py-5">
                                      <div className="text-muted mb-2 bg-secondary rounded-2 p-2" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                                        <i className="bi bi-check-circle fs-2"></i>
                                      </div>
                                      <h6 className="text-muted mb-1">{myAssignmentsSubTab === 'forms' ? 'No completed forms' : 'No completed assignments'}</h6>
                                      <small className="text-muted">{myAssignmentsSubTab === 'forms' ? 'No forms have been submitted or completed yet.' : 'No assignments have been submitted or completed yet.'}</small>
                                    </div>
                                  ) : (
                                    completedDocuments.map(a => (
                                      
                                      <div
                                        key={`doc-completed-${a.id}`}
                                        className="border-alt-light custom-hover-bg-alt rounded-3 mb-2 p-3 gap-2 d-flex flex-md-row flex-column justify-content-md-between justify-content-start align-items-md-center align-items-start"
                                        onClick={() => setMyAssignmentsSelectedId(a.id)}
                                        style={{ cursor: 'pointer' }}
                                      >
                                          <div className="d-flex flex-row align-items-center gap-3">
                                            <Badge bg="secondary" className="rounded-2 p-2">
                                              <i className={`${(a.form || a.formId) ? 'bi bi-ui-checks-grid' : a.documentRequest ? 'bi bi-file-earmark-plus-fill' : getFileIcon(a.document?.originalName, a.document?.mimeType)} fs-4`}></i>
                                            </Badge>
                                            <div>
                                              <div className="gap-2 d-flex flex-row align-items-center">
                                            <span className="fw-semibold">{a.customName || a.form?.title || a.document?.title || a.documentRequest?.title || ((a.form || a.formId) ? 'Form' : 'Document')}</span>
                                                {getSubmittedFilesForAssignment[a.id]?.map((fileData) => (
                                                    <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                                  ))}
                                              </div>
                                              {/* {a.documentRequest?.description && (
                                                <small className="text-muted">{a.documentRequest.description}</small>
                                              )} */}
                                              {/* Show file size and date created if document is available */}
                                              {( a.document?.createdAt) && (
                                                <small className="text-muted">
                                                  <span className="fw-semibold">Created:</span> {new Date(a.document.createdAt).toLocaleDateString()}
                                                </small>
                                              )}
                                            </div>
                                          </div>
                                          <div className="d-flex flex-row align-items-center gap-2">
                                          {(a.document?.category || a.form?.status || a.formId || a.form) && (
                                                <Badge bg="secondary">{(a.form || a.formId) ? 'Form' : (a.document?.category || a.form?.status || 'No category')}</Badge>
                                              )}
                                          </div>
                                          <div className="d-flex flex-row justify-content-between align-items-md-center gap-3">
                                            <small className="text-muted">
                                              <span className="fw-semibold">Due:</span> {a.dueDate ? new Date(a.dueDate).toLocaleDateString( 'en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No due date'}
                                            </small>
                                            <Badge bg={a.status === 'completed' ? 'success' : 'secondary'}>{a.status}</Badge>
                                            
                                          </div>
                                        </div>
                                    ))
                                  )}
                                </Accordion.Body>
                              </Accordion.Item>
                            </Accordion>
                            </div>
                          </>
                        );
                      }
                    }

                    // Show details for selected item - unchanged from previous version
                    if (myAssignmentsSubTab === 'packages') {
                      const selectedGroup = packageGroups.find(g => g.package.id === myAssignmentsSelectedId);
                      if (!selectedGroup) {
                        setMyAssignmentsSelectedId(null);
                        return null;
                      }

                      const g = selectedGroup;
                      const assignment = g.packageAssignment;
                      const packageFormAssignments = g.formAssignments || [];

                      return (
                        <Card className="shadow-sm border-0 p-0">
                          <Card.Header className="border-0 p-4 border-bottom d-flex justify-content-between align-items-center" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                            <div className="d-flex align-items-center gap-4">
                              <Button size="sm" variant="light-solid" onClick={() => setMyAssignmentsSelectedId(null)}>
                                <i className="bi bi-arrow-left"></i>
                              </Button>
                              <div className="d-flex flex-column align-items-start">
                              <h5 className="mb-0">{assignment?.customName || g.package.name}</h5>
                              <small className="text-muted">Due: {assignment?.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}</small>
                              </div>
                            </div>
                          </Card.Header>
                          <Card.Body className="px-4">
                            <div className="d-flex flex-column align-items-start">
                              <small className="mb-2 fw-semibold">Description:</small> 
                              <p>{g.package.description}</p>
                            </div>
                            {assignment?.instructions && (
                              <div className="d-flex flex-column align-items-start">
                                <small className="mb-2 fw-semibold">Instructions</small>
                                <p>{assignment.instructions}</p>
                              </div>
                            )}
                            {packageFormAssignments.length > 0 && (
                              <div className="mt-3 border rounded-3 p-3 bg-light-subtle d-flex flex-column gap-3">
                                <h6 className="fw-semibold mb-0">Assigned Forms</h6>
                                {packageFormAssignments.map((formAssignment) => {
                                  const latestFormSubmission = getLatestMyFormSubmissionForAssignment(formAssignment.id);
                                  const isReadOnlyForm = ['submitted', 'approved'].includes(String(latestFormSubmission?.status || '').toLowerCase());
                                  return (
                                    <div key={`pkg-form-${formAssignment.id}`} className="border rounded-3 p-3 bg-white d-flex flex-row justify-content-between align-items-start gap-3">
                                      <div>
                                        <h6 className="fw-semibold mb-1">{formAssignment.form?.title || formAssignment.customName || 'Form'}</h6>
                                        <p className="text-muted mb-2">{formAssignment.form?.description || 'No description provided.'}</p>
                                        {latestFormSubmission?.status === 'rejected' && latestFormSubmission?.reviewNotes ? (
                                          <Alert variant="warning" className="py-2 mb-0">
                                            <strong>Rejection reason:</strong> {latestFormSubmission.reviewNotes}
                                          </Alert>
                                        ) : null}
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="primary"
                                        onClick={() => {
                                          setActiveFormAssignment(formAssignment);
                                          setShowCompleteFormModal(true);
                                        }}
                                      >
                                        {isReadOnlyForm ? 'View Form' : 'Open Form'}
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            <DocumentCompletionPageV2
                              packageAssignmentId={assignment?.id}
                              embedded={true}
                              hideNavbar={true}
                              showContinueToApp={false}
                            />
                          </Card.Body>
                        </Card>
                      );
                    } else {
                      // Standalone document details
                      const selectedAssignment = (myAssignmentsSubTab === 'forms' ? standaloneFormAssignments : standaloneGroups
                        .flatMap(g => [...g.documentAssignments, ...g.documentRequestAssignments]))
                        .find(a => a.id === myAssignmentsSelectedId);
                      
                      if (!selectedAssignment) {
                        setMyAssignmentsSelectedId(null);
                        return null;
                      }

                      const a = selectedAssignment;
                      return (
                        <Card className="shadow-sm border-0 p-0">
                          <Card.Header className="border-0 px-4 py-3 border-bottom d-flex justify-content-between align-items-center" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                            <div className="d-flex align-items-center gap-4">
                              <Button size="sm" variant="light-solid" onClick={() => setMyAssignmentsSelectedId(null)}>
                                <i className="bi bi-arrow-left"></i>
                              </Button>
                              <h5 className="mb-0">{a.customName || a.form?.title || a.document?.title || a.documentRequest?.title || ((a.form || a.formId) ? 'Form' : 'Document')}</h5>
                            </div>
                          </Card.Header>
                          <Card.Body className="p-4">
                            {a.instructions && (
                              <div className="d-flex flex-column align-items-start">
                                <h6 className="mb-2 fw-semibold">Instructions</h6>
                                <p>{a.instructions}</p>
                              </div>
                            )}

                            <div className="mb-3 bg-light rounded-2 p-2 ps-3 d-flex flex-row justify-content-between align-items-center">
                              <div className="d-flex gap-4 align-items-center">
                              <span className="fw-semibold">{a.customName || a.form?.title || a.document?.title || a.documentRequest?.title || ((a.form || a.formId) ? 'Form' : 'Document')}</span>
                                <Badge className="d-none d-md-inline" bg={a.status === 'completed' ? 'success' : 'secondary'}>{a.status}</Badge>
                                <Badge className="d-none d-md-inline" bg="secondary">{(a.form || a.formId) ? 'Form' : (a.document?.category || a.form?.status || 'No category')}</Badge>
                                <small className="text-muted">
                                  <span className="fw-semibold">Due:</span> {a.dueDate ? new Date(a.dueDate).toLocaleDateString() : 'No due date'}
                                </small>
                              </div>
                              {a.document?.webPath && (
                                <Button size="sm" variant="light" onClick={async (e) => { 
                                  e.preventDefault(); 
                                  const url = `${API_ENDPOINTS.PROFILE_PICS}${a.document.webPath}`; 
                                  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } }); 
                                  const blob = await r.blob(); 
                                  const dl = URL.createObjectURL(blob); 
                                  const link = document.createElement('a'); 
                                  link.href = dl; 
                                  link.download = a.document.originalName || a.document.title; 
                                  document.body.appendChild(link); 
                                  link.click(); 
                                  document.body.removeChild(link); 
                                  URL.revokeObjectURL(dl); 
                                }}>
                                  <i className="bi bi-download"></i> <span className="d-none d-md-inline">Download</span>
                                </Button>
                              )}
                            </div>

                            {/* Form assignment flow */}
                            {a.form && (
                              <div className="mt-3 border rounded-3 p-3 bg-light-subtle">
                                {(() => {
                                  const latestFormSubmission = getLatestMyFormSubmissionForAssignment(a.id);
                                  const isReadOnlyForm = ['submitted', 'approved'].includes(String(latestFormSubmission?.status || '').toLowerCase());
                                  return (
                                <>
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6 className="fw-semibold mb-1">{a.form.title}</h6>
                                    <p className="text-muted mb-2">{a.form.description || 'No description provided.'}</p>
                                    {(() => {
                                      return latestFormSubmission?.status === 'rejected' && latestFormSubmission?.reviewNotes ? (
                                        <Alert variant="warning" className="py-2 mb-2">
                                          <strong>Rejection reason:</strong> {latestFormSubmission.reviewNotes}
                                        </Alert>
                                      ) : null;
                                    })()}
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => {
                                      setActiveFormAssignment(a);
                                      setShowCompleteFormModal(true);
                                    }}
                                  >
                                    {isReadOnlyForm ? 'View Form' : 'Open Form'}
                                  </Button>
                                </div>
                                </>
                                  );
                                })()}
                              </div>
                            )}

                            {/* Uploaded Files for Standalone Document Assignment */}
                            {!a.form && (() => {
                              const assignmentFiles = uploadedFiles[a.id] || [];
                              const submittedAssignmentFiles = getSubmittedFilesForAssignment[a.id] || [];
                              const hasUploadedFiles = assignmentFiles.length > 0;
                              const hasSubmittedFiles = submittedAssignmentFiles.length > 0;
                              
                              return (hasUploadedFiles || hasSubmittedFiles) ? (
                                <div className="mt-3">
                                  {hasUploadedFiles && (
                                    <>
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h6 className="mb-0 fs-6">Uploaded Files</h6>
                                        <Button 
                                          size="sm" 
                                          variant="success" 
                                          onClick={() => submitUploadedFiles(a.id)}
                                          disabled={uploading}
                                        >
                                          {uploading ? 'Submitting...' : 'Submit'}
                                        </Button>
                                      </div>
                                      <div className="list-group">
                                        {assignmentFiles.map((fileData) => (
                                          <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                            <div className="d-flex align-items-center gap-2">
                                              <i className={`${getFileIcon(fileData.file.name)} fs-6`}></i>
                                              <span className="small">{fileData.file.name}</span>
                                              <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                            </div>
                                            <Button 
                                              size="sm" 
                                              variant="outline-danger" 
                                              onClick={() => removeUploadedFile(a.id, fileData.id)}
                                            >
                                              <i className="bi bi-trash"></i>
                                            </Button>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                  
                                  {hasSubmittedFiles && (
                                    <>
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <small className="mb-2 fw-semibold">Submitted Files</small>
                                      </div>
                                      <div className="list-group">
                                        {submittedAssignmentFiles.map((fileData) => (
                                          <div key={fileData.id} className="list-group-item d-flex justify-content-between align-items-center py-2">
                                            <div className="d-flex align-items-center gap-2">
                                              <i className={`${getFileIcon(fileData.file.name)} fs-6 text-muted`}></i>
                                              <span className="small text-muted">{fileData.file.name}</span>
                                              <small className="text-muted">({(fileData.file.size / 1024).toFixed(1)} KB)</small>
                                            </div>
                                            <Badge bg="secondary" className="ms-1"><i className="bi bi-cloud-check-fill"></i></Badge>
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className="text-center d-flex flex-column align-items-center justify-content-center rounded-3 py-5" style={{ border: '2px dashed var(--border-bg)' }}>
                                  <img src="/images/undraw_files-uploading_qf8u.svg" alt="Upload Document" className="mb-3" style={{ maxWidth: '160px', width: '100%' }} />
                                  <Button size="sm" variant="outline-primary" onClick={() => openUploadModal(a)}>
                                    
                                    Upload Document
                                  </Button>
                                </div>
                              );
                            })()}
                          </Card.Body>
                        </Card>
                      );
                    }
                  })()}
                </Col>
              </Row>
            )}
            {activeTab === 'completed' && (
              <Row>
                <Col lg={2} md={3} className="d-none d-md-block mb-3">
                  <Card className="bg-white shadow-sm border-0 p-0 py-2 custom-sticky-top" style={{ top: '5rem' }}>
                    <Nav variant="pills" className="flex-column">
                      <Nav.Item>
                        <Nav.Link className="disabled fw-semibold">Menu</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="packages" active={completedSubTab === 'packages'} onClick={() => { setCompletedSubTab('packages'); setCompletedSelectedId(null); }}>
                          Packages
                        </Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link eventKey="standalone" active={completedSubTab === 'standalone'} onClick={() => { setCompletedSubTab('standalone'); setCompletedSelectedId(null); }}>
                          Standalone Documents
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  </Card>
                </Col>
                <Col md={12} className="d-md-none mb-3">
                  <Card className="bg-white shadow-sm border-0 p-0">
                    <Accordion>
                      <Accordion.Item eventKey="menu" className="border-0 custom-accordian-border">
                        <Accordion.Header>
                          <span className="fw-semibold">Menu</span>
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                          <Nav variant="pills" className="flex-column mb-2">
                            <Nav.Item>
                              <Nav.Link eventKey="packages" active={completedSubTab === 'packages'} onClick={() => { setCompletedSubTab('packages'); setCompletedSelectedId(null); }}>
                                Packages
                              </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                              <Nav.Link eventKey="standalone" active={completedSubTab === 'standalone'} onClick={() => { setCompletedSubTab('standalone'); setCompletedSelectedId(null); }}>
                                Standalone Documents
                              </Nav.Link>
                            </Nav.Item>
                          </Nav>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  </Card>
                </Col>
                <Col lg={10} md={9}>
                  {(() => {
                    // Group completed submissions by package and customName
                    const groupsMap = new Map();
                    for (const s of myCompletedSubmissions) {
                      const pkgId =
                        (s.assignment && s.assignment.packageId) ||
                        (s.document && s.document.packageId);
                      if (pkgId) {
                        const customName = s.assignment?.customName || 'original';
                        const key = `${pkgId}_${customName}`;
                        if (!groupsMap.has(key)) groupsMap.set(key, []);
                        groupsMap.get(key).push(s);
                      } else {
                        if (!groupsMap.has('standalone')) groupsMap.set('standalone', []);
                        groupsMap.get('standalone').push(s);
                      }
                    }

                    const groups = Array.from(groupsMap.entries()).map(([key, items]) => {
                      if (key === 'standalone') {
                        return { key, pkgId: 'standalone', pkg: null, items };
                      }
                      const [pkgId, customName] = key.split('_');
                      const pkg = packages.find(p => p.id === parseInt(pkgId, 10));
                      return { key, pkgId, pkg, customName, items };
                    });

                    const packageGroups = groups.filter(g => g.pkg);
                    const standaloneGroups = groups.filter(g => !g.pkg);
                    const packageFormSubmissionGroups = (myFormSubmissions || [])
                      .filter((s) => {
                        const assignmentPackageId = s.assignment?.packageId || s.assignment?.package?.id || null;
                        const status = String(s.status || '').toLowerCase();
                        return Boolean(assignmentPackageId) && status !== 'rejected';
                      })
                      .reduce((acc, s) => {
                        const pkgId = Number(s.assignment?.packageId || s.assignment?.package?.id);
                        const customName = s.assignment?.customName || 'original';
                        const key = `${pkgId}_${customName}`;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(s);
                        return acc;
                      }, {});
                    const hasPackageFormSubmissions = Object.values(packageFormSubmissionGroups).some((items) => items.length > 0);
                    const hasCompletedContent = myCompletedSubmissions.length > 0 || hasPackageFormSubmissions;

                    if (!hasCompletedContent) {
                      return (
                        <Card className="shadow-sm border-0 p-0">
                          <Card.Header className="border-0 px-4 py-3">
                            <div className="d-flex flex-column">
                              <h6 className="mb-0 fw-semibold">Completed Submissions</h6>
                              <p className="text-muted small mb-0">
                                {completedSubTab === 'packages'
                                  ? `showing ${packageGroups.length} package submissions`
                                  : `showing ${standaloneGroups.flatMap(g => g.items).length} standalone document submissions`}
                              </p>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            <div className="text-center py-5">
                              <div className="text-muted mb-3">
                                <i className="bi bi-check-circle-fill fs-1"></i>
                              </div>
                              <h6 className="text-muted">No completed submissions yet</h6>
                              <p className="text-muted">
                                You haven't submitted any onboarding documents for review.
                              </p>
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    }

                    // If no selection, show list view
                    if (!completedSelectedId) {
                      return (
                        <Card className="shadow-sm border-0 p-0">
                          <Card.Header className="border-0 px-4 py-3">
                            <div className="d-flex flex-column">
                              <h6 className="mb-0 fw-semibold">Completed Submissions</h6>
                              <p className="text-muted small mb-0">
                                {completedSubTab === 'packages'
                                  ? `showing ${packageGroups.length} package submissions`
                                  : `showing ${standaloneGroups.flatMap(g => g.items).length} standalone document submissions`}
                              </p>
                            </div>
                          </Card.Header>
                          <Card.Body className="p-0">
                            {completedSubTab === 'packages' ? (
                              packageGroups.length === 0 ? (
                                <div className="text-center py-5">
                                  <div className="text-muted mb-3">
                                    <i className="bi bi-folder fs-1"></i>
                                  </div>
                                  <h6 className="text-muted">No package submissions</h6>
                                  <p className="text-muted">You haven't submitted any package documents.</p>
                                </div>
                              ) : (
                                <Table hover responsive className="mb-0">
                                  <thead>
                                    <tr className="custom-header-bg">
                                      <th className="ps-3">Type</th>
                                      <th className="text-start">Package</th>
                                      <th>Submissions</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {packageGroups.map((g) => {
                                      const headerTitle = (g.customName && g.customName !== 'original') ? g.customName : (g.pkg ? g.pkg.name : 'Package');
                                      const approvedCount = g.items.filter(s => s.status === 'approved').length;
                                      const rejectedCount = g.items.filter(s => s.status === 'rejected').length;
                                      const pendingCount = g.items.filter(s => s.status === 'submitted').length;
                                      return (
                                        <tr 
                                          key={`pkg-${g.pkg.id}-${g.customName}`} 
                                          className="assignment-hover"
                                          onClick={() => setCompletedSelectedId(g.key)}
                                          style={{cursor: 'pointer'}}
                                        >
                                          <td className="ps-3">
                                            <Badge bg="secondary" className="rounded-2 p-2">
                                              <i className="bi bi-folder-fill text-warning fs-5"></i>
                                            </Badge>
                                          </td>
                                          <td className="text-start">
                                            <div className="fw-semibold">{headerTitle}</div>
                                            {g.pkg.description && (
                                              <small className="text-muted">{g.pkg.description}</small>
                                            )}
                                          </td>
                                          <td>
                                            <Badge bg="secondary">{g.items.length}</Badge>
                                          </td>
                                          <td>
                                            <div className="d-flex gap-1 justify-content-center align-items-center">
                                              {approvedCount > 0 && <Badge bg="success">{approvedCount} approved</Badge>}
                                              {pendingCount > 0 && <Badge bg="warning" text="dark">{pendingCount} pending</Badge>}
                                              {rejectedCount > 0 && <Badge bg="danger">{rejectedCount} rejected</Badge>}
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              )
                            ) : (
                              standaloneGroups.length === 0 ? (
                                <div className="text-center py-5">
                                  <div className="text-muted mb-3">
                                    <i className="bi bi-file-earmark fs-1"></i>
                                  </div>
                                  <h6 className="text-muted">No standalone document submissions</h6>
                                  <p className="text-muted">You haven't submitted any standalone documents.</p>
                                </div>
                              ) : (
                                <Table hover responsive className="mb-0">
                                  <thead>
                                    <tr className="custom-header-bg">
                                      <th className="ps-3">Type</th>
                                      <th className="text-start">Document title</th>
                                      <th>Document submitted for</th>
                                      <th>Category</th>
                                      <th>Status</th>
                                      <th>Submitted</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {standaloneGroups.flatMap(g => g.items).map((s) => (
                                      <tr 
                                        key={`sub-${s.id}`} 
                                        className="assignment-hover"
                                        onClick={() => setCompletedSelectedId(`standalone-${s.id}`)}
                                        style={{cursor: 'pointer'}}
                                      >
                                        <td className="ps-3">
                                          <Badge bg="secondary" className="rounded-2 p-2">
                                            <i className={`${getFileIcon(s.originalName, s.mimeType)} fs-5`}></i>
                                          </Badge>
                                        </td>
                                        <td className="text-start">
                                          <div className="fw-semibold">{s.originalName || 'Document'}</div>
                                        </td>
                                        <td>
                                          {s.assignment?.customName || 'No assignment'}
                                        </td>
                                        <td>
                                          <Badge bg="secondary">{s.document?.category || 'No category'}</Badge>
                                        </td>
                                        <td>
                                          <Badge bg={s.status === 'approved' ? 'success' : s.status === 'rejected' ? 'danger' : 'secondary'}>
                                            {s.status}
                                          </Badge>
                                        </td>
                                        <td>{new Date(s.createdAt).toLocaleString()}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )
                            )}
                          </Card.Body>
                        </Card>
                      );
                    }

                    // Show details for selected item
                    if (completedSubTab === 'packages') {
                      const selectedGroup = packageGroups.find(g => g.key === completedSelectedId);
                      if (!selectedGroup) {
                        setCompletedSelectedId(null);
                        return null;
                      }

                      const g = selectedGroup;
                      const headerTitle = (g.customName && g.customName !== 'original') ? g.customName : (g.pkg ? g.pkg.name : 'Package');
                      const packageFormSubmissions = packageFormSubmissionGroups[g.key] || [];

                      return (
                        <Card className="shadow-sm border-0 p-0">
                          <Card.Header className="border-0 px-4 py-3 border-bottom d-flex justify-content-between align-items-center" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                            <div className="d-flex align-items-center gap-4">
                              <Button size="sm" variant="light-solid" onClick={() => setCompletedSelectedId(null)}>
                                <i className="bi bi-arrow-left"></i>
                              </Button>
                              <div className="d-flex flex-column align-items-start">
                              <h5 className="mb-0">{headerTitle}</h5>
                              {g.pkg && g.pkg.description && (
                                <p className="text-muted mb-0 small">{g.pkg.description}</p>
                              )}
                              </div>
                            </div>
                          </Card.Header>
                          <Card.Body>
                            {g.pkg && g.pkg.instructions && (
                              <div className="mb-3">
                                <small className="mb-2 fw-semibold">Package Instructions</small>
                                <p className="text-muted mb-0 small">{g.pkg.instructions}</p>
                              </div>
                            )}
                            <div className="mb-3">
                              <small className="mb-2 fw-semibold">Submitted Documents</small>
                            </div>
                            <Table responsive className="custom-modern-responsive custom-modern-responsive-hover">
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th className="text-start">Document</th>
                                  <th>Status</th>
                                  <th>Submitted</th>
                                  <th>Reviewed</th>
                                  <th className="text-end">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {g.items.map((s) => (
                                  <tr key={s.id}>
                                    <td>
                                      <Badge bg="secondary" className="rounded-2 p-2">
                                        <i className={`${getFileIcon(s.originalName, s.mimeType)} fs-5`}></i>
                                      </Badge>
                                    </td>
                                    <td className="text-start text-break">
                                      {s.originalName || '—'}
                                    </td>
                                    <td>
                                      <Badge bg={s.status === 'approved' ? 'success' : s.status === 'rejected' ? 'danger' : 'secondary'}>
                                        {s.status}
                                      </Badge>
                                    </td>
                                    <td>{new Date(s.createdAt).toLocaleString()}</td>
                                    <td>
                                      {s.reviewedAt ? (
                                        <div>
                                          <div>{new Date(s.reviewedAt).toLocaleString()}</div>
                                          {s.reviewer && (
                                            <small className="text-muted">by {s.reviewer.firstName} {s.reviewer.lastName}</small>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-muted">Pending</span>
                                      )}
                                    </td>
                                    <td className="text-end">
                                      {s.status !== 'rejected' && (
                                        <Button size="sm" variant="light" onClick={async (e) => { 
                                          e.preventDefault(); 
                                          const url = `${API_ENDPOINTS.PROFILE_PICS}${s.webPath || s.filePath}`; 
                                          const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } }); 
                                          const blob = await r.blob(); 
                                          const dl = URL.createObjectURL(blob); 
                                          const a = document.createElement('a'); 
                                          a.href = dl; 
                                          a.download = s.originalName; 
                                          a.click(); 
                                          URL.revokeObjectURL(dl); 
                                        }}>
                                          <i className="bi bi-download"></i>
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                            {packageFormSubmissions.length > 0 && (
                              <div className="mt-4">
                                <small className="mb-2 fw-semibold d-block">Submitted Forms</small>
                                <Accordion className="d-flex flex-column gap-2">
                                  {packageFormSubmissions.map((s) => (
                                    <Accordion.Item key={`completed-pkg-form-${s.id}`} eventKey={`completed-package-form-${s.id}`} className="border rounded-3 overflow-hidden">
                                      <Accordion.Header>
                                        <div className="d-flex w-100 justify-content-between align-items-start pe-2">
                                          <div>
                                            <div className="fw-semibold">{s.form?.title || s.assignment?.form?.title || 'Form submission'}</div>
                                            <div className="small text-muted">{new Date(s.createdAt).toLocaleString()}</div>
                                          </div>
                                          <div className="d-flex align-items-center gap-2">
                                            <Badge bg={s.status === 'approved' ? 'success' : s.status === 'submitted' ? 'info' : 'secondary'}>
                                              {s.status}
                                            </Badge>
                                            <Button
                                              size="sm"
                                              variant="light"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePrintFormSubmission({
                                                  title: s.form?.title || s.assignment?.form?.title || 'Form submission',
                                                  description: s.form?.description || s.assignment?.form?.description || '',
                                                  formData: s.formData || {},
                                                  schema: s.form?.schema || s.assignment?.form?.schema || {},
                                                  submittedAt: s.createdAt || null
                                                });
                                              }}
                                            >
                                              <i className="bi bi-printer me-1"></i>
                                              Export
                                            </Button>
                                          </div>
                                        </div>
                                      </Accordion.Header>
                                      <Accordion.Body className="d-flex flex-column gap-2">
                                        {renderFormSubmissionPreview(s)}
                                      </Accordion.Body>
                                    </Accordion.Item>
                                  ))}
                                </Accordion>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      );
                    } else {
                      // Standalone document details
                      const submissionId = completedSelectedId.replace('standalone-', '');
                      const selectedSubmission = standaloneGroups
                        .flatMap(g => g.items)
                        .find(s => s.id === parseInt(submissionId));
                        
                      
                      if (!selectedSubmission) {
                        setCompletedSelectedId(null);
                        return null;
                      }

                      const s = selectedSubmission;
                      return (
                        <Card className="shadow-sm border-0 p-0">
                          <Card.Header className="border-0 px-4 py-3 border-bottom d-flex justify-content-between align-items-center" style={{backgroundColor: 'var(--bs-body-bg)'}}>
                            <div className="d-flex align-items-center gap-4">
                              <Button size="sm" variant="light-solid" onClick={() => setCompletedSelectedId(null)}>
                                <i className="bi bi-arrow-left"></i>
                              </Button>
                              <h5 className="mb-0">
                                {s.document?.title || 'Document'}
                              </h5>
                            </div>
                          </Card.Header>
                          <Card.Body className="p-4">
                            {/* Instructions, if any */}
                            {s.document?.instructions || s.instructions && (
                              <div className="d-flex flex-column align-items-start">
                                <h6 className="mb-2 fw-semibold">Instructions</h6>
                                <p>{s.document?.instructions || s.instructions}</p>
                              </div>
                            )}


                            {/* Metadata Row */}
                            <div className="mb-3 bg-light rounded-2 p-2 ps-3 d-flex flex-row justify-content-between align-items-center">
                              <div className="d-flex gap-4 align-items-center">
                                <span className="fw-semibold">{s.originalName || 'Document'}</span>
                                <Badge className="d-none d-md-inline" bg={s.status === 'approved' ? 'success' : s.status === 'rejected' ? 'danger' : 'secondary'}>
                                  {s.status}
                                </Badge>
                                <Badge className="d-none d-md-inline" bg="secondary">{s.document?.category || 'No category'}</Badge>
                                <small className="text-muted">
                                  <span className="fw-semibold">Submitted:</span> {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '-'}
                                </small>
                                {s.reviewedAt && (
                                  <small className="text-muted">
                                    <span className="fw-semibold">Reviewed:</span> {new Date(s.reviewedAt).toLocaleDateString()}
                                    {s.reviewer && ` by ${s.reviewer.firstName} ${s.reviewer.lastName}`}
                                  </small>
                                )}
                              </div>
                              {s.status !== 'rejected' && (
                                <Button size="sm" variant="light" onClick={async (e) => { 
                                  e.preventDefault(); 
                                  const url = `${API_ENDPOINTS.PROFILE_PICS}${s.webPath || s.filePath}`; 
                                  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } }); 
                                  const blob = await r.blob(); 
                                  const dl = URL.createObjectURL(blob); 
                                  const link = document.createElement('a'); 
                                  link.href = dl; 
                                  link.download = s.originalName; 
                                  document.body.appendChild(link); 
                                  link.click(); 
                                  document.body.removeChild(link); 
                                  URL.revokeObjectURL(dl); 
                                }}>
                                  <i className="bi bi-download"></i> <span className="d-none d-md-inline">Download</span>
                                </Button>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      );
                    }
                  })()}
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>

      <Modal show={showNewPackageModal} onHide={() => setShowNewPackageModal(false)}>
        <Modal.Header closeButton className="border-0">
          <div className="d-flex flex-column justify-content-start w-100">
          <Modal.Title>New Package</Modal.Title>
          <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info mb-3">
            <div className="d-flex align-items-start">
              <i className="bi bi-info-circle-fill me-2 mt-1"></i>
              <div>
                <strong>Create a Package</strong>
                <p className="mb-0 small">Packages group related documents and requests together. Residents assigned to a package will need to complete all documents and requests within it. This helps organize onboarding requirements by topic or department.</p>
              </div>
            </div>
          </div>
          <Form>
            <Row>
              <Col md={6}>
            <Form.Group className="mb-3">
              <FloatingLabel controlId="floatingInput" label="Name" className="mb-3">
              <Form.Control value={newPackage.name} onChange={(e) => setNewPackage((p) => ({ ...p, name: e.target.value }))} placeholder="Enter package name" />
              </FloatingLabel>
            </Form.Group>
              </Col>
              <Col md={6}>
            <Form.Group className="mb-3">
              <FloatingLabel controlId="floatingInput" label="Category" className="mb-3">
              <Form.Control
                list="package-category-options"
                placeholder="Select or type a category"
                value={newPackage.category}
                onChange={(e) => setNewPackage((p) => ({ ...p, category: e.target.value }))}
              />
              </FloatingLabel>
              <datalist id="package-category-options" >
                {categoryOptions.map((opt) => (
                  <option key={opt} value={opt}/>
                ))}
              </datalist>
            </Form.Group>
              </Col>
              <Col md={12}>
                          <Form.Group>
              <FloatingLabel controlId="floatingInput" label="Description" className="mb-3">
              <Form.Control as="textarea" value={newPackage.description} onChange={(e) => setNewPackage((p) => ({ ...p, description: e.target.value }))} placeholder="Enter package description" style={{ height: '100px' }} />
              </FloatingLabel>
            </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowNewPackageModal(false)}>Cancel</Button>
          <Button size="sm" variant="primary" onClick={handleCreatePackage}>Create</Button>
        </Modal.Footer>
      </Modal>
      <OnboardingAssignModal
        show={showAssignModal}
        onHide={() => setShowAssignModal(false)}
        target={assignTarget}
        onAssigned={() => fetchData()}
      />
      <OnboardingUploadModal
        show={showUploadModal}
        onHide={() => setShowUploadModal(false)}
        onUpload={handleUploadMultiple}
        uploading={uploading}
        isDocumentUpload={!activeUploadAssignment}
        isAssignmentUpload={!!activeUploadAssignment}
        categoryOptions={categoryOptions}
      />
      <OnboardingFormModal
        show={showCompleteFormModal}
        onHide={() => {
          setShowCompleteFormModal(false);
          setActiveFormAssignment(null);
        }}
        assignment={activeFormAssignment}
        form={activeFormAssignment?.form || forms.find((f) => Number(f.id) === Number(activeFormAssignment?.formId)) || null}
        initialData={getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.formData || {}}
        readOnly={['submitted', 'approved'].includes(String(getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.status || '').toLowerCase())}
        reviewNotes={getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.status === 'rejected' ? (getLatestMyFormSubmissionForAssignment(activeFormAssignment?.id)?.reviewNotes || '') : ''}
        signContext={
          activeFormAssignment?.id
            ? { mode: 'standalone', assignmentId: activeFormAssignment.id }
            : null
        }
        onSubmit={(formData) => handleSubmitFormAssignment(activeFormAssignment, formData)}
      />

      {/* Package content builder modal (sections + items) */}
      <Modal
        show={showPackageContentModal}
        onHide={() => setShowPackageContentModal(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton className="border-0">
          <div className="d-flex flex-column gap-2 w-100">
            <Modal.Title>
              {packageSections.length > 0 ? 'Edit content' : 'Add content'}
              {(() => {
                const p = packages.find(x => x.id === manageSelectedPackageId);
                return p?.name ? ` — ${p.name}` : '';
              })()}
            </Modal.Title>
            <div className="text-hl"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row>
             {/* left: simple numbered preview of sections + items */}
            <Col md={3}>
              {packageSections.length === 0 ? (
                <div className="text-muted small">
                  Add at least one section to see a the list .
                </div>
              ) : (
                <div className="d-flex flex-column g-1 p-2 rounded-3 border">
                  {packageSections.map((s, sectionIndex) => (
                    
                        <div className="d-flex flex-column align-items-startrounded-3 p-2 rounded-3 custom-hover-bg-alt">
                            <div className="d-flex flex-row justify-content-between w-100">
                              <small className="text-muted fw-semibold">Section {sectionIndex + 1}</small> 
                            <span className="text-muted small">{(s.items || []).length} item{(s.items || []).length === 1 ? '' : 's'}</span>

                              </div><span className="fw-semibold ">{s.title || 'Untitled section'}</span>
                            
                        </div>
                  ))}
                </div>
              )}
            </Col>
            <Col md={9} className="border-end pe-md-3 mb-3 mb-md-0">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="secondary" className="rounded-2 px-3 py-2 fs-6">
                    {packageSections.length} {packageSections.length === 1 ? 'section' : 'sections'}
                  </Badge>
                  {packageSectionsLoading && <Spinner animation="border" size="sm" variant="primary" />}
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setShowAddSectionFields(true)}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  Add a section
                </Button>
              </div>

              

              {/* Left: section + item editor */}
              {packageSectionsLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : packageSections.length === 0 ? (
                <div className="text-muted p-3 border rounded-3 bg-white">
                  No sections yet. Click <strong>Add a section</strong> to start.
                </div>
              ) : (
                <Accordion 
                className="landing-feature-accordion"
                  activeKey={activePackageSectionId ? String(activePackageSectionId) : undefined}
                  onSelect={(key) => {
                    const id = key ? parseInt(key, 10) : null;
                    if (Number.isFinite(id)) {
                      setActivePackageSectionId(id);
                      setNewItemSectionId(id);
                      setShowNewItemForSectionId(null);
                      setNewItemDraft({ ...initialItemDraft, referenceFiles: [] });
                      setEditingItemId(null);
                      setEditingItemDraft({ ...initialItemDraft, referenceFiles: [] });
                    }
                  }}
                >
                  {packageSections.map((s, sectionIndex) => (
                    <Accordion.Item key={s.id} eventKey={String(s.id)}>
                      <Accordion.Header>
                        <div className="w-100 d-flex align-items-center justify-content-between gap-2 edit-content-section-container">
                            <div className="fw-semibold d-flex align-items-center gap-2">
                              <span>{sectionIndex + 1}. {s.title}</span>
                            </div>
                          <div className="d-flex hide-button">
                            <Button
                              size="sm"
                              variant="light-lite"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setEditingSectionId(s.id);
                                setEditingSectionDraft({ title: s.title || '', instructions: s.instructions || '' });
                              }}
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              size="sm"
                              variant="light-lite"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeletePackageSection(s.id);
                              }}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body className="pt-0">
                        <div>
                        {s.instructions && <span className="text-muted">{s.instructions}</span>}
                        </div>
                        {editingSectionId === s.id ? (
                          <div className="p-3 rounded-3 border bg-light">
                            <div className="row g-2">
                              <div className="col-md-6">
                                <FloatingLabel controlId={`modal-edit-section-title-${s.id}`} label="Section title" className="mb-2">
                                  <Form.Control
                                    value={editingSectionDraft.title}
                                    onChange={(e) => setEditingSectionDraft(prev => ({ ...prev, title: e.target.value }))}
                                  />
                                </FloatingLabel>
                              </div>
                              <div className="col-md-6">
                                <FloatingLabel controlId={`modal-edit-section-instructions-${s.id}`} label="Description / instructions" className="mb-2">
                                  <Form.Control
                                    as="textarea"
                                    style={{ height: 80 }}
                                    value={editingSectionDraft.instructions}
                                    onChange={(e) => setEditingSectionDraft(prev => ({ ...prev, instructions: e.target.value }))}
                                  />
                                </FloatingLabel>
                              </div>
                            </div>
                            <div className="d-flex justify-content-end gap-2 mt-2">
                              <Button size="sm" variant="primary" disabled={!editingSectionDraft.title.trim()} onClick={() => handleUpdatePackageSection(s.id)}>
                                Save
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => { setEditingSectionId(null); setEditingSectionDraft({ title: '', instructions: '' }); }}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <small className="fw-semibold">Items</small>
                              <div className="d-flex align-items-center gap-2">
                                <Badge bg="secondary">{(s.items || []).length}</Badge>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => {
                                    setNewItemSectionId(s.id);
                                    setShowNewItemForSectionId(s.id);
                                    setEditingItemId(null);
                                    setNewItemDraft({ ...initialItemDraft, referenceFiles: [] });
                                  }}
                                >
                                  <i className="bi bi-plus-circle me-1"></i>
                                  Add item
                                </Button>
                              </div>
                            </div>

                            {(s.items || []).length === 0 ? (
                              <div className="text-muted p-2">No items yet.</div>
                            ) : (
                              <div className="d-flex flex-column gap-2 mb-3">
                                {(s.items || []).map((item, itemIndex) => (
                                  <div key={item.id} className="p-3 rounded-3 border ">
                                    

                                    {editingItemId === item.id ? (
                                      <>
                                         <small className="text-muted">Item {itemIndex + 1}</small>
                                        <div className="fw-semibold mb-2">Edit item</div>
                                        <div className="row g-2">
                                          <div className="col-md-6">
                                            <FloatingLabel controlId={`modal-edit-item-title-${item.id}`} label="Item title" className="mb-2">
                                              <Form.Control
                                                value={editingItemDraft.title}
                                                onChange={(e) => setEditingItemDraft(prev => ({ ...prev, title: e.target.value }))}
                                              />
                                            </FloatingLabel>
                                          </div>
                                          <div className="col-md-6">
                                            <FloatingLabel controlId={`modal-edit-item-type-${item.id}`} label="Item type" className="mb-2">
                                              <Form.Select
                                                value={editingItemDraft.itemType}
                                                onChange={(e) => setEditingItemDraft(prev => ({ ...prev, itemType: e.target.value }))}
                                              >
                                                <option value="file_upload">File item</option>
                                                <option value="info_form">Request information (form)</option>
                                              </Form.Select>
                                            </FloatingLabel>
                                          </div>
                                          <div className="col-md-12">
                                            <FloatingLabel controlId={`modal-edit-item-instructions-${item.id}`} label="Description / instructions" className="mb-2">
                                              <Form.Control
                                                as="textarea"
                                                style={{ height: 80 }}
                                                value={editingItemDraft.instructions}
                                                onChange={(e) => setEditingItemDraft(prev => ({ ...prev, instructions: e.target.value }))}
                                              />
                                            </FloatingLabel>
                                          </div>

                                          {editingItemDraft.itemType === 'file_upload' && (
                                            <>
                                              <div className={editingItemDraft.contentMode === 'request_upload' ? "col-md-6" : "col-md-12"}>
                                                <FloatingLabel controlId={`modal-edit-item-contentmode-${item.id}`} label="File item behavior" className="mb-2">
                                                  <Form.Select
                                                    value={editingItemDraft.contentMode}
                                                    onChange={(e) => setEditingItemDraft(prev => ({ ...prev, contentMode: e.target.value }))}
                                                  >
                                                    <option value="request_upload">Request specific file upload</option>
                                                    <option value="reference_files">Reference file(s) with completion mode</option>
                                                  </Form.Select>
                                                </FloatingLabel>
                                              </div>
                                              {editingItemDraft.contentMode === 'request_upload' ? (
                                                <div className="col-md-6">
                                                  <FloatingLabel controlId={`modal-edit-item-filetypes-${item.id}`} label="Required file types (comma-separated, optional)" className="mb-2">
                                                    <Form.Control
                                                      value={editingItemDraft.requiredFileTypes}
                                                      onChange={(e) => setEditingItemDraft(prev => ({ ...prev, requiredFileTypes: e.target.value }))}
                                                    />
                                                  </FloatingLabel>
                                                </div>
                                              ) : (
                                                <div className="col-md-12">
                                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <span className="fw-semibold">Reference files</span>
                                                    <>
                                                      <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        style={{ maxWidth: 260 }}
                                                        onClick={() => {
                                                          document.getElementById(`edit-ref-upload-${item.id}`)?.click();
                                                        }}
                                                      >
                                                        <i className="bi bi-upload me-1" /> Upload reference file
                                                      </Button>
                                                      <Form.Control
                                                        id={`edit-ref-upload-${item.id}`}
                                                        type="file"
                                                        size="sm"
                                                        style={{ display: 'none' }}
                                                        onChange={async (e) => {
                                                          const file = e.target.files?.[0];
                                                          if (file) {
                                                            await handleUploadReferenceFile(item.id, file, 'acknowledge_read', true);
                                                          }
                                                          e.target.value = '';
                                                        }}
                                                      />
                                                    </>
                                                  </div>
                                                  {(editingItemDraft.referenceFiles || []).length === 0 ? (
                                                    <div className="text-muted small">No reference files uploaded yet.</div>
                                                  ) : (
                                                    <div className="d-flex flex-column gap-1">
                                                      {(editingItemDraft.referenceFiles || []).map((rf) => (
                                                        <div key={rf.id} className="p-2 bg-light rounded-3 d-flex align-items-center justify-content-between gap-2">
                                                          <div className="text-truncate" style={{ maxWidth: '55%' }}>{rf.originalName || rf.fileName}</div>
                                                          <div className="d-flex align-items-center gap-2">
                                                            <Form.Select
                                                              size="sm"
                                                              style={{ maxWidth: 230 }}
                                                              value={rf.completionMode || 'acknowledge_read'}
                                                              onChange={(e) => {
                                                                const mode = e.target.value;
                                                                setEditingItemDraft(prev => ({
                                                                  ...prev,
                                                                  referenceFiles: (prev.referenceFiles || []).map(f => f.id === rf.id ? { ...f, completionMode: mode } : f)
                                                                }));
                                                              }}
                                                            >
                                                              <option value="acknowledge_read">Resident acknowledges read</option>
                                                              <option value="upload_required">Resident uploads evidence</option>
                                                            </Form.Select>
                                                            <Button
                                                              size="sm"
                                                              variant="outline-danger"
                                                              onClick={() => {
                                                                setEditingItemDraft(prev => ({
                                                                  ...prev,
                                                                  referenceFiles: (prev.referenceFiles || []).filter(f => f.id !== rf.id)
                                                                }));
                                                              }}
                                                            >
                                                              <i className="bi bi-trash"></i>
                                                            </Button>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </>
                                          )}

                                          {editingItemDraft.itemType === 'info_form' && (
                                            <div className="col-md-12">
                                              <FloatingLabel controlId={`modal-edit-item-form-${item.id}`} label="Select form" className="mb-2">
                                                <Form.Select
                                                  value={editingItemDraft.formId || ''}
                                                  onChange={(e) => setEditingItemDraft(prev => ({ ...prev, formId: e.target.value }))}
                                                >
                                                  <option value="">Select a form</option>
                                                  {forms.filter((f) => f.status === 'published' || f.id === Number(editingItemDraft.formId)).map((f) => (
                                                    <option key={`edit-form-option-${f.id}`} value={f.id}>
                                                      {f.title} ({f.status})
                                                    </option>
                                                  ))}
                                                </Form.Select>
                                              </FloatingLabel>
                                            </div>
                                          )}
                                        </div>
                                        <div className="d-flex justify-content-end gap-2 mt-2">
                                          <Button size="sm" variant="primary" disabled={!editingItemDraft.title.trim() || (editingItemDraft.itemType === 'info_form' && !editingItemDraft.formId)} onClick={() => handleUpdatePackageItem(item.id)}>
                                            Save
                                          </Button>
                                          <Button size="sm" variant="secondary" onClick={() => { setEditingItemId(null); setEditingItemDraft({ ...initialItemDraft, referenceFiles: [] }); }}>
                                            Cancel
                                          </Button>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div className="d-flex flex-column">
                                        <div className="d-flex justify-content-between align-items-start gap-2 edit-content-section-container">
                                        <div className="d-flex flex-column"> 
                                          <small className="text-muted">Item {itemIndex + 1}</small>
                                          <div className="fw-semibold">{item.title}</div>
                                          </div>
                                        <div className="d-flex gap-2 hide-button">
                                            <Button
                                              size="sm"
                                              variant="light-solid"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setEditingItemId(item.id);
                                                setShowNewItemForSectionId(null);
                                                setEditingItemDraft({
                                                  title: item.title || '',
                                                  instructions: item.instructions || '',
                                                  itemType: item.itemType || 'file_upload',
                                                  contentMode: item.itemType === 'file_upload' && item.formSchema?.contentMode === 'reference_files' ? 'reference_files' : 'request_upload',
                                                  requiredFileTypes: item.requiredFileTypes || '',
                                                  formSchemaJson: item.itemType === 'info_form' && item.formSchema ? JSON.stringify(item.formSchema) : '',
                                                  formId: item.itemType === 'info_form' ? (item.formId || '') : '',
                                                  referenceFiles: Array.isArray(item.formSchema?.referenceFiles) ? item.formSchema.referenceFiles : []
                                                });
                                              }}
                                            >
                                              <i className="bi bi-pencil"></i>
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="light-solid"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDeletePackageItem(item.id);
                                              }}
                                            >
                                              <i className="bi bi-trash"></i>
                                            </Button>
                                          </div>
                                          </div>
                                          <div>
                                            {item.instructions && <div className="text-muted mt-1" style={{ whiteSpace: 'pre-wrap' }}>{item.instructions}</div>}

                                            <div className="d-flex flex-row justify-content-start gap-3 w-100 mt-1">
                                            <small className="text-muted">
                                              {item.itemType === 'info_form'
                                                ? 'Info form request'
                                                : item.formSchema?.contentMode === 'reference_files'
                                                  ? 'Reference file(s): acknowledge read or upload evidence'
                                                  : 'Required file upload'}
                                            </small>
                                            {item.itemType === 'file_upload' && item.requiredFileTypes && (
                                              <small className="text-muted d-block">File types: {item.requiredFileTypes}</small>
                                            )}
                                            </div>
                                          </div>
                                          
                                        </div>
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* New item editor appears at the bottom for the active section */}
                            {showNewItemForSectionId === s.id && (
                              <div className="p-3 rounded-3 border bg-white mt-2">
                                <div className="fw-semibold mb-2 d-flex align-items-center justify-content-between gap-2">
                                  <span>
                                    <i className="bi bi-plus-circle me-1"></i>
                                    New item in "{s.title}"
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="light"
                                    onClick={() => {
                                      setShowNewItemForSectionId(null);
                                      setNewItemDraft({ ...initialItemDraft, referenceFiles: [] });
                                    }}
                                  >
                                    <i className="bi bi-x-lg me-1"></i>
                                    Close
                                  </Button>
                                </div>
                                <div className="row g-2">
                                  <div className="col-md-6">
                                    <FloatingLabel controlId={`modal-item-title-${s.id}`} label="Item title" className="mb-2">
                                      <Form.Control
                                        value={newItemDraft.title}
                                        onChange={(e) => setNewItemDraft(prev => ({ ...prev, title: e.target.value }))}
                                        placeholder="e.g., Upload vaccination record"
                                      />
                                    </FloatingLabel>
                                  </div>
                                  <div className="col-md-6">
                                    <FloatingLabel controlId={`modal-item-type-${s.id}`} label="Item type" className="mb-2">
                                      <Form.Select
                                        value={newItemDraft.itemType}
                                        onChange={(e) => setNewItemDraft(prev => ({ ...prev, itemType: e.target.value }))}
                                      >
                                        <option value="file_upload">File item</option>
                                        <option value="info_form">Request information (form)</option>
                                      </Form.Select>
                                    </FloatingLabel>
                                  </div>
                                  <div className="col-md-12">
                                    <FloatingLabel controlId={`modal-item-instructions-${s.id}`} label="Description / instructions" className="mb-2">
                                      <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={newItemDraft.instructions}
                                        onChange={(e) => setNewItemDraft(prev => ({ ...prev, instructions: e.target.value }))}
                                        placeholder="How should the resident complete this item?"
                                      />
                                    </FloatingLabel>
                                  </div>

                                  {newItemDraft.itemType === 'file_upload' && (
                                    <>
                                      <div className={newItemDraft.contentMode === 'request_upload' ? "col-md-6" : "col-md-12"}>
                                        <FloatingLabel controlId={`modal-item-contentmode-${s.id}`} label="File item behavior" className="mb-2">
                                          <Form.Select
                                            value={newItemDraft.contentMode}
                                            onChange={(e) => setNewItemDraft(prev => ({ ...prev, contentMode: e.target.value, referenceFilesPending: [] }))}
                                          >
                                            <option value="request_upload">Request specific file upload</option>
                                            <option value="reference_files">Add file(s) for resident to review</option>
                                          </Form.Select>
                                        </FloatingLabel>
                                      </div>
                                      {newItemDraft.contentMode === 'request_upload' ? (
                                        <div className="col-md-6">
                                          <FloatingLabel controlId={`modal-item-filetypes-${s.id}`} label="Required file types (comma-separated extensions, optional)" className="mb-2">
                                            <Form.Control
                                              value={newItemDraft.requiredFileTypes}
                                              onChange={(e) => setNewItemDraft(prev => ({ ...prev, requiredFileTypes: e.target.value }))}
                                              placeholder="pdf,doc,docx"
                                            />
                                          </FloatingLabel>
                                        </div>
                                      ) : (
                                        <div className="col-md-12">
                                          <div className="d-flex align-items-center justify-content-between mb-2">
                                            <small className="fw-semibold">Reference files</small>
                                            <Button
                                              size="sm"
                                              variant="outline-primary"
                                              style={{ maxWidth: 260 }}
                                              onClick={() => {
                                                document.getElementById(`ref-upload-${s.id}`)?.click();
                                              }}
                                            >
                                              <i className="bi bi-upload me-1" /> Upload reference file(s)
                                            </Button>
                                            <Form.Control
                                              id={`ref-upload-${s.id}`}
                                              type="file"
                                              multiple
                                              size="sm"
                                              style={{ display: 'none' }}
                                              onChange={(e) => {
                                                const files = Array.from(e.target.files || []);
                                                if (!files.length) return;
                                                const newPending = files.map((file) => ({
                                                  tmpId: `tmp_${Date.now()}_${Math.round(Math.random() * 1e6)}`,
                                                  file,
                                                  completionMode: 'acknowledge_read'
                                                }));
                                                setNewItemDraft(prev => ({
                                                  ...prev,
                                                  referenceFilesPending: [...(prev.referenceFilesPending || []), ...newPending]
                                                }));
                                                e.target.value = '';
                                              }}
                                            />
                                          </div>
                                          {(newItemDraft.referenceFilesPending || []).length === 0 ? (
                                            <div className="text-muted small">Upload one or more reference files above.</div>
                                          ) : (
                                            <div className="d-flex flex-column gap-2">
                                              {(newItemDraft.referenceFilesPending || []).map((rf) => (
                                                <div key={rf.tmpId} className="p-2 border rounded-3 bg-white d-flex align-items-center justify-content-between gap-2">
                                                  <div className="text-truncate" style={{ maxWidth: '55%' }}>
                                                    {rf.file?.name || 'reference file'}
                                                  </div>
                                                  <div className="d-flex align-items-center gap-2">
                                                    <Form.Select
                                                      size="sm"
                                                      style={{ maxWidth: 230 }}
                                                      value={rf.completionMode}
                                                      onChange={(e) => {
                                                        const mode = e.target.value;
                                                        setNewItemDraft(prev => ({
                                                          ...prev,
                                                          referenceFilesPending: (prev.referenceFilesPending || []).map(x => x.tmpId === rf.tmpId ? { ...x, completionMode: mode } : x)
                                                        }));
                                                      }}
                                                    >
                                                      <option value="acknowledge_read">Resident acknowledges read</option>
                                                      <option value="upload_required">Resident uploads evidence</option>
                                                    </Form.Select>
                                                    <Button
                                                      size="sm"
                                                      variant="outline-danger"
                                                      onClick={() => {
                                                        setNewItemDraft(prev => ({
                                                          ...prev,
                                                          referenceFilesPending: (prev.referenceFilesPending || []).filter(x => x.tmpId !== rf.tmpId)
                                                        }));
                                                      }}
                                                    >
                                                      <i className="bi bi-trash"></i>
                                                    </Button>
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )}

                                  {newItemDraft.itemType === 'info_form' && (
                                    <div className="col-md-12">
                                      <FloatingLabel controlId={`modal-item-form-${s.id}`} label="Select form" className="mb-2">
                                        <Form.Select
                                          value={newItemDraft.formId || ''}
                                          onChange={(e) => setNewItemDraft(prev => ({ ...prev, formId: e.target.value }))}
                                        >
                                          <option value="">Select a form</option>
                                          {forms.filter((f) => f.status === 'published').map((f) => (
                                            <option key={`new-form-option-${f.id}`} value={f.id}>
                                              {f.title}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      </FloatingLabel>
                                    </div>
                                  )}
                                </div>
                                <div className="d-flex justify-content-end mt-2">
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    disabled={
                                      !newItemDraft.title.trim() ||
                                      packageSectionsLoading ||
                                      (newItemDraft.itemType === 'info_form' && !newItemDraft.formId) ||
                                      (newItemDraft.itemType === 'file_upload' && newItemDraft.contentMode === 'reference_files' && (newItemDraft.referenceFilesPending || []).length === 0)
                                    }
                                    onClick={async () => {
                                      await handleCreatePackageItem();
                                      setShowNewItemForSectionId(null);
                                    }}
                                  >
                                    Save item
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}

              {showAddSectionFields && (
                <div className="mb-4 p-3 rounded-3 border bg-light">
                  <div className="row g-2">
                    <div className="col-md-12">
                      <h6 className="mb-2 fw-semibold">Section {packageSections.length + 1}</h6>
                    </div>
                    <div className="col-md-4">
                      <FloatingLabel controlId="modal-new-section-title" label="Section title" className="mb-2">
                        <Form.Control
                          value={newSectionDraft.title}
                          onChange={(e) => setNewSectionDraft(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="e.g., Immunizations"
                        />
                      </FloatingLabel>
                    </div>
                    <div className="col-md-8">
                      <FloatingLabel controlId="modal-new-section-instructions" label="Description / instructions" className="mb-2">
                        <Form.Control
                          as="textarea"
                          rows={1}
                          value={newSectionDraft.instructions}
                          onChange={(e) => setNewSectionDraft(prev => ({ ...prev, instructions: e.target.value }))}
                          placeholder="What should the resident do for this section?"
                        />
                      </FloatingLabel>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end mt-2 gap-2">
                     <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setShowAddSectionFields(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={!newSectionDraft.title.trim() || packageSectionsLoading}
                      onClick={async () => {
                        await handleCreatePackageSection();
                        setShowAddSectionFields(false);
                      }}
                    >
                      <i className="bi bi-save me-1"></i>
                      Save section
                    </Button>
                   
                  </div>
                </div>
              )}
            </Col>

           
          </Row>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowPackageContentModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <OnboardingDocumentRequestModal
        show={showDocumentRequestModal}
        onHide={() => setShowDocumentRequestModal(false)}
        onSubmit={handleCreateDocumentRequest}
        packageId={documentRequestPackageId}
      />
      
      {/* Delete Assignment Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <div className="d-flex flex-column justify-content-start w-100">
          <Modal.Title>Delete Assignment</Modal.Title>
          <div className="text-hl-danger"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this assignment? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDeleteAssignment}>
            Delete Assignment
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Document Confirmation Modal */}
      <Modal show={showDeleteDocumentModal} onHide={() => setShowDeleteDocumentModal(false)} centered>
        <Modal.Header closeButton className="border-0" >
          <div className="d-flex flex-column justify-content-start w-100">
          <Modal.Title>Delete Document</Modal.Title>
          <div className="text-hl-danger"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this document and all related assignments/submissions? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteDocumentModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDeleteDocument}>
            Delete Document
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Package Confirmation Modal */}
      <Modal show={showDeletePackageModal} onHide={() => setShowDeletePackageModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <div className="d-flex flex-column justify-content-start w-100">
          <Modal.Title>Delete Package</Modal.Title>
          <div className="text-hl-danger"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this package, its documents, and all related data? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowDeletePackageModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDeletePackage}>
            Delete Package
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Document Request Confirmation Modal */}
      <Modal show={showDeleteDocumentRequestModal} onHide={() => setShowDeleteDocumentRequestModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <div className="d-flex flex-column justify-content-start w-100">
          <Modal.Title>Delete Document Request</Modal.Title>
          <div className="text-hl-danger"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this document request and all related assignments? This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteDocumentRequestModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDeleteDocumentRequest}>
            Delete Document Request
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <div className="d-flex flex-column justify-content-start w-100">
          <Modal.Title>Bulk Delete Confirmation</Modal.Title>
          <div className="text-hl-danger"></div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete {bulkDeleteCount} selected {
              bulkDeleteType === 'package-documents' ? 'document(s) and related assignments/submissions' :
              bulkDeleteType === 'standalone-documents' ? 'document(s) and related assignments/submissions' :
              bulkDeleteType === 'document-requests' ? 'document request(s) and related assignments' :
              bulkDeleteType === 'package-document-requests' ? 'document request(s) and related assignments' :
              'item(s)'
            }? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="secondary" size="sm" onClick={() => setShowBulkDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={() => {
            switch (bulkDeleteType) {
              case 'package-documents':
                confirmBulkDeletePackageDocuments();
                break;
              case 'standalone-documents':
                confirmBulkDeleteStandaloneDocuments();
                break;
              case 'document-requests':
                confirmBulkDeleteDocumentRequests();
                break;
              case 'package-document-requests':
                confirmBulkDeletePackageDocumentRequests();
                break;
              default:
                setShowBulkDeleteModal(false);
            }
          }}>
            Delete {bulkDeleteCount} Item{bulkDeleteCount !== 1 ? 's' : ''}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OnboardingPage;


